import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Plus, Trash2, Edit2, Play, FileText, Award, X, ChevronDown, ChevronUp, Unlock, Lock, BarChart2, User, BookOpen, Calendar, ExternalLink, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from '../utils/toast';
import { exportarPDF, exportarWord } from '../utils/exportDoc';
import { uploadMaterial, urlMaterial, excluirArquivoMaterial } from '../lib/storage-ensino';
import { uploadVideoAula, urlVideoAula, excluirVideoAula } from '../lib/storage-videos';
import { supabase } from '../lib/supabase';
import MensagensTutor from './MensagensTutor';
import { liberarRemocaoEmMassa } from '../lib/liberacaoRemocao';
import { aproveitamentoNaTurma, baixarCertificado, codigoCertificado, dataPorExtenso } from '../utils/certificado';

// ── Chaves localStorage ────────────────────────────────────────────────────
const K = {
  cursos:   'kzerodiary_cursos',
  turmas:   'kzerodiary_turmas',
  aulas:    'kzerodiary_aulas',
  mats:     'kzerodiary_materiais',
  questoes: 'kzerodiary_questoes_banco',
  pastas:   'kzerodiary_pastas_banco',
  testes:   'kzerodiary_testes',
  respostas:'kzerodiary_respostas_testes',
  agenda:   'kzerodiary_agenda_trein',
  perfilTr: 'kzerodiary_perfis_trein',
};

const LS = {
  get: (k) => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : []; } catch { return []; } },
  // Cache offline apenas: o Supabase é a fonte. No iPhone a cota (~5MB) estoura
  // e o throw dentro do useEffect derrubava o app inteiro no ErrorBoundary.
  set: (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* quota */ } },
};

const novoId = () => Date.now() + Math.random();
const fmtData = (d) => d ? new Date(d + 'T00:00').toLocaleDateString('pt-BR') : '—';
const fmtPct  = (v) => `${v.toFixed(1)}%`;

// ── Componente principal ───────────────────────────────────────────────────
export function Treinamentos({ usuarioAtual, isAdmin, usuarios = [], agendamentos = [], setAgendamentos, dadosTr = {}, setDadosTr }) {
  const papel = usuarioAtual?.papel || 'aluno';
  const isGestor = isAdmin || papel === 'instrutor';

  const [aba, setAba] = useState(isGestor ? 'cursos' : 'perfil');

  // Inicializa do Supabase compartilhado (dadosTr), com fallback para localStorage
  const [cursos,      setCursos]      = useState(() => dadosTr.cursos    ?? LS.get(K.cursos));
  const [turmas,      setTurmas]      = useState(() => dadosTr.turmas    ?? LS.get(K.turmas));
  const [aulas,       setAulas]       = useState(() => dadosTr.aulas     ?? LS.get(K.aulas));
  const [mats,        setMats]        = useState(() => dadosTr.mats      ?? LS.get(K.mats));
  const [questoes,    setQuestoes]    = useState(() => dadosTr.questoes  ?? LS.get(K.questoes));
  const [pastas,      setPastas]      = useState(() => dadosTr.pastas    ?? LS.get(K.pastas));
  const [testes,      setTestes]      = useState(() => dadosTr.testes    ?? LS.get(K.testes));
  const [respostas,   setRespostas]   = useState(() => dadosTr.respostas ?? LS.get(K.respostas));
  const [agendaTr,    setAgendaTr]    = useState(() => dadosTr.agenda    ?? LS.get(K.agenda));
  const [perfisExtra, setPerfisExtra] = useState(() => dadosTr.perfis   ?? LS.get(K.perfilTr));
  const [presencial,  setPresencial]  = useState(() => dadosTr.presencial ?? []);
  const [certificados, setCertificados] = useState(() => dadosTr.certificados ?? []);
  const [certConfig, setCertConfig] = useState(() => dadosTr.certConfig ?? {});
  const [avisos, setAvisos] = useState(() => dadosTr.avisos ?? []);

  // Controla se o mount inicial já ocorreu — evita sobrescrever Supabase com dados vazios
  const montadoRef = useRef(false);

  // Último dadosTr recebido (da nuvem ou do próprio save)
  const dadosTrRef = useRef(dadosTr);
  dadosTrRef.current = dadosTr;

  // Salva no Supabase compartilhado — só após o mount inicial.
  // Ignora o que é idêntico ao que já está em dadosTr: quando outro usuário
  // salva, o efeito de "Recebe atualizações" abaixo repassa a MESMA referência
  // para o estado local, e sem esse filtro cada Diary aberto regravava o bloco
  // inteiro de volta — um eco que podia sobrescrever uma alteração mais nova.
  const salvarTr = (patch) => {
    if (!montadoRef.current || !setDadosTr) return;
    const atual = dadosTrRef.current || {};
    const mudou = Object.fromEntries(Object.entries(patch).filter(([k, v]) => v !== atual[k]));
    if (!Object.keys(mudou).length) return;
    setDadosTr(prev => ({ ...(prev || {}), ...mudou }));
  };

  useEffect(() => { LS.set(K.cursos,    cursos);               salvarTr({ cursos });            }, [cursos]);
  useEffect(() => { LS.set(K.turmas,    turmas);               salvarTr({ turmas });            }, [turmas]);
  useEffect(() => { LS.set(K.aulas,     aulas);                salvarTr({ aulas });             }, [aulas]);
  useEffect(() => { LS.set(K.mats,      mats);                 salvarTr({ mats });              }, [mats]);
  useEffect(() => { LS.set(K.questoes,  questoes);             salvarTr({ questoes });          }, [questoes]);
  useEffect(() => { LS.set(K.pastas,    pastas);               salvarTr({ pastas });            }, [pastas]);
  useEffect(() => { LS.set(K.testes,    testes);               salvarTr({ testes });            }, [testes]);
  useEffect(() => { LS.set(K.respostas, respostas);            salvarTr({ respostas });         }, [respostas]);
  useEffect(() => { LS.set(K.agenda,    agendaTr);             salvarTr({ agenda: agendaTr }); }, [agendaTr]);
  useEffect(() => { LS.set(K.perfilTr,  perfisExtra);          salvarTr({ perfis: perfisExtra }); }, [perfisExtra]);
  useEffect(() => {                                             salvarTr({ presencial });           }, [presencial]);
  useEffect(() => {                                             salvarTr({ certificados });         }, [certificados]);
  useEffect(() => {                                             salvarTr({ certConfig });           }, [certConfig]);
  useEffect(() => {                                             salvarTr({ avisos });               }, [avisos]);

  // Marca mount como concluído após primeiro ciclo
  useEffect(() => { montadoRef.current = true; }, []);

  // Recebe atualizações em tempo real do Supabase (outro usuário salvou)
  useEffect(() => {
    if (!dadosTr || Object.keys(dadosTr).length === 0) return;
    if (dadosTr.cursos    !== undefined) setCursos(dadosTr.cursos);
    if (dadosTr.turmas    !== undefined) setTurmas(dadosTr.turmas);
    if (dadosTr.aulas     !== undefined) setAulas(dadosTr.aulas);
    if (dadosTr.mats      !== undefined) setMats(dadosTr.mats);
    if (dadosTr.questoes  !== undefined) setQuestoes(dadosTr.questoes);
    if (dadosTr.pastas    !== undefined) setPastas(dadosTr.pastas);
    if (dadosTr.testes    !== undefined) setTestes(dadosTr.testes);
    if (dadosTr.respostas !== undefined) setRespostas(dadosTr.respostas);
    if (dadosTr.agenda    !== undefined) setAgendaTr(dadosTr.agenda);
    if (dadosTr.perfis    !== undefined) setPerfisExtra(dadosTr.perfis);
    if (dadosTr.presencial !== undefined) setPresencial(dadosTr.presencial);
    if (dadosTr.certificados !== undefined) setCertificados(dadosTr.certificados);
    if (dadosTr.certConfig !== undefined) setCertConfig(dadosTr.certConfig);
    if (dadosTr.avisos !== undefined) setAvisos(dadosTr.avisos);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dadosTr]);

  // Turmas em que o aluno está matriculado
  const turmasDoAluno = useMemo(() =>
    turmas.filter(t => (t.alunosIds || []).includes(String(usuarioAtual?.id))),
    [turmas, usuarioAtual]
  );

  const cursosDoAluno = useMemo(() =>
    cursos.filter(c => turmasDoAluno.some(t => String(t.cursoId) === String(c.id))),
    [cursos, turmasDoAluno]
  );

  // Abas por perfil
  const abas = isGestor
    ? [
        ['cursos',   '🎓 Cursos & Turmas'],
        ['aulas',    '🎬 Aulas'],
        ['material', '📚 Material'],
        ['questoes', '❓ Banco de Questões'],
        ['testes',   '🧪 Testes'],
        ['presencial','🏢 Presencial'],
        ['agenda',   '📅 Calendário'],
        ['mural',    '📢 Mural de avisos'],
        ['resultados','📊 Resultados'],
        ['mensagens','💬 Mensagens'],
      ]
    : [
        ['perfil',   '👤 Meu Perfil'],
        // Calendário do aluno virou Mural de avisos, logo após o perfil (30/10/2026).
        ['mural',    '📢 Mural de avisos'],
        ['aulas',    '🎬 Aulas'],
        ['material', '📚 Material'],
        ['simulados','📝 Simulados'],
        ['testes',   '🧪 Meus Testes'],
        // Meus Resultados e Meus Certificados ficam dentro de Meu Perfil (30/10/2026).
        ['mensagens','💬 Fale com o tutor'],
      ];

  const props = { cursos, setCursos, turmas, setTurmas, aulas, setAulas, mats, setMats, questoes, setQuestoes, pastas, setPastas, testes, setTestes, respostas, setRespostas, agendaTr, setAgendaTr, perfisExtra, setPerfisExtra, presencial, setPresencial, certificados, setCertificados, certConfig, setCertConfig, avisos, setAvisos, usuarios, usuarioAtual, isGestor, turmasDoAluno, cursosDoAluno, agendamentos, setAgendamentos };

  return (
    <div className="m-container" style={s.container}>
      <h2 style={s.titulo}>🎓 Treinamentos</h2>
      <div style={s.abas}>
        {abas.map(([id, label]) => (
          <button key={id} style={{ ...s.aba, ...(aba === id ? s.abaAtiva : {}) }} onClick={() => setAba(id)}>{label}</button>
        ))}
      </div>
      {aba === 'cursos'    && isGestor && <AbaCursos {...props} />}
      {aba === 'aulas'     && <AbaAulas {...props} />}
      {aba === 'material'  && <AbaMaterial {...props} />}
      {aba === 'questoes'  && isGestor && <AbaBancoQuestoes {...props} />}
      {aba === 'simulados' && !isGestor && <AbaSimulados {...props} />}
      {aba === 'testes'    && <AbaTestes {...props} />}
      {aba === 'presencial'&& isGestor && <AbaPresencial {...props} />}
      {aba === 'agenda'    && isGestor && <AbaAgenda {...props} />}
      {aba === 'mural'     && <AbaMural {...props} />}
      {aba === 'resultados'&& isGestor && <AbaResultados {...props} />}
      {aba === 'perfil'    && !isGestor && (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
            <AbaMeuPerfil {...props} />
            <AbaDesempenhoAluno {...props} />
            <AbaResultados {...props} />
            <AbaMeusCertificados {...props} />
          </div>
        </>
      )}
      {aba === 'mensagens' && <MensagensTutor isGestor={isGestor} usuarioAtual={usuarioAtual} cursos={isGestor ? cursos : cursosDoAluno} usuarios={usuarios} />}
    </div>
  );
}

// ── Utilitário: converte evento de treinamento → agendamento principal ──────
function eventoParaAgendamento(ev, cursos, turmas) {
  const curso = cursos.find(c => String(c.id) === String(ev.cursoId));
  const turma = turmas.find(t => String(t.id) === String(ev.turmaId));
  const descPartes = [ev.titulo];
  if (curso) descPartes.push(`Curso: ${curso.nome}`);
  if (turma) descPartes.push(`Turma: ${turma.nome}`);
  if (ev.descricao) descPartes.push(ev.descricao);
  return {
    id: `trein_${ev.id}`,
    data: ev.data,
    horaInicio: ev.hora || '',
    horaFinal: ev.horaFim || '',
    classificacao: 'TREINAMENTO',
    cliente: curso?.nome || ev.titulo,
    profissionais: [],
    observacoes: descPartes.join(' | '),
    valor: 0,
    pausaAlmoco: false,
    duracaoAlmoco: 60,
    origemTreinamento: true,
    origemEvId: ev.id,
  };
}

// ── ABA: Cursos & Turmas ───────────────────────────────────────────────────
function AbaCursos({ cursos, setCursos, turmas, setTurmas, usuarios, agendaTr, setAgendaTr, agendamentos, setAgendamentos, testes = [], respostas = [], certificados = [], setCertificados, certConfig = {}, setCertConfig }) {
  const [formCurso, setFormCurso] = useState(null);
  const [formTurma, setFormTurma] = useState(null);
  const [cursoAberto, setCursoAberto] = useState(null);

  const salvarCurso = () => {
    if (!formCurso.nome) { toast.aviso('Informe o nome do curso.'); return; }
    if (formCurso.id) {
      setCursos(cursos.map(c => c.id === formCurso.id ? formCurso : c));
      toast.sucesso('Curso atualizado!');
    } else {
      setCursos([...cursos, { ...formCurso, id: novoId() }]);
      toast.sucesso('Curso criado!');
    }
    setFormCurso(null);
  };

  const excluirCurso = (id) => {
    if (!window.confirm('Excluir curso e todas as suas turmas?')) return;
    setCursos(cursos.filter(c => c.id !== id));
    setTurmas(turmas.filter(t => t.cursoId !== id));
    toast.sucesso('Curso excluído.');
  };

  const criarEventosTurma = (turma, curso) => {
    const eventos = [];
    // Evento de abertura de turma (dataInicio)
    if (turma.dataInicio) {
      const evAbertura = {
        id: novoId(),
        titulo: `Abertura de Turma: ${turma.nome}`,
        cursoId: turma.cursoId,
        turmaId: turma.id,
        data: turma.dataInicio,
        hora: '',
        horaFim: '',
        tipo: 'aberturaTurma',
        descricao: `Início da turma ${turma.nome}${curso ? ` — ${curso.nome}` : ''}`,
        link: '',
      };
      eventos.push(evAbertura);
    }
    // Evento de encerramento de turma (dataFim)
    if (turma.dataFim) {
      const evFim = {
        id: novoId(),
        titulo: `Encerramento de Turma: ${turma.nome}`,
        cursoId: turma.cursoId,
        turmaId: turma.id,
        data: turma.dataFim,
        hora: '',
        horaFim: '',
        tipo: 'aberturaTurma',
        descricao: `Encerramento da turma ${turma.nome}${curso ? ` — ${curso.nome}` : ''}`,
        link: '',
      };
      eventos.push(evFim);
    }
    if (eventos.length === 0) return;
    // Adiciona no calendário de treinamentos
    if (setAgendaTr) setAgendaTr(prev => [...prev, ...eventos]);
    // Adiciona na agenda principal como TREINAMENTO
    if (setAgendamentos) {
      const ags = eventos.map(ev => eventoParaAgendamento(ev, cursos, [...turmas, { ...turma }]));
      setAgendamentos(prev => [...prev, ...ags]);
    }
  };

  const salvarTurma = () => {
    if (!formTurma.nome) { toast.aviso('Informe o nome da turma.'); return; }
    if (formTurma.id) {
      setTurmas(turmas.map(t => t.id === formTurma.id ? formTurma : t));
      toast.sucesso('Turma atualizada!');
    } else {
      const novaId = novoId();
      const novaTurma = { ...formTurma, id: novaId, alunosIds: formTurma.alunosIds || [] };
      setTurmas([...turmas, novaTurma]);
      const curso = cursos.find(c => String(c.id) === String(formTurma.cursoId));
      criarEventosTurma(novaTurma, curso);
      toast.sucesso('Turma criada! Eventos gerados na agenda.');
    }
    setFormTurma(null);
  };

  const toggleAluno = (turmaId, alunoId) => {
    setTurmas(turmas.map(t => {
      if (t.id !== turmaId) return t;
      const ids = t.alunosIds || [];
      return { ...t, alunosIds: ids.includes(String(alunoId)) ? ids.filter(i => i !== String(alunoId)) : [...ids, String(alunoId)] };
    }));
  };

  // Alunos vêm do cadastro de Pessoas > Alunos (localStorage)
  // Usa sempre os usuários do Supabase (IDs consistentes entre dispositivos)
  const alunos = usuarios.filter(u => u.papel === 'aluno');

  return (
    <div style={s.secao}>
      <div style={s.secaoHeader}>
        <div style={s.secaoTitulo}>Cursos cadastrados</div>
        {!formCurso && <button style={s.btnNovo} onClick={() => setFormCurso({ nome:'', descricao:'', instrutorNome:'', cargaHoraria:'', categoria:'' })}><Plus size={14}/> Novo Curso</button>}
      </div>
      {setCertConfig && <AssinaturasCertificado certConfig={certConfig} setCertConfig={setCertConfig} cursos={cursos} />}

      {formCurso && (
        <div style={s.formCard}>
          <div style={s.formTitulo}>{formCurso.id ? 'Editar Curso' : 'Novo Curso'}</div>
          <div style={s.formRow}>
            <Campo label="Nome *"><input style={s.input} value={formCurso.nome} onChange={e => setFormCurso({...formCurso, nome: e.target.value})} /></Campo>
            <Campo label="Categoria"><input style={s.input} value={formCurso.categoria} placeholder="Ex: Gestão, Liderança..." onChange={e => setFormCurso({...formCurso, categoria: e.target.value})} /></Campo>
            <Campo label="Carga horária"><input style={s.input} value={formCurso.cargaHoraria} placeholder="Ex: 40h" onChange={e => setFormCurso({...formCurso, cargaHoraria: e.target.value})} /></Campo>
          </div>
          <Campo label="Descrição"><textarea style={{...s.input, height:70, resize:'vertical'}} value={formCurso.descricao} onChange={e => setFormCurso({...formCurso, descricao: e.target.value})} /></Campo>
          <Campo label="Instrutor responsável"><input style={s.input} value={formCurso.instrutorNome} onChange={e => setFormCurso({...formCurso, instrutorNome: e.target.value})} /></Campo>
          <div style={s.formAcoes}>
            <button style={s.btnSalvar} onClick={salvarCurso}>Salvar</button>
            <button style={s.btnCancelar} onClick={() => setFormCurso(null)}>Cancelar</button>
          </div>
        </div>
      )}

      {cursos.length === 0 && !formCurso && <div style={s.vazio}>Nenhum curso cadastrado.</div>}

      {cursos.map(curso => {
        const turmasDoCurso = turmas.filter(t => String(t.cursoId) === String(curso.id));
        const aberto = cursoAberto === curso.id;
        return (
          <div key={curso.id} style={s.card}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', cursor:'pointer' }} onClick={() => setCursoAberto(aberto ? null : curso.id)}>
              <div>
                <div style={s.cardTitulo}>{curso.nome}</div>
                <div style={s.cardMeta}>{curso.categoria && `${curso.categoria} · `}{curso.cargaHoraria && `${curso.cargaHoraria} · `}{curso.instrutorNome && `Instrutor: ${curso.instrutorNome}`}</div>
              </div>
              <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                <span style={s.badge}>{turmasDoCurso.length} turma(s)</span>
                <button style={s.btnIcone} onClick={e => { e.stopPropagation(); setFormCurso({...curso}); }}><Edit2 size={13}/></button>
                <button style={{...s.btnIcone, color:'#ef4444'}} onClick={e => { e.stopPropagation(); excluirCurso(curso.id); }}><Trash2 size={13}/></button>
                {aberto ? <ChevronUp size={16} color="#5f5874"/> : <ChevronDown size={16} color="#5f5874"/>}
              </div>
            </div>

            {aberto && (
              <div style={{ marginTop:'1rem', borderTop:'1px solid #e5e0ef', paddingTop:'1rem' }}>
                {curso.descricao && <p style={{ color:'#64748b', fontSize:13, marginBottom:'1rem', whiteSpace:'pre-wrap' }}>{curso.descricao}</p>}
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.75rem' }}>
                  <div style={{ color:'#1c1530', fontWeight:600, fontSize:14 }}>Turmas</div>
                  {!formTurma && <button style={{...s.btnNovo, padding:'6px 14px', fontSize:12}} onClick={() => setFormTurma({ cursoId: curso.id, nome:'', dataInicio:'', dataFim:'' })}><Plus size={12}/> Nova Turma</button>}
                </div>

                {formTurma && String(formTurma.cursoId) === String(curso.id) && (
                  <div style={{...s.formCard, background:'#ffffff', marginBottom:'1rem'}}>
                    <div style={{ fontSize:13, fontWeight:600, color:'#1c1530', marginBottom:'0.5rem' }}>{formTurma.id ? 'Editar Turma' : 'Nova Turma'}</div>
                    <div style={s.formRow}>
                      <Campo label="Nome da turma *"><input style={s.input} value={formTurma.nome} onChange={e => setFormTurma({...formTurma, nome: e.target.value})} /></Campo>
                      <Campo label="Início"><input type="date" style={s.input} value={formTurma.dataInicio} onChange={e => setFormTurma({...formTurma, dataInicio: e.target.value})} /></Campo>
                      <Campo label="Fim"><input type="date" style={s.input} value={formTurma.dataFim} onChange={e => setFormTurma({...formTurma, dataFim: e.target.value})} /></Campo>
                    </div>
                    {alunos.length > 0 && (
                      <div>
                        <div style={{ fontSize:11, color:'#5f5874', fontWeight:600, marginBottom:'0.5rem' }}>ALUNOS MATRICULADOS</div>
                        <SeletorAlunos
                          alunos={alunos}
                          selecionados={formTurma.alunosIds || []}
                          onToggle={id => {
                            const ids = formTurma.alunosIds || [];
                            setFormTurma({...formTurma, alunosIds: ids.includes(id) ? ids.filter(i => i !== id) : [...ids, id]});
                          }}
                        />
                      </div>
                    )}
                    {alunos.length === 0 && (
                      <div style={{ fontSize:12, color:'#64748b' }}>Nenhum aluno cadastrado. Acesse Pessoas → Alunos para cadastrar.</div>
                    )}
                    <div style={s.formAcoes}>
                      <button style={s.btnSalvar} onClick={salvarTurma}>Salvar</button>
                      <button style={s.btnCancelar} onClick={() => setFormTurma(null)}>Cancelar</button>
                    </div>
                  </div>
                )}

                {turmasDoCurso.length === 0 && <div style={s.vazio}>Nenhuma turma. Crie a primeira.</div>}

                {turmasDoCurso.map(turma => (
                  <div key={turma.id} style={{ background:'#ffffff', borderRadius:8, padding:'0.875rem', marginBottom:'0.5rem' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                      <div>
                        <div style={{ color:'#1c1530', fontWeight:600, fontSize:13 }}>{turma.nome}</div>
                        <div style={s.cardMeta}>{turma.dataInicio && `${fmtData(turma.dataInicio)} → ${fmtData(turma.dataFim)}`}</div>
                      </div>
                      <div style={{ display:'flex', gap:4 }}>
                        <button style={s.btnIcone} onClick={() => setFormTurma({...turma})}><Edit2 size={13}/></button>
                        <button style={{...s.btnIcone, color:'#ef4444'}} onClick={() => { if(window.confirm('Excluir turma?')) { setTurmas(turmas.filter(t => t.id !== turma.id)); toast.sucesso('Turma excluída.'); }}}><Trash2 size={13}/></button>
                      </div>
                    </div>
                    <div style={{ marginTop:'0.75rem' }}>
                      <div style={{ fontSize:12, color:'#64748b', fontWeight:600, marginBottom:'0.5rem' }}>ALUNOS MATRICULADOS</div>
                      {alunos.length === 0 && <div style={{ fontSize:12, color:'#64748b' }}>Nenhum aluno cadastrado no sistema ainda.</div>}
                      {alunos.length > 0 && (
                        <SeletorAlunos
                          alunos={alunos}
                          selecionados={turma.alunosIds || []}
                          onToggle={id => toggleAluno(turma.id, id)}
                        />
                      )}
                    </div>
                    <CertificadosDaTurma turma={turma} curso={curso} alunos={alunos} testes={testes} respostas={respostas} certificados={certificados} setCertificados={setCertificados} certConfig={certConfig} />
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Assinaturas do certificado (visão do gestor) ───────────────────────────
// Imagens em tr_dados.certConfig como data URL reduzida (até 600 px de largura).
// Sem imagem da Kzero, o certificado usa o logo como assinatura institucional.
function reduzirImagem(file) {
  return new Promise((ok, erro) => {
    if (!file || !/^image\/(png|jpe?g|webp)$/.test(file.type)) { erro(new Error('Envie a assinatura em PNG ou JPG.')); return; }
    const fr = new FileReader();
    fr.onerror = () => erro(new Error('Não foi possível ler a imagem.'));
    fr.onload = () => {
      const img = new Image();
      img.onerror = () => erro(new Error('Não foi possível ler a imagem.'));
      img.onload = () => {
        const escala = Math.min(1, 600 / img.width);
        const cv = document.createElement('canvas');
        cv.width = Math.round(img.width * escala); cv.height = Math.round(img.height * escala);
        cv.getContext('2d').drawImage(img, 0, 0, cv.width, cv.height);
        ok(cv.toDataURL('image/png'));
      };
      img.src = fr.result;
    };
    fr.readAsDataURL(file);
  });
}

function AssinaturasCertificado({ certConfig = {}, setCertConfig, cursos }) {
  const [aberto, setAberto] = useState(false);
  const instrutores = [...new Set(cursos.map(c => (c.instrutorNome || '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  const atualizar = (patch) => setCertConfig({ ...certConfig, ...patch });
  const enviar = async (file, aplicar) => {
    try { aplicar(await reduzirImagem(file)); toast.sucesso('Assinatura salva.'); } catch (e) { toast.erro(e.message); }
  };
  const caixa = { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 12px', border: '1px solid #eeeaf5', borderRadius: 10 };
  const btn = { fontSize: 12, padding: '5px 10px', borderRadius: 8, border: '1px solid #e5e0ef', background: '#ffffff', color: '#1c1530', cursor: 'pointer', fontWeight: 600, minHeight: 0 };
  const previa = (img) => img
    ? <img src={img} alt="Assinatura" style={{ height: 40, maxWidth: 160, objectFit: 'contain', background: '#fff', border: '1px dashed #e5e0ef', borderRadius: 6 }} />
    : <span style={{ fontSize: 12, color: '#8a849c' }}>Sem imagem</span>;
  return (
    <div style={{ ...s.card, marginBottom: '1rem' }}>
      <button style={{ ...btn, border: 'none', padding: 0, fontSize: 14, fontWeight: 700 }} onClick={() => setAberto(!aberto)}>
        {aberto ? '▾' : '▸'} 🖋️ Assinaturas do certificado
      </button>
      {aberto && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
          <div style={{ fontSize: 12, color: '#5f5874' }}>Assinatura do(a) instrutor(a) que vai impressa no certificado: imagem com fundo branco ou transparente (PNG ou JPG). O aluno assina à mão no espaço dele.</div>
          {instrutores.map(nome => (
            <div key={nome} style={caixa}>
              <b style={{ fontSize: 13, minWidth: 150 }}>{nome}<span style={{ fontWeight: 400, color: '#8a849c' }}> · instrutor(a)</span></b>
              {previa(certConfig.assinaturasInstrutor?.[nome])}
              <label style={btn}>Enviar imagem<input type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={e => { enviar(e.target.files?.[0], img => atualizar({ assinaturasInstrutor: { ...(certConfig.assinaturasInstrutor || {}), [nome]: img } })); e.target.value = ''; }} /></label>
              {certConfig.assinaturasInstrutor?.[nome] && <button style={{ ...btn, color: '#dc2626' }} onClick={() => { const r = { ...(certConfig.assinaturasInstrutor || {}) }; delete r[nome]; atualizar({ assinaturasInstrutor: r }); }}>Remover</button>}
            </div>
          ))}
          <button style={{ ...btn, alignSelf: 'flex-start' }} onClick={() => baixarCertificado({
            alunoNome: 'Nome do Aluno', cursoNome: cursos[0]?.nome || 'Nome do curso', cargaHoraria: cursos[0]?.cargaHoraria || '16h',
            instrutorNome: cursos[0]?.instrutorNome || '', dataInicio: '', dataFim: '', percentual: 90,
            emitidoEm: new Date().toISOString().slice(0, 10), codigo: 'KZ-EXEMPLO',
          }, certConfig)}>Ver exemplo do certificado</button>
        </div>
      )}
    </div>
  );
}

// ── Certificados da turma (visão do gestor) ────────────────────────────────
// Emitir gera o registro com os dados do momento (nome, curso, carga horária,
// período da turma e aproveitamento); o PDF sai do modelo em utils/certificado.
// Anexar guarda um PDF pronto no lugar do modelo.
function CertificadosDaTurma({ turma, curso, alunos, testes, respostas, certificados, setCertificados, certConfig = {} }) {
  const [aberto, setAberto] = useState(true);
  const [enviandoId, setEnviandoId] = useState(null);
  const matriculados = alunos
    .filter(u => (turma.alunosIds || []).includes(String(u.id)))
    .sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR'));
  const doAluno = (u) => certificados.find(c => String(c.turmaId) === String(turma.id) && String(c.alunoId) === String(u.id));
  const emitidos = matriculados.filter(doAluno).length;

  const montar = (u, extra = {}) => {
    const nota = aproveitamentoNaTurma(u.id, turma, testes, respostas);
    const id = novoId();
    return {
      id, codigo: codigoCertificado(id), turmaId: turma.id, cursoId: curso.id, alunoId: String(u.id),
      alunoNome: u.nome || u.email, cursoNome: curso.nome, cargaHoraria: curso.cargaHoraria || '',
      instrutorNome: curso.instrutorNome || '', dataInicio: turma.dataInicio || '', dataFim: turma.dataFim || '',
      percentual: nota ? nota.percentual : null, baseNota: nota ? nota.base : null,
      emitidoEm: new Date().toISOString().slice(0, 10), ...extra,
    };
  };
  const emitir = (u) => {
    if (!aproveitamentoNaTurma(u.id, turma, testes, respostas) &&
        !window.confirm(`${u.nome} ainda não respondeu nenhum teste desta turma. Emitir o certificado sem o aproveitamento?`)) return;
    setCertificados([...certificados.filter(c => c !== doAluno(u)), montar(u)]);
    toast.sucesso(`Certificado de ${u.nome} emitido.`);
  };
  const emitirAprovados = () => {
    const novos = matriculados
      .filter(u => !doAluno(u))
      .filter(u => (aproveitamentoNaTurma(u.id, turma, testes, respostas)?.percentual ?? -1) >= 70);
    if (!novos.length) { toast.aviso('Nenhum aluno aprovado (70% ou mais) sem certificado nesta turma.'); return; }
    setCertificados([...certificados, ...novos.map(u => montar(u))]);
    toast.sucesso(`${novos.length} certificado(s) emitido(s).`);
  };
  const anexar = async (u, file) => {
    if (!file) return;
    setEnviandoId(u.id);
    try {
      const arquivo = await uploadMaterial(file, `certificados-${turma.id}`);
      const antigo = doAluno(u);
      if (antigo?.arquivo?.path) excluirArquivoMaterial(antigo.arquivo.path);
      setCertificados([...certificados.filter(c => c !== antigo), montar(u, { arquivo })]);
      toast.sucesso(`Certificado de ${u.nome} anexado.`);
    } catch (e) { toast.erro(e.message); } finally { setEnviandoId(null); }
  };
  const remover = (u) => {
    const c = doAluno(u);
    if (!c || !window.confirm(`Remover o certificado de ${u.nome}? O aluno deixa de vê-lo.`)) return;
    if (c.arquivo?.path) excluirArquivoMaterial(c.arquivo.path);
    setCertificados(certificados.filter(x => x !== c));
  };

  const btn = { fontSize: 12, padding: '4px 10px', borderRadius: 8, border: '1px solid #e5e0ef', background: '#ffffff', color: '#1c1530', cursor: 'pointer', fontWeight: 600, minHeight: 0 };
  return (
    <div style={{ marginTop: '0.75rem', borderTop: '1px dashed #e5e0ef', paddingTop: '0.75rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <button style={{ ...btn, border: 'none', background: 'transparent', padding: 0, color: '#5f5874', fontWeight: 700 }} onClick={() => setAberto(!aberto)}>
          {aberto ? '▾' : '▸'} CERTIFICADOS ({emitidos}/{matriculados.length})
        </button>
        {aberto && matriculados.length > 0 && (
          <button style={{ ...btn, background: '#7c3aed', color: '#fff', border: 'none' }} onClick={emitirAprovados}>Emitir para todos os aprovados</button>
        )}
      </div>
      {aberto && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
          {matriculados.length === 0 && <div style={{ fontSize: 12, color: '#64748b' }}>Nenhum aluno matriculado.</div>}
          {matriculados.map(u => {
            const c = doAluno(u);
            const nota = aproveitamentoNaTurma(u.id, turma, testes, respostas);
            return (
              <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '6px 10px', border: '1px solid #eeeaf5', borderRadius: 8 }}>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1c1530' }}>{u.nome}</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>
                    {nota ? `Aproveitamento ${nota.percentual}% (${nota.base})` : 'Ainda sem teste respondido'}
                    {c && ` · ${c.arquivo ? 'PDF anexado' : 'emitido'} em ${dataPorExtenso(c.emitidoEm)}`}
                  </div>
                </div>
                {c && <button style={{ ...btn, color: '#047857', borderColor: '#a7e3c8', background: '#e2f5ec' }} onClick={() => abrirCertificado(c, certConfig)}>🏅 Ver</button>}
                <button style={btn} onClick={() => emitir(u)}>{c && !c.arquivo ? 'Emitir de novo' : 'Emitir'}</button>
                <label style={{ ...btn, cursor: enviandoId === u.id ? 'wait' : 'pointer' }}>
                  {enviandoId === u.id ? 'Enviando...' : 'Anexar PDF'}
                  <input type="file" accept="application/pdf" hidden disabled={enviandoId === u.id} onChange={e => { anexar(u, e.target.files?.[0]); e.target.value = ''; }} />
                </label>
                {c && <button style={{ ...btn, color: '#dc2626' }} onClick={() => remover(u)}>Remover</button>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Abre o certificado: PDF anexado por URL assinada; emitido, desenhado na hora.
async function abrirCertificado(c, cfg = {}) {
  if (c.arquivo?.path) {
    try { window.open(await urlMaterial(c.arquivo.path), '_blank', 'noopener'); } catch (e) { toast.erro(e.message); }
    return;
  }
  try { await baixarCertificado(c, cfg); } catch { toast.erro('Não foi possível gerar o certificado. Tente novamente.'); }
}

// ── ABA: Mural de avisos ───────────────────────────────────────────────────
// Gestor publica avisos (gerais, por curso ou por turma). O aluno vê os avisos
// que valem para ele, mais recentes primeiro, e os próximos eventos do
// calendário das turmas dele (30/10/2026).
function AbaMural({ avisos = [], setAvisos, agendaTr = [], cursos, turmas, isGestor, turmasDoAluno, cursosDoAluno, usuarioAtual }) {
  const [form, setForm] = useState(null);
  const hojeIso = new Date().toISOString().slice(0, 10);
  const idsTurmas = new Set(turmasDoAluno.map(t => String(t.id)));
  const idsCursos = new Set(cursosDoAluno.map(c => String(c.id)));
  const valeParaAluno = (x) => x.turmaId
    ? idsTurmas.has(String(x.turmaId))
    : (!x.cursoId || idsCursos.has(String(x.cursoId)));
  const nomeCurso = (id) => cursos.find(c => String(c.id) === String(id))?.nome;
  const nomeTurma = (id) => turmas.find(t => String(t.id) === String(id))?.nome;
  const destino = (x) => [nomeCurso(x.cursoId), nomeTurma(x.turmaId)].filter(Boolean).join(' · ') || 'Todos os alunos';
  const porNome = (a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR');

  const lista = [...avisos]
    .filter(a => isGestor || valeParaAluno(a))
    .sort((a, b) => String(b.publicadoEm).localeCompare(String(a.publicadoEm)));
  const proximos = agendaTr
    .filter(e => e.data && e.data >= hojeIso && (isGestor || valeParaAluno(e)))
    .sort((a, b) => (a.data + (a.hora || '')).localeCompare(b.data + (b.hora || '')))
    .slice(0, 10);

  const salvar = () => {
    if (!form.titulo?.trim() || !form.texto?.trim()) { toast.aviso('Informe o título e o texto do aviso.'); return; }
    if (form.id) setAvisos(avisos.map(a => a.id === form.id ? form : a));
    else setAvisos([...avisos, { ...form, id: novoId(), publicadoEm: new Date().toISOString(), autor: usuarioAtual?.nome || '' }]);
    toast.sucesso(form.id ? 'Aviso atualizado.' : 'Aviso publicado.');
    setForm(null);
  };
  const excluir = (id) => {
    if (!window.confirm('Excluir este aviso?')) return;
    setAvisos(avisos.filter(a => a.id !== id));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
      <div style={s.secao}>
        <div style={s.secaoHeader}>
          <div style={s.secaoTitulo}>📢 Mural de avisos</div>
          {isGestor && !form && <button style={s.btnNovo} onClick={() => setForm({ titulo: '', texto: '', cursoId: '', turmaId: '' })}><Plus size={14}/> Novo aviso</button>}
        </div>
        {isGestor && form && (
          <div style={s.formCard}>
            <div style={s.formTitulo}>{form.id ? 'Editar aviso' : 'Novo aviso'}</div>
            <div style={s.formRow}>
              <Campo label="Título *"><input style={s.input} value={form.titulo} onChange={e => setForm({ ...form, titulo: e.target.value })} /></Campo>
            </div>
            <div style={s.formRow}>
              <Campo label="Curso">
                <select style={s.input} value={form.cursoId} onChange={e => setForm({ ...form, cursoId: e.target.value, turmaId: '' })}>
                  <option value="">Todos</option>
                  {[...cursos].sort(porNome).map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
              </Campo>
              <Campo label="Turma">
                <select style={s.input} value={form.turmaId} onChange={e => setForm({ ...form, turmaId: e.target.value })}>
                  <option value="">Todas</option>
                  {turmas.filter(t => !form.cursoId || String(t.cursoId) === String(form.cursoId)).sort(porNome).map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
                </select>
              </Campo>
            </div>
            <Campo label="Texto *">
              <textarea style={{ ...s.input, minHeight: 100, resize: 'vertical', fontFamily: 'inherit' }} value={form.texto} onChange={e => setForm({ ...form, texto: e.target.value })} />
            </Campo>
            <div style={s.formAcoes}>
              <button style={s.btnSalvar} onClick={salvar}>{form.id ? 'Salvar' : 'Publicar'}</button>
              <button style={s.btnCancelar} onClick={() => setForm(null)}>Cancelar</button>
            </div>
          </div>
        )}
        {lista.length === 0 && <div style={s.vazio}>Nenhum aviso publicado.</div>}
        {lista.map(a => (
          <div key={a.id} style={{ ...s.card, borderLeft: '4px solid #7c3aed' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
              <div style={s.cardTitulo}>{a.titulo}</div>
              {isGestor && (
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button style={s.btnIcone} onClick={() => setForm(a)} aria-label="Editar aviso"><Edit2 size={14}/></button>
                  <button style={s.btnIcone} onClick={() => excluir(a.id)} aria-label="Excluir aviso"><Trash2 size={14}/></button>
                </div>
              )}
            </div>
            <div style={s.cardMeta}>{a.publicadoEm ? new Date(a.publicadoEm).toLocaleDateString('pt-BR') : ''}{a.autor ? ` · ${a.autor}` : ''} · {destino(a)}</div>
            <div style={{ fontSize: 14, color: '#2d2640', whiteSpace: 'pre-line', marginTop: 8 }}>{a.texto}</div>
          </div>
        ))}
      </div>

      <div style={s.secao}>
        <div style={s.secaoHeader}><div style={s.secaoTitulo}>📅 Próximos eventos</div></div>
        {proximos.length === 0 && <div style={s.vazio}>Nenhum evento agendado.</div>}
        {proximos.map(e => (
          <div key={e.id} style={s.card}>
            <div style={s.cardTitulo}>{e.titulo}</div>
            <div style={s.cardMeta}>{fmtData(e.data)}{e.hora ? ` · ${e.hora}${e.horaFim ? `–${e.horaFim}` : ''}` : ''} · {destino(e)}</div>
            {e.descricao && <div style={{ fontSize: 13, color: '#5f5874', marginTop: 6 }}>{e.descricao}</div>}
            {e.link && <a href={e.link} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: '#7c3aed' }}>Acessar link</a>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── ABA: Meus Certificados (aluno) ─────────────────────────────────────────
function AbaMeusCertificados({ certificados = [], usuarioAtual, certConfig = {} }) {
  const meus = certificados
    .filter(c => String(c.alunoId) === String(usuarioAtual?.id))
    .sort((a, b) => String(b.emitidoEm).localeCompare(String(a.emitidoEm)));
  return (
    <div style={s.secao}>
      <div style={s.secaoHeader}><div style={s.secaoTitulo}>🏅 Meus Certificados</div></div>
      {meus.length === 0 && <div style={s.vazio}>Nenhum certificado emitido ainda. Ele aparece aqui quando o curso for concluído.</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '0.75rem' }}>
        {meus.map(c => (
          <div key={c.id} style={{ ...s.card, display: 'flex', flexDirection: 'column', gap: 6, borderTop: '4px solid #7c3aed' }}>
            <div style={{ fontSize: 26 }}>🏅</div>
            <div style={s.cardTitulo}>{c.cursoNome}</div>
            <div style={s.cardMeta}>
              {c.cargaHoraria && `${String(c.cargaHoraria).replace(/h$/i, '')} horas · `}
              {c.percentual != null && `aproveitamento ${c.percentual}% · `}
              emitido em {dataPorExtenso(c.emitidoEm)}
            </div>
            <button style={{ ...s.btnNovo, alignSelf: 'flex-start', marginTop: 4 }} onClick={() => abrirCertificado(c, certConfig)}>⬇ Baixar certificado</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Seletor de alunos da turma: matriculados como etiquetas + busca ────────
function SeletorAlunos({ alunos, selecionados, onToggle }) {
  const [busca, setBusca] = useState('');
  const [aberto, setAberto] = useState(false);
  const ids = selecionados.map(String);
  const porNome = (a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR');
  const matriculados = alunos.filter(u => ids.includes(String(u.id))).sort(porNome);
  const termo = busca.trim().toLocaleLowerCase('pt-BR');
  const opcoes = alunos
    .filter(u => !ids.includes(String(u.id)))
    .filter(u => !termo || [u.nome, u.email, u.empresa].some(v => (v || '').toLocaleLowerCase('pt-BR').includes(termo)))
    .sort(porNome)
    .slice(0, 30);

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
      {matriculados.length > 0 ? (
        <div style={{ display:'flex', flexWrap:'wrap', gap:'0.375rem' }}>
          {matriculados.map(u => (
            <span key={u.id} style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:12, padding:'4px 6px 4px 10px', borderRadius:20, background:'#ede6fb', color:'#5b21b6', fontWeight:600 }}>
              {u.nome}{u.empresa ? <span style={{ fontWeight:400, color:'#6d28d9' }}>· {u.empresa}</span> : null}
              <button type="button" onClick={() => onToggle(String(u.id))} aria-label={`Remover ${u.nome}`} title="Remover da turma"
                style={{ border:'none', background:'transparent', color:'#5b21b6', cursor:'pointer', padding:0, minHeight:0, display:'flex' }}>
                <X size={13}/>
              </button>
            </span>
          ))}
        </div>
      ) : (
        <div style={{ fontSize:12, color:'#64748b' }}>Nenhum aluno matriculado.</div>
      )}

      <div style={{ position:'relative', maxWidth:420 }}>
        <input
          style={{ width:'100%', background:'#ffffff', border:'1px solid #e5e0ef', borderRadius:8, padding:'8px 12px', fontSize:13, color:'#1c1530' }}
          type="search"
          name="busca-aluno-turma"
          autoComplete="off"
          data-lpignore="true"
          data-form-type="other"
          placeholder="🔍 Buscar aluno para matricular (nome, e-mail ou empresa)"
          value={busca}
          onChange={e => { setBusca(e.target.value); setAberto(true); }}
          onFocus={() => setAberto(true)}
          onBlur={() => setTimeout(() => setAberto(false), 150)}
        />
        {aberto && (
          <div style={{ position:'absolute', top:'calc(100% + 4px)', left:0, right:0, zIndex:30, background:'#ffffff', border:'1px solid #e5e0ef', borderRadius:8, boxShadow:'0 8px 24px rgba(28,21,48,0.12)', maxHeight:260, overflowY:'auto' }}>
            {opcoes.length === 0 && (
              <div style={{ padding:'10px 12px', fontSize:12, color:'#64748b' }}>
                {termo ? 'Nenhum aluno encontrado.' : 'Todos os alunos já estão matriculados.'}
              </div>
            )}
            {opcoes.map(u => (
              <button key={u.id} type="button"
                onMouseDown={e => e.preventDefault()}
                onClick={() => { onToggle(String(u.id)); setBusca(''); }}
                style={{ display:'flex', flexDirection:'column', alignItems:'flex-start', width:'100%', textAlign:'left', padding:'8px 12px', border:'none', borderBottom:'1px solid #f3f0f9', background:'transparent', cursor:'pointer' }}>
                <span style={{ fontSize:13, color:'#1c1530', fontWeight:600 }}>{u.nome}</span>
                <span style={{ fontSize:11, color:'#64748b' }}>{[u.empresa, u.email].filter(Boolean).join(' · ')}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── ABA: Aulas ─────────────────────────────────────────────────────────────
// Vídeo anexado à aula: toca por URL assinada (bucket privado, mig 031).
// A URL vence em 4h; se vencer com a aula aberta, gera outra ao dar erro.
function VideoDaAula({ path, titulo, permitirBaixar, onEnded }) {
  const [url, setUrl] = useState(null);
  const [erro, setErro] = useState('');
  const tentativas = useRef(0);
  const carregar = React.useCallback(() => {
    let vivo = true;
    urlVideoAula(path).then(u => vivo && setUrl(u)).catch(e => vivo && setErro(e.message));
    return () => { vivo = false; };
  }, [path]);
  useEffect(() => { tentativas.current = 0; setUrl(null); setErro(''); return carregar(); }, [carregar]);

  if (erro) return <div style={{ padding:'1rem', color:'#dc2626', fontSize:13 }}>{erro}</div>;
  if (!url) return <div style={{ padding:'1rem', color:'#5f5874', fontSize:13 }}>Carregando o vídeo...</div>;
  return (
    <video
      key={url}
      src={url}
      title={titulo}
      controls
      preload="metadata"
      playsInline
      controlsList={permitirBaixar ? undefined : 'nodownload'}
      onContextMenu={e => { if (!permitirBaixar) e.preventDefault(); }}
      onEnded={onEnded}
      onError={() => {
        if (tentativas.current >= 1) { setErro('Não foi possível reproduzir o vídeo. Atualize a página e tente novamente.'); return; }
        tentativas.current += 1;
        carregar();
      }}
      style={{ width:'100%', maxHeight:'75vh', borderRadius:10, background:'#000', display:'block' }}
    />
  );
}

// ── Aula assistida (tabela aulas_assistidas, mig 032) ──────────────────────
// Uma linha por (aula, pessoa). Cada um vê os próprios registros; a equipe do
// Ensino vê todos (RLS), para saber quem da turma já assistiu.
const fmtDataHora = (iso) => iso ? new Date(iso).toLocaleDateString('pt-BR') : '—';

function useAulasAssistidas(meuId) {
  const [linhas, setLinhas] = useState([]);
  useEffect(() => {
    if (!meuId) return;
    let vivo = true;
    (async () => {
      // Pagina de 1000 em 1000: o PostgREST corta calado acima disso.
      const todas = [];
      for (let de = 0; ; de += 1000) {
        const { data, error } = await supabase.from('aulas_assistidas')
          .select('aula_id, curso_id, aluno_id, assistida_em, automatico')
          .order('assistida_em', { ascending: true })
          .range(de, de + 999);
        if (error) return;
        todas.push(...(data || []));
        if (!data || data.length < 1000) break;
      }
      if (vivo) setLinhas(todas);
    })();
    return () => { vivo = false; };
  }, [meuId]);

  const minha = (aulaId) => linhas.find(l => l.aula_id === String(aulaId) && l.aluno_id === meuId);
  const daAula = (aulaId) => linhas.filter(l => l.aula_id === String(aulaId));

  const marcar = async (aula, automatico = false) => {
    if (!meuId || minha(aula.id)) return;
    const { data, error } = await supabase.from('aulas_assistidas')
      .insert({ aula_id: String(aula.id), curso_id: String(aula.cursoId), aluno_id: meuId, automatico })
      .select('aula_id, curso_id, aluno_id, assistida_em, automatico')
      .single();
    if (error && error.code !== '23505') { toast.erro('Não foi possível registrar a aula como assistida. Tente novamente.'); return; }
    const linha = data || { aula_id: String(aula.id), curso_id: String(aula.cursoId), aluno_id: meuId, assistida_em: new Date().toISOString(), automatico };
    setLinhas(ls => [...ls.filter(l => !(l.aula_id === linha.aula_id && l.aluno_id === meuId)), linha]);
    toast.sucesso(automatico ? 'Vídeo concluído. Aula registrada como assistida.' : 'Aula registrada como assistida.');
  };

  const desmarcar = async (aula) => {
    const { error } = await supabase.from('aulas_assistidas')
      .delete().eq('aula_id', String(aula.id)).eq('aluno_id', meuId);
    if (error) { toast.erro('Não foi possível desmarcar a aula. Tente novamente.'); return; }
    setLinhas(ls => ls.filter(l => !(l.aula_id === String(aula.id) && l.aluno_id === meuId)));
  };

  return { minha, daAula, marcar, desmarcar };
}

// Botão abaixo do player: "Marcar como assistida" ↔ selo "Assistida em ...".
function MarcarAssistida({ registro, onMarcar, onDesmarcar }) {
  const [ocupado, setOcupado] = useState(false);
  const rodar = async (fn) => { setOcupado(true); try { await fn(); } finally { setOcupado(false); } };
  if (registro) {
    return (
      <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', marginTop:'0.75rem' }}>
        <span style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'6px 12px', borderRadius:999, background:'rgba(16,185,129,0.12)', color:'#047857', fontSize:13, fontWeight:700 }}>
          <CheckCircle size={15}/> Assistida em {fmtDataHora(registro.assistida_em)}
        </span>
        <button type="button" style={{ ...s.btnCancelar, padding:'6px 12px', fontSize:12 }} disabled={ocupado} onClick={() => rodar(onDesmarcar)}>Desmarcar</button>
      </div>
    );
  }
  return (
    <div style={{ marginTop:'0.75rem' }}>
      <button type="button" disabled={ocupado} onClick={() => rodar(onMarcar)}
        style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'8px 16px', borderRadius:8, border:'1px solid #10b981', background:'#ffffff', color:'#047857', fontSize:13, fontWeight:700, cursor: ocupado ? 'wait' : 'pointer' }}>
        <CheckCircle size={15}/> Marcar como assistida
      </button>
    </div>
  );
}

// Visão da equipe: quem dos alunos matriculados nas turmas do curso já assistiu.
function AssistidaPelaTurma({ registros, matriculados, usuarios }) {
  const [aberto, setAberto] = useState(false);
  const porId = new Map(registros.map(r => [r.aluno_id, r]));
  const lista = matriculados
    .map(id => ({ id, nome: usuarios.find(u => String(u.id) === id)?.nome || 'Aluno', reg: porId.get(id) }))
    .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
  const qtd = lista.filter(a => a.reg).length;
  return (
    <div style={{ marginTop:'0.75rem', padding:'0.625rem 0.75rem', border:'1px solid #e5e0ef', borderRadius:8, background:'#f7f5fb' }}>
      <button type="button" onClick={() => setAberto(!aberto)} aria-expanded={aberto}
        style={{ background:'none', border:'none', padding:0, cursor:'pointer', fontSize:12, fontWeight:700, color:'#5f5874' }}>
        {aberto ? '▾' : '▸'} ASSISTIDA POR {qtd} DE {lista.length} ALUNO{lista.length !== 1 ? 'S' : ''} MATRICULADO{lista.length !== 1 ? 'S' : ''}
      </button>
      {aberto && (
        <div style={{ marginTop:'0.5rem', display:'flex', flexDirection:'column', gap:4 }}>
          {lista.length === 0 && <div style={{ fontSize:12, color:'#64748b' }}>Nenhum aluno matriculado nas turmas deste curso.</div>}
          {lista.map(a => (
            <div key={a.id} style={{ display:'flex', justifyContent:'space-between', gap:8, fontSize:13, color:'#1c1530' }}>
              <span>{a.nome}</span>
              {a.reg
                ? <span style={{ color:'#047857', fontWeight:600 }}>✓ {fmtDataHora(a.reg.assistida_em)}{a.reg.automatico ? ' (fim do vídeo)' : ''}</span>
                : <span style={{ color:'#94a3b8' }}>Não assistiu</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AbaAulas({ cursos, turmas = [], usuarios = [], usuarioAtual, aulas, setAulas, isGestor, cursosDoAluno, turmasDoAluno }) {
  const [filtroCurso, setFiltroCurso] = useState('');
  const [form, setForm] = useState(null);
  const [aulaAberta, setAulaAberta] = useState(null);
  const meuId = usuarioAtual?.id ? String(usuarioAtual.id) : '';
  const assistidas = useAulasAssistidas(meuId);
  const matriculadosDoCurso = (cursoId) => [...new Set(
    turmas.filter(t => String(t.cursoId) === String(cursoId)).flatMap(t => (t.alunosIds || []).map(String))
  )];

  const listaCursos = isGestor ? cursos : cursosDoAluno;

  const listaCursosFiltrados = listaCursos.filter(c =>
    c.nome.toLowerCase().includes(filtroCurso.toLowerCase())
  );

  // Envio do vídeo em andamento: { pct, cancelar }.
  const [envio, setEnvio] = useState(null);

  // Vídeo enviado nesta edição e ainda não salvo: sai do Storage se for
  // trocado, removido ou se a edição for cancelada.
  const descartarVideoNaoSalvo = (f) => {
    if (f?.video?.path && f.video.path !== f.videoOriginal) excluirVideoAula(f.video.path);
  };

  const anexarVideo = async (file) => {
    if (!file || envio) return;
    const idAula = form.id || form.idNovo || novoId();
    const { promessa, cancelar } = uploadVideoAula(file, idAula, pct => setEnvio(e => e && { ...e, pct }));
    setEnvio({ pct: 0, cancelar });
    try {
      const video = await promessa;
      setForm(f => {
        descartarVideoNaoSalvo(f);
        return { ...f, idNovo: f.id ? f.idNovo : idAula, video };
      });
      toast.sucesso('Vídeo anexado. Clique em Salvar para concluir.');
    } catch (e) {
      if (e.message === 'Envio cancelado.') toast.aviso(e.message); else toast.erro(e.message);
    } finally {
      setEnvio(null);
    }
  };

  const removerVideo = () => {
    if (!window.confirm('Remover o vídeo desta aula? O arquivo será apagado ao salvar.')) return;
    descartarVideoNaoSalvo(form);
    setForm(f => ({ ...f, video: null }));
  };

  const salvar = () => {
    if (!form.titulo || !form.cursoId) { toast.aviso('Informe título e curso.'); return; }
    if (envio) { toast.aviso('Aguarde o envio do vídeo terminar.'); return; }
    const { idNovo, videoOriginal, fonteVideo, ...dados } = form;
    const porArquivo = dados.tipo === 'gravada' && fonteVideo === 'arquivo';
    if (porArquivo && !dados.video?.path) { toast.aviso('Anexe o arquivo de vídeo ou escolha a opção YouTube.'); return; }
    if (porArquivo) dados.youtubeUrl = '';
    else {
      // Voltou para YouTube ou virou aula síncrona: o vídeo anexado deixa de valer.
      descartarVideoNaoSalvo(form);
      dados.video = null;
    }
    // Vídeo antigo trocado ou removido: apaga o objeto do Storage.
    if (videoOriginal && videoOriginal !== dados.video?.path) excluirVideoAula(videoOriginal);
    if (dados.id) {
      setAulas(aulas.map(a => a.id === dados.id ? dados : a));
      toast.sucesso('Aula atualizada!');
    } else {
      setAulas([...aulas, { ...dados, id: idNovo || novoId() }]);
      toast.sucesso('Aula cadastrada!');
    }
    setForm(null);
  };

  const cancelar = () => {
    if (envio) { envio.cancelar(); return; }
    descartarVideoNaoSalvo(form);
    setForm(null);
  };

  const editar = (aula) => setForm({ ...aula, fonteVideo: aula.video?.path ? 'arquivo' : 'youtube', videoOriginal: aula.video?.path });

  const excluirAula = (aula) => {
    if (!window.confirm('Excluir aula?')) return;
    setAulas(aulas.filter(a => a.id !== aula.id));
    excluirVideoAula(aula.video?.path);
    toast.sucesso('Aula excluída.');
  };

  const getYoutubeId = (url) => {
    if (!url) return null;
    const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([^&\n?#]+)/);
    return m ? m[1] : null;
  };

  return (
    <div style={s.secao}>
      <div style={s.secaoHeader}>
        <div style={s.secaoTitulo}>Aulas</div>
        <input
          style={{ ...s.input, maxWidth: 280 }}
          placeholder="🔍 Filtrar cursos..."
          value={filtroCurso}
          onChange={e => setFiltroCurso(e.target.value)}
        />
      </div>

      {listaCursosFiltrados.length === 0 && <div style={s.vazio}>Nenhum curso encontrado.</div>}

      {listaCursosFiltrados.map(curso => {
        const aulasDoCurso = aulas.filter(a => String(a.cursoId) === String(curso.id)).sort((a,b) => (a.ordem||0) - (b.ordem||0));
        const modulosDoCurso = [...new Set(aulasDoCurso.map(a => a.modulo || 'Geral'))];
        return (
          <div key={curso.id} style={{ marginBottom:'2rem' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.75rem', padding:'0.5rem 0.75rem', background:'rgba(139,92,246,0.08)', borderRadius:8, borderLeft:'3px solid #8b5cf6' }}>
              <div style={{ fontWeight:600, color:'#2d2640', fontSize:14 }}>🎓 {curso.nome} <span style={{ color:'#5f5874', fontWeight:400, fontSize:12 }}>({aulasDoCurso.length} aula{aulasDoCurso.length !== 1 ? 's' : ''})</span></div>
              {isGestor && !(form?.cursoId === curso.id) && (
                <button style={s.btnNovo} onClick={() => setForm({ cursoId: curso.id, titulo:'', tipo:'gravada', fonteVideo:'youtube', youtubeUrl:'', linkSincrona:'', modulo:'', descricao:'', ordem: aulasDoCurso.length + 1 })}>
                  <Plus size={14}/> Nova Aula
                </button>
              )}
            </div>

            {isGestor && form?.cursoId === curso.id && (
              <div style={s.formCard}>
                <div style={s.formTitulo}>{form.id ? 'Editar Aula' : 'Nova Aula'}</div>
                <div style={s.formRow}>
                  <Campo label="Título *"><input style={s.input} value={form.titulo} onChange={e => setForm({...form, titulo: e.target.value})} /></Campo>
                  <Campo label="Módulo / Unidade"><input style={s.input} value={form.modulo} placeholder="Ex: Módulo 1" onChange={e => setForm({...form, modulo: e.target.value})} /></Campo>
                  <Campo label="Ordem"><input type="number" style={s.input} value={form.ordem} onChange={e => setForm({...form, ordem: parseInt(e.target.value)||1})} /></Campo>
                </div>
                <div style={s.formRow}>
                  <Campo label="Tipo">
                    <select style={s.input} value={form.tipo} onChange={e => setForm({...form, tipo: e.target.value})}>
                      <option value="gravada">🎬 Videoaula Gravada</option>
                      <option value="sincrona">🔴 Aula Síncrona (Link)</option>
                    </select>
                  </Campo>
                  {form.tipo === 'gravada' && (
                    <Campo label="Origem do vídeo">
                      <select style={s.input} value={form.fonteVideo || 'youtube'} disabled={!!envio} onChange={e => setForm({...form, fonteVideo: e.target.value})}>
                        <option value="youtube">Link do YouTube</option>
                        <option value="arquivo">Arquivo de vídeo (MP4, WebM ou MOV)</option>
                      </select>
                    </Campo>
                  )}
                  {form.tipo === 'gravada' && (form.fonteVideo || 'youtube') === 'youtube' && (
                    <Campo label="URL do YouTube"><input style={s.input} value={form.youtubeUrl || ''} placeholder="https://www.youtube.com/watch?v=..." onChange={e => setForm({...form, youtubeUrl: e.target.value})} /></Campo>
                  )}
                  {form.tipo === 'sincrona' && (
                    <Campo label="Link da aula ao vivo"><input style={s.input} value={form.linkSincrona || ''} placeholder="https://teams.microsoft.com/..." onChange={e => setForm({...form, linkSincrona: e.target.value})} /></Campo>
                  )}
                </div>
                {form.tipo === 'gravada' && form.fonteVideo === 'arquivo' && (
                  <Campo label="Arquivo de vídeo *">
                    {envio ? (
                      <div style={{ display:'flex', flexDirection:'column', gap:6, padding:'10px 12px', border:'1px solid #e5e0ef', borderRadius:8, background:'#f7f5fb' }}>
                        <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, color:'#1c1530', fontWeight:600 }}>
                          <span>Enviando o vídeo...</span><span>{envio.pct}%</span>
                        </div>
                        <div role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={envio.pct} aria-label="Progresso do envio do vídeo" style={{ height:8, borderRadius:4, background:'#e5e0ef', overflow:'hidden' }}>
                          <div style={{ width:`${envio.pct}%`, height:'100%', background:'#7c3aed', transition:'width .3s' }} />
                        </div>
                        <div style={{ fontSize:12, color:'#5f5874' }}>Mantenha esta tela aberta até o envio terminar. Se a conexão cair, anexe o mesmo arquivo de novo: o envio continua de onde parou.</div>
                      </div>
                    ) : form.video?.path ? (
                      <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', padding:'8px 12px', border:'1px solid #e5e0ef', borderRadius:8, background:'#f7f5fb' }}>
                        <Play size={16} color="#7c3aed"/>
                        <span style={{ fontSize:13, color:'#1c1530', fontWeight:600, flex:1, minWidth:0, overflow:'hidden', textOverflow:'ellipsis' }}>{form.video.nome}</span>
                        <span style={{ fontSize:12, color:'#5f5874' }}>{(form.video.tamanho / 1024 / 1024).toFixed(1)} MB</span>
                        <label style={{ ...s.btnCancelar, cursor:'pointer' }}>
                          Trocar vídeo
                          <input type="file" accept="video/mp4,video/webm,video/quicktime,.mp4,.m4v,.webm,.mov" hidden onChange={e => { anexarVideo(e.target.files?.[0]); e.target.value = ''; }} />
                        </label>
                        <button type="button" style={{ ...s.btnCancelar, color:'#dc2626' }} onClick={removerVideo}>Remover</button>
                      </div>
                    ) : (
                      <label style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8, padding:'14px', border:'1.5px dashed #c9c2d8', borderRadius:8, background:'#ffffff', color:'#5b21b6', fontWeight:700, fontSize:13, cursor:'pointer' }}>
                        <Play size={16}/> Anexar vídeo (MP4, WebM ou MOV)
                        <input type="file" accept="video/mp4,video/webm,video/quicktime,.mp4,.m4v,.webm,.mov" hidden onChange={e => { anexarVideo(e.target.files?.[0]); e.target.value = ''; }} />
                      </label>
                    )}
                  </Campo>
                )}
                <Campo label="Descrição / Ementa"><textarea style={{...s.input, height:70, resize:'vertical'}} value={form.descricao} onChange={e => setForm({...form, descricao: e.target.value})} /></Campo>
                <div style={s.formAcoes}>
                  <button style={{ ...s.btnSalvar, opacity: envio ? 0.6 : 1, cursor: envio ? 'wait' : 'pointer' }} onClick={salvar} disabled={!!envio}>Salvar</button>
                  <button style={s.btnCancelar} onClick={cancelar}>{envio ? 'Cancelar envio' : 'Cancelar'}</button>
                </div>
              </div>
            )}

            {aulasDoCurso.length === 0 && !(form?.cursoId === curso.id) && <div style={s.vazio}>Nenhuma aula cadastrada neste curso.</div>}

            {modulosDoCurso.map(mod => {
              const aulasDoMod = aulasDoCurso.filter(a => (a.modulo || 'Geral') === mod);
              return (
                <div key={mod} style={{ marginBottom:'1.5rem' }}>
                  <div style={{ fontSize:12, color:'#8b5cf6', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'0.5rem' }}>{mod}</div>
                  {aulasDoMod.map(aula => {
                    const ytId = getYoutubeId(aula.youtubeUrl);
                    const aberta = aulaAberta === aula.id;
                    const temVideo = aula.tipo === 'gravada' && (aula.video?.path || ytId);
                    const minhaAssistida = assistidas.minha(aula.id);
                    return (
                      <div key={aula.id} style={s.card}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                          <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', cursor:'pointer', flex:1 }} onClick={() => setAulaAberta(aberta ? null : aula.id)}>
                            <div style={{ width:36, height:36, borderRadius:8, background: aula.tipo==='sincrona' ? 'rgba(239,68,68,0.15)' : 'rgba(139,92,246,0.15)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                              {aula.tipo === 'sincrona' ? <ExternalLink size={16} color="#ef4444"/> : <Play size={16} color="#8b5cf6"/>}
                            </div>
                            <div>
                              <div style={{ ...s.cardTitulo, display:'flex', alignItems:'center', gap:6 }}>
                                {aula.titulo}
                                {minhaAssistida && (
                                  <span title={`Assistida em ${fmtDataHora(minhaAssistida.assistida_em)}`} aria-label="Aula assistida" style={{ display:'inline-flex' }}>
                                    <CheckCircle size={15} color="#10b981"/>
                                  </span>
                                )}
                              </div>
                              <div style={s.cardMeta}>{aula.tipo === 'sincrona' ? '🔴 Aula Síncrona' : '🎬 Videoaula'}{aula.descricao && ` · ${aula.descricao.substring(0,60)}${aula.descricao.length>60?'...':''}`}</div>
                            </div>
                          </div>
                          <div style={{ display:'flex', gap:4 }}>
                            {isGestor && <button style={s.btnIcone} onClick={() => editar(aula)} disabled={!!envio} aria-label="Editar aula" title="Editar aula"><Edit2 size={13}/></button>}
                            {isGestor && <button style={{...s.btnIcone, color:'#ef4444'}} onClick={() => excluirAula(aula)} aria-label="Excluir aula" title="Excluir aula"><Trash2 size={13}/></button>}
                            {aberta ? <ChevronUp size={16} color="#5f5874"/> : <ChevronDown size={16} color="#5f5874"/>}
                          </div>
                        </div>

                        {aberta && (
                          <div style={{ marginTop:'1rem', borderTop:'1px solid #e5e0ef', paddingTop:'1rem' }}>
                            {aula.descricao && <p style={{ color:'#64748b', fontSize:13, marginBottom:'1rem', whiteSpace:'pre-wrap' }}>{aula.descricao}</p>}
                            {aula.tipo === 'gravada' && aula.video?.path && (
                              <VideoDaAula path={aula.video.path} titulo={aula.titulo} permitirBaixar={isGestor}
                                onEnded={() => { if (!assistidas.minha(aula.id)) assistidas.marcar(aula, true); }} />
                            )}
                            {aula.tipo === 'gravada' && !aula.video?.path && ytId && (
                              <div style={{ position:'relative', paddingBottom:'56.25%', height:0, borderRadius:10, overflow:'hidden', background:'#000' }}>
                                <iframe
                                  style={{ position:'absolute', top:0, left:0, width:'100%', height:'100%', border:'none' }}
                                  src={`https://www.youtube-nocookie.com/embed/${ytId}?rel=0&modestbranding=1`}
                                  title={aula.titulo}
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                />
                              </div>
                            )}
                            {aula.tipo === 'gravada' && !aula.video?.path && !ytId && aula.youtubeUrl && (
                              <div style={{ color:'#f59e0b', fontSize:13 }}>⚠️ URL do YouTube inválida. Verifique o link.</div>
                            )}
                            {temVideo && meuId && (
                              <MarcarAssistida
                                registro={minhaAssistida}
                                onMarcar={() => assistidas.marcar(aula)}
                                onDesmarcar={() => assistidas.desmarcar(aula)}
                              />
                            )}
                            {temVideo && isGestor && (
                              <AssistidaPelaTurma registros={assistidas.daAula(aula.id)} matriculados={matriculadosDoCurso(aula.cursoId)} usuarios={usuarios} />
                            )}
                            {aula.tipo === 'sincrona' && aula.linkSincrona && (
                              <a href={aula.linkSincrona} target="_blank" rel="noopener noreferrer" style={{ display:'inline-flex', alignItems:'center', gap:6, background:'#ef4444', color:'white', borderRadius:8, padding:'10px 20px', textDecoration:'none', fontWeight:600, fontSize:14 }}>
                                <ExternalLink size={15}/> Entrar na Aula ao Vivo
                              </a>
                            )}
                            {aula.tipo === 'sincrona' && !aula.linkSincrona && (
                              <div style={{ color:'#64748b', fontSize:13 }}>Link da aula não informado ainda.</div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

// ── ABA: Material de Apoio ─────────────────────────────────────────────────
// PDF anexado ao material: abre por URL assinada de 1h, sem a barra do leitor.
// Sem sandbox no iframe: o leitor de PDF do Chrome não abre em iframe com sandbox.
function PdfDoMaterial({ path, titulo }) {
  const [url, setUrl] = useState(null);
  const [erro, setErro] = useState('');
  useEffect(() => {
    let vivo = true;
    urlMaterial(path).then(u => vivo && setUrl(u)).catch(e => vivo && setErro(e.message));
    return () => { vivo = false; };
  }, [path]);
  // Zoom: 0 = ajustar à largura; demais valores em %. Como a barra do leitor
  // fica oculta, o zoom vai no fragmento da URL e o iframe é remontado (key).
  const [zoom, setZoom] = useState(0);
  const caixaRef = useRef(null);
  const [cheia, setCheia] = useState(false);
  useEffect(() => {
    const f = () => setCheia(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', f);
    return () => document.removeEventListener('fullscreenchange', f);
  }, []);
  const NIVEIS = [50, 75, 100, 125, 150, 200, 300];
  const atual = zoom || 100;
  const menos = () => setZoom(NIVEIS.slice().reverse().find(n => n < atual) || NIVEIS[0]);
  const mais = () => setZoom(NIVEIS.find(n => n > atual) || NIVEIS[NIVEIS.length - 1]);
  const telaCheia = () => {
    const el = caixaRef.current;
    if (document.fullscreenElement) document.exitFullscreen?.();
    else el?.requestFullscreen?.().catch(() => {});
  };

  if (erro) return <div style={{ padding:'1rem', color:'#dc2626', fontSize:13 }}>{erro}</div>;
  if (!url) return <div style={{ padding:'1rem', color:'#5f5874', fontSize:13 }}>Carregando o material...</div>;
  const btn = { display:'inline-flex', alignItems:'center', justifyContent:'center', minWidth:36, height:34, padding:'0 10px', border:'1px solid #e5e0ef', borderRadius:8, background:'#ffffff', color:'#1c1530', fontSize:14, fontWeight:700, cursor:'pointer' };
  return (
    <div ref={caixaRef} style={{ display:'flex', flexDirection:'column', gap:8, background:'#ffffff' }}>
      <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
        <button type="button" style={btn} onClick={menos} aria-label="Diminuir" title="Diminuir">−</button>
        <span style={{ minWidth:64, textAlign:'center', fontSize:13, fontWeight:700, color:'#5f5874' }}>{zoom ? `${zoom}%` : 'Largura'}</span>
        <button type="button" style={btn} onClick={mais} aria-label="Aumentar" title="Aumentar">+</button>
        <button type="button" style={{ ...btn, fontWeight:600, fontSize:13 }} onClick={() => setZoom(0)}>Ajustar à largura</button>
        <button type="button" style={{ ...btn, fontWeight:600, fontSize:13, marginLeft:'auto' }} onClick={telaCheia}>{cheia ? '✕ Sair da tela cheia' : '⛶ Tela cheia'}</button>
      </div>
      <iframe
        key={zoom}
        src={`${url}#toolbar=0&navpanes=0&${zoom ? `zoom=${zoom}` : 'view=FitH'}`}
        title={titulo}
        style={{ width:'100%', height: cheia ? 'calc(100vh - 50px)' : '75vh', minHeight:500, border:'1px solid #e5e0ef', borderRadius:8 }}
      />
    </div>
  );
}

function AbaMaterial({ cursos, mats, setMats, isGestor, cursosDoAluno }) {
  const [filtroCurso, setFiltroCurso] = useState('');
  const [form, setForm] = useState(null);
  const [matAberto, setMatAberto] = useState(null);

  const listaCursos = isGestor ? cursos : cursosDoAluno;
  const listaCursosFiltrados = listaCursos.filter(c =>
    c.nome.toLowerCase().includes(filtroCurso.toLowerCase())
  );

  const [enviando, setEnviando] = useState(false);

  const anexarPdf = async (file) => {
    if (!file) return;
    setEnviando(true);
    try {
      const idMat = form.id || form.idNovo || novoId();
      const arquivo = await uploadMaterial(file, idMat);
      // Arquivo trocado antes de salvar: o anterior enviado nesta edição sai do Storage.
      if (form.arquivo?.path && form.arquivo.path !== form.arquivoOriginal) excluirArquivoMaterial(form.arquivo.path);
      setForm(f => ({ ...f, idNovo: f.id ? f.idNovo : idMat, arquivo }));
      toast.sucesso('PDF anexado. Clique em Salvar para concluir.');
    } catch (e) {
      toast.erro(e.message);
    } finally {
      setEnviando(false);
    }
  };

  const salvar = () => {
    if (!form.nome || (!form.url && !form.arquivo)) { toast.aviso('Informe o nome e anexe o PDF ou cole o link do material.'); return; }
    const { idNovo, arquivoOriginal, ...dados } = form;
    if (arquivoOriginal && arquivoOriginal !== dados.arquivo?.path) excluirArquivoMaterial(arquivoOriginal);
    if (dados.id) {
      setMats(mats.map(m => m.id === dados.id ? dados : m));
      toast.sucesso('Material atualizado!');
    } else {
      setMats([...mats, { ...dados, id: idNovo || novoId() }]);
      toast.sucesso('Material cadastrado!');
    }
    setForm(null);
  };

  const cancelar = () => {
    // Enviou um PDF e desistiu: não deixa o arquivo solto no Storage.
    if (form?.arquivo?.path && form.arquivo.path !== form.arquivoOriginal) excluirArquivoMaterial(form.arquivo.path);
    setForm(null);
  };

  // Transforma URL do Google Drive para URL de visualização
  const getEmbedUrl = (url) => {
    const driveMatch = url?.match(/\/file\/d\/([^/]+)/);
    if (driveMatch) return `https://drive.google.com/file/d/${driveMatch[1]}/preview`;
    return url;
  };

  return (
    <div style={s.secao}>
      <div style={s.secaoHeader}>
        <div style={s.secaoTitulo}>Material de Apoio</div>
        <input
          style={{ ...s.input, maxWidth: 280 }}
          placeholder="🔍 Filtrar cursos..."
          value={filtroCurso}
          onChange={e => setFiltroCurso(e.target.value)}
        />
      </div>

      {listaCursosFiltrados.length === 0 && <div style={s.vazio}>Nenhum curso encontrado.</div>}

      {listaCursosFiltrados.map(curso => {
        const matsDoC = mats.filter(m => String(m.cursoId) === String(curso.id));
        return (
          <div key={curso.id} style={{ marginBottom:'2rem' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.75rem', padding:'0.5rem 0.75rem', background:'rgba(16,185,129,0.08)', borderRadius:8, borderLeft:'3px solid #10b981' }}>
              <div style={{ fontWeight:600, color:'#2d2640', fontSize:14 }}>📚 {curso.nome} <span style={{ color:'#5f5874', fontWeight:400, fontSize:12 }}>({matsDoC.length} material{matsDoC.length !== 1 ? 'is' : ''})</span></div>
              {isGestor && !(form?.cursoId === curso.id) && (
                <button style={s.btnNovo} onClick={() => setForm({ cursoId: curso.id, nome:'', url:'', modulo:'', descricao:'' })}>
                  <Plus size={14}/> Novo Material
                </button>
              )}
            </div>

            {isGestor && form?.cursoId === curso.id && (
              <div style={s.formCard}>
                <div style={s.formTitulo}>{form.id ? 'Editar Material' : 'Novo Material'}</div>
                <div style={s.formRow}>
                  <Campo label="Nome *"><input style={s.input} value={form.nome} onChange={e => setForm({...form, nome: e.target.value})} /></Campo>
                  <Campo label="Módulo"><input style={s.input} value={form.modulo} placeholder="Ex: Módulo 1" onChange={e => setForm({...form, modulo: e.target.value})} /></Campo>
                </div>
                <Campo label="Arquivo PDF *">
                  {form.arquivo ? (
                    <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', padding:'8px 12px', border:'1px solid #e5e0ef', borderRadius:8, background:'#f7f5fb' }}>
                      <FileText size={16} color="#7c3aed"/>
                      <span style={{ fontSize:13, color:'#1c1530', fontWeight:600, flex:1, minWidth:0, overflow:'hidden', textOverflow:'ellipsis' }}>{form.arquivo.nome}</span>
                      <span style={{ fontSize:12, color:'#5f5874' }}>{(form.arquivo.tamanho / 1024 / 1024).toFixed(1)} MB</span>
                      <label style={{ ...s.btnCancelar, cursor: enviando ? 'wait' : 'pointer' }}>
                        {enviando ? 'Enviando...' : 'Trocar PDF'}
                        <input type="file" accept="application/pdf" hidden disabled={enviando} onChange={e => { anexarPdf(e.target.files?.[0]); e.target.value = ''; }} />
                      </label>
                    </div>
                  ) : (
                    <label style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8, padding:'14px', border:'1.5px dashed #c9c2d8', borderRadius:8, background:'#ffffff', color:'#5b21b6', fontWeight:700, fontSize:13, cursor: enviando ? 'wait' : 'pointer' }}>
                      <FileText size={16}/> {enviando ? 'Enviando o PDF...' : 'Anexar PDF (até 50 MB)'}
                      <input type="file" accept="application/pdf" hidden disabled={enviando} onChange={e => { anexarPdf(e.target.files?.[0]); e.target.value = ''; }} />
                    </label>
                  )}
                </Campo>
                {!form.arquivo && (
                  <Campo label="Ou cole o link do arquivo (Google Drive)">
                    <input style={s.input} value={form.url || ''} placeholder="https://drive.google.com/file/d/..." onChange={e => setForm({...form, url: e.target.value})} />
                  </Campo>
                )}
                <Campo label="Descrição"><textarea style={{...s.input, height:60, resize:'vertical'}} value={form.descricao} onChange={e => setForm({...form, descricao: e.target.value})} /></Campo>
                <div style={s.formAcoes}>
                  <button style={s.btnSalvar} onClick={salvar} disabled={enviando}>Salvar</button>
                  <button style={s.btnCancelar} onClick={cancelar} disabled={enviando}>Cancelar</button>
                </div>
              </div>
            )}

            {matsDoC.length === 0 && !(form?.cursoId === curso.id) && <div style={s.vazio}>Nenhum material cadastrado neste curso.</div>}

            {matsDoC.map(mat => {
              const aberto = matAberto === mat.id;
              const embedUrl = getEmbedUrl(mat.url);
              return (
                <div key={mat.id} style={s.card}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', cursor:'pointer', flex:1 }} onClick={() => setMatAberto(aberto ? null : mat.id)}>
                      <div style={{ width:36, height:36, borderRadius:8, background:'rgba(16,185,129,0.15)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        <FileText size={16} color="#10b981"/>
                      </div>
                      <div>
                        <div style={s.cardTitulo}>{mat.nome}</div>
                        <div style={s.cardMeta}>{mat.modulo && `${mat.modulo} · `}{mat.descricao && mat.descricao.substring(0,60)}</div>
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:4 }}>
                      {isGestor && <button style={s.btnIcone} onClick={() => setForm({...mat, arquivoOriginal: mat.arquivo?.path})}><Edit2 size={13}/></button>}
                      {isGestor && <button style={{...s.btnIcone, color:'#ef4444'}} onClick={() => { if(window.confirm('Excluir material?')) { setMats(mats.filter(m => m.id !== mat.id)); excluirArquivoMaterial(mat.arquivo?.path); toast.sucesso('Material excluído.'); }}}><Trash2 size={13}/></button>}
                      {aberto ? <ChevronUp size={16} color="#5f5874"/> : <ChevronDown size={16} color="#5f5874"/>}
                    </div>
                  </div>
                  {aberto && (
                    <div style={{ marginTop:'1rem', borderTop:'1px solid #e5e0ef', paddingTop:'1rem' }}>
                      <div style={{ position:'relative', userSelect:'none' }} onContextMenu={e => { if(!isGestor) e.preventDefault(); }}>
                        {mat.arquivo?.path ? (
                          <PdfDoMaterial path={mat.arquivo.path} titulo={mat.nome} />
                        ) : (
                          <iframe
                            src={embedUrl}
                            style={{ width:'100%', height:600, border:'none', borderRadius:8 }}
                            title={mat.nome}
                            sandbox="allow-same-origin allow-scripts allow-presentation"
                          />
                        )}
                        {!isGestor && (
                          <style>{`@media print { .mat-viewer { display: none !important; } }`}</style>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

// ── Banco de questões pré-definidas ────────────────────────────────────────
const QUESTOES_ISO9001 = [
  {
    pasta: 'Contexto da Organização (Seção 4)',
    enunciado: 'De acordo com a ISO 9001:2015, o que a organização deve determinar ao analisar seu contexto?',
    pontos: 1,
    alternativas: [
      { texto: 'As questões externas e internas relevantes para seus propósitos e que afetam sua capacidade de atingir os resultados pretendidos do SGQ', correta: true },
      { texto: 'Apenas os requisitos dos clientes e das partes interessadas externas', correta: false },
      { texto: 'Somente os riscos associados ao processo produtivo', correta: false },
      { texto: 'Os objetivos de qualidade anuais da organização', correta: false },
    ],
  },
  {
    pasta: 'Contexto da Organização (Seção 4)',
    enunciado: 'O que significa "parte interessada" no contexto da ISO 9001:2015?',
    pontos: 1,
    alternativas: [
      { texto: 'Pessoa ou organização que pode afetar, ser afetada ou perceber-se afetada por uma decisão ou atividade da organização', correta: true },
      { texto: 'Apenas clientes finais que adquirem os produtos ou serviços', correta: false },
      { texto: 'Somente acionistas e investidores da empresa', correta: false },
      { texto: 'Fornecedores e parceiros de negócios exclusivamente', correta: false },
    ],
  },
  {
    pasta: 'Liderança (Seção 5)',
    enunciado: 'Qual é a responsabilidade da alta direção em relação ao SGQ segundo a ISO 9001:2015?',
    pontos: 1,
    alternativas: [
      { texto: 'Demonstrar liderança e comprometimento, assegurando que a política e os objetivos da qualidade sejam estabelecidos e compatíveis com o contexto e a direção estratégica', correta: true },
      { texto: 'Delegar todas as responsabilidades do SGQ ao representante da qualidade', correta: false },
      { texto: 'Apenas aprovar o manual da qualidade anualmente', correta: false },
      { texto: 'Garantir que a organização obtenha a certificação ISO dentro do prazo', correta: false },
    ],
  },
  {
    pasta: 'Liderança (Seção 5)',
    enunciado: 'O que a política da qualidade deve incluir obrigatoriamente segundo a ISO 9001:2015?',
    pontos: 1,
    alternativas: [
      { texto: 'Comprometimento com o atendimento de requisitos aplicáveis e com a melhoria contínua do SGQ', correta: true },
      { texto: 'Os procedimentos detalhados de cada processo da organização', correta: false },
      { texto: 'O cronograma de auditorias internas do ano', correta: false },
      { texto: 'Os indicadores de desempenho e suas metas numéricas', correta: false },
    ],
  },
  {
    pasta: 'Planejamento (Seção 6)',
    enunciado: 'Na abordagem de riscos e oportunidades da ISO 9001:2015, o que a organização deve fazer?',
    pontos: 1,
    alternativas: [
      { texto: 'Determinar os riscos e oportunidades que precisam ser tratados para assegurar que o SGQ alcance seus resultados pretendidos', correta: true },
      { texto: 'Eliminar todos os riscos identificados antes de implementar qualquer processo', correta: false },
      { texto: 'Contratar consultoria externa para gerenciar todos os riscos', correta: false },
      { texto: 'Registrar os riscos apenas quando ocorre uma não conformidade', correta: false },
    ],
  },
  {
    pasta: 'Planejamento (Seção 6)',
    enunciado: 'Quais características os objetivos da qualidade devem ter segundo a ISO 9001:2015?',
    pontos: 1,
    alternativas: [
      { texto: 'Ser mensuráveis, monitorados, comunicados e atualizados conforme apropriado', correta: true },
      { texto: 'Ser definidos exclusivamente pela alta direção sem participação das equipes', correta: false },
      { texto: 'Ser revisados apenas a cada cinco anos durante o ciclo de certificação', correta: false },
      { texto: 'Ser mantidos em sigilo para evitar pressão sobre a equipe operacional', correta: false },
    ],
  },
  {
    pasta: 'Suporte (Seção 7)',
    enunciado: 'O que a ISO 9001:2015 exige em relação à competência das pessoas que afetam o desempenho do SGQ?',
    pontos: 1,
    alternativas: [
      { texto: 'Determinar a competência necessária, assegurar que as pessoas sejam competentes e, onde aplicável, tomar ações para adquirir a competência necessária', correta: true },
      { texto: 'Contratar apenas pessoas com certificação ISO pessoal para funções críticas', correta: false },
      { texto: 'Exigir que todos os funcionários realizem treinamento externo anualmente', correta: false },
      { texto: 'Manter registros de diplomas universitários de toda a equipe', correta: false },
    ],
  },
  {
    pasta: 'Suporte (Seção 7)',
    enunciado: 'O que a norma ISO 9001:2015 define como "informação documentada"?',
    pontos: 1,
    alternativas: [
      { texto: 'Informação que deve ser controlada e mantida pela organização, em qualquer formato e mídia, de qualquer fonte', correta: true },
      { texto: 'Apenas documentos em papel assinados pela alta direção', correta: false },
      { texto: 'Exclusivamente os registros de auditoria e não conformidades', correta: false },
      { texto: 'Procedimentos operacionais escritos e aprovados pelo setor de qualidade', correta: false },
    ],
  },
  {
    pasta: 'Operação (Seção 8)',
    enunciado: 'O que a organização deve fazer para controlar os processos, produtos e serviços providos externamente?',
    pontos: 1,
    alternativas: [
      { texto: 'Assegurar que processos, produtos e serviços externos estejam conformes com os requisitos especificados, determinando o tipo e extensão do controle', correta: true },
      { texto: 'Visitar obrigatoriamente todos os fornecedores mensalmente', correta: false },
      { texto: 'Exigir que fornecedores possuam certificação ISO 9001 antes de qualquer contratação', correta: false },
      { texto: 'Realizar apenas inspeção no recebimento dos produtos adquiridos', correta: false },
    ],
  },
  {
    pasta: 'Avaliação de Desempenho (Seção 9)',
    enunciado: 'Com que finalidade a organização deve conduzir auditorias internas segundo a ISO 9001:2015?',
    pontos: 1,
    alternativas: [
      { texto: 'Para prover informações sobre se o SGQ está em conformidade com os requisitos da organização e da norma, e se está implementado e mantido eficazmente', correta: true },
      { texto: 'Apenas para preparar a organização para a auditoria de certificação externa', correta: false },
      { texto: 'Para identificar e punir os responsáveis por não conformidades', correta: false },
      { texto: 'Somente quando há reclamações de clientes ou falhas graves no processo', correta: false },
    ],
  },
  {
    pasta: 'Avaliação de Desempenho (Seção 9)',
    enunciado: 'O que deve ser analisado durante a análise crítica pela direção segundo a ISO 9001:2015?',
    pontos: 1,
    alternativas: [
      { texto: 'O desempenho e a eficácia do SGQ, incluindo tendências em não conformidades, resultados de monitoramento e oportunidades de melhoria', correta: true },
      { texto: 'Apenas o desempenho financeiro da organização em relação ao investimento em qualidade', correta: false },
      { texto: 'Exclusivamente os resultados das auditorias externas de certificação', correta: false },
      { texto: 'Somente as metas de produção e os indicadores operacionais', correta: false },
    ],
  },
  {
    pasta: 'Melhoria (Seção 10)',
    enunciado: 'O que a organização deve fazer quando ocorre uma não conformidade segundo a ISO 9001:2015?',
    pontos: 1,
    alternativas: [
      { texto: 'Reagir à não conformidade, avaliar a necessidade de ação para eliminar suas causas, implementar ações corretivas e revisar a eficácia das ações tomadas', correta: true },
      { texto: 'Registrar a não conformidade e aguardar a próxima auditoria para tratá-la', correta: false },
      { texto: 'Informar imediatamente ao organismo de certificação sobre qualquer não conformidade', correta: false },
      { texto: 'Substituir o responsável pelo processo onde ocorreu a não conformidade', correta: false },
    ],
  },
  {
    pasta: 'Melhoria (Seção 10)',
    enunciado: 'O que é melhoria contínua no contexto da ISO 9001:2015?',
    pontos: 1,
    alternativas: [
      { texto: 'Atividade recorrente para melhorar o desempenho do SGQ, considerando resultados de análises, oportunidades de melhoria e objetivos estratégicos', correta: true },
      { texto: 'Realizar um grande projeto de melhoria a cada ciclo de certificação (3 anos)', correta: false },
      { texto: 'Aumentar continuamente o volume de documentos do sistema de gestão', correta: false },
      { texto: 'Substituir equipamentos e tecnologias a cada exercício fiscal', correta: false },
    ],
  },
];

const QUESTOES_ISO14001 = [
  {
    pasta: 'Contexto da Organização (Seção 4)',
    enunciado: 'O que a organização deve determinar ao estabelecer o escopo do Sistema de Gestão Ambiental (SGA) segundo a ISO 14001:2015?',
    pontos: 1,
    alternativas: [
      { texto: 'Os limites e a aplicabilidade do SGA, considerando as questões externas/internas, as obrigações de conformidade e suas unidades, funções e atividades organizacionais', correta: true },
      { texto: 'Apenas os processos produtivos que geram resíduos sólidos', correta: false },
      { texto: 'Somente as instalações físicas onde há risco de contaminação ambiental', correta: false },
      { texto: 'Os produtos e serviços vendidos que impactam o meio ambiente', correta: false },
    ],
  },
  {
    pasta: 'Contexto da Organização (Seção 4)',
    enunciado: 'O que são "aspectos ambientais" segundo a ISO 14001:2015?',
    pontos: 1,
    alternativas: [
      { texto: 'Elementos das atividades, produtos ou serviços de uma organização que interagem ou podem interagir com o meio ambiente', correta: true },
      { texto: 'Apenas os resíduos químicos descartados no meio ambiente', correta: false },
      { texto: 'As multas e penalidades ambientais aplicadas pelos órgãos reguladores', correta: false },
      { texto: 'Os regulamentos ambientais federais que a organização deve cumprir', correta: false },
    ],
  },
  {
    pasta: 'Liderança (Seção 5)',
    enunciado: 'O que a política ambiental deve incluir obrigatoriamente segundo a ISO 14001:2015?',
    pontos: 1,
    alternativas: [
      { texto: 'Comprometimento com a proteção do meio ambiente, prevenção da poluição e atendimento às obrigações de conformidade', correta: true },
      { texto: 'Metas numéricas anuais de redução de emissões de CO₂', correta: false },
      { texto: 'O nome do responsável ambiental e seus contatos de emergência', correta: false },
      { texto: 'O cronograma de auditorias ambientais externas', correta: false },
    ],
  },
  {
    pasta: 'Planejamento (Seção 6)',
    enunciado: 'O que a organização deve fazer em relação aos aspectos ambientais significativos?',
    pontos: 1,
    alternativas: [
      { texto: 'Determinar os aspectos ambientais significativos usando critérios definidos e considerar o ciclo de vida dos produtos e serviços', correta: true },
      { texto: 'Eliminar todos os aspectos ambientais de suas atividades imediatamente', correta: false },
      { texto: 'Informar o órgão ambiental competente sobre cada aspecto identificado', correta: false },
      { texto: 'Contratar uma consultoria para gerenciar todos os aspectos ambientais', correta: false },
    ],
  },
  {
    pasta: 'Planejamento (Seção 6)',
    enunciado: 'O que são "obrigações de conformidade" na ISO 14001:2015?',
    pontos: 1,
    alternativas: [
      { texto: 'Requisitos legais e outros requisitos que a organização subscreve relacionados a seus aspectos ambientais', correta: true },
      { texto: 'Apenas as leis federais de proteção ambiental', correta: false },
      { texto: 'Somente as multas e penalidades previstas em lei', correta: false },
      { texto: 'As normas ISO aplicáveis ao setor de atuação da organização', correta: false },
    ],
  },
  {
    pasta: 'Suporte (Seção 7)',
    enunciado: 'Qual é o requisito de comunicação externa do SGA segundo a ISO 14001:2015?',
    pontos: 1,
    alternativas: [
      { texto: 'A organização deve estabelecer processos para comunicação externa relevante ao SGA e considerar suas obrigações de conformidade ao decidir se comunica externamente', correta: true },
      { texto: 'Publicar obrigatoriamente um relatório ambiental anual na mídia', correta: false },
      { texto: 'Comunicar mensalmente ao Ibama os resultados dos indicadores ambientais', correta: false },
      { texto: 'Divulgar a política ambiental em todos os materiais de marketing da empresa', correta: false },
    ],
  },
  {
    pasta: 'Operação (Seção 8)',
    enunciado: 'O que a ISO 14001:2015 exige em relação à preparação e resposta a emergências?',
    pontos: 1,
    alternativas: [
      { texto: 'Estabelecer, implementar e manter processos para potenciais situações de emergência ambiental, incluindo como responder a elas', correta: true },
      { texto: 'Contratar uma equipe de bombeiros própria para situações de emergência', correta: false },
      { texto: 'Apenas registrar os acidentes ambientais após sua ocorrência', correta: false },
      { texto: 'Instalar equipamentos de combate a incêndio em todas as instalações', correta: false },
    ],
  },
  {
    pasta: 'Avaliação de Desempenho (Seção 9)',
    enunciado: 'O que a organização deve monitorar e medir no SGA segundo a ISO 14001:2015?',
    pontos: 1,
    alternativas: [
      { texto: 'As características das operações que podem ter impacto ambiental significativo, incluindo obrigações de conformidade e o progresso nos objetivos ambientais', correta: true },
      { texto: 'Apenas a temperatura e umidade das instalações produtivas', correta: false },
      { texto: 'Somente o volume de resíduos sólidos gerados mensalmente', correta: false },
      { texto: 'O consumo de energia elétrica e água das áreas administrativas', correta: false },
    ],
  },
  {
    pasta: 'Avaliação de Desempenho (Seção 9)',
    enunciado: 'Qual é o objetivo da avaliação do cumprimento das obrigações de conformidade na ISO 14001:2015?',
    pontos: 1,
    alternativas: [
      { texto: 'Avaliar e documentar periodicamente se a organização cumpre seus requisitos legais e outros comprometimentos ambientais', correta: true },
      { texto: 'Verificar se os concorrentes cumprem as mesmas leis ambientais', correta: false },
      { texto: 'Obter isenções fiscais por meio de certificações ambientais', correta: false },
      { texto: 'Preparar relatórios para redução de multas aplicadas pelo IBAMA', correta: false },
    ],
  },
  {
    pasta: 'Melhoria (Seção 10)',
    enunciado: 'Qual é a abordagem da ISO 14001:2015 para melhoria contínua do SGA?',
    pontos: 1,
    alternativas: [
      { texto: 'Melhorar continuamente a adequação, suficiência e eficácia do SGA para aprimorar o desempenho ambiental', correta: true },
      { texto: 'Renovar a certificação ambiental a cada dois anos obrigatoriamente', correta: false },
      { texto: 'Aumentar o investimento em tecnologia verde a cada exercício fiscal', correta: false },
      { texto: 'Substituir processos poluentes por tecnologias limpas no prazo máximo de 3 anos', correta: false },
    ],
  },
  {
    pasta: 'Melhoria (Seção 10)',
    enunciado: 'Quando ocorre um incidente ambiental, o que a organização deve fazer segundo a ISO 14001:2015?',
    pontos: 1,
    alternativas: [
      { texto: 'Reagir ao incidente, avaliar a necessidade de ações para eliminar as causas, implementar ações e analisar a eficácia, revisando o SGA se necessário', correta: true },
      { texto: 'Notificar apenas a agência reguladora competente e aguardar orientações', correta: false },
      { texto: 'Paralisar imediatamente todas as atividades da organização', correta: false },
      { texto: 'Contratar auditoria externa de emergência antes de tomar qualquer ação', correta: false },
    ],
  },
  {
    pasta: 'Perspectiva do Ciclo de Vida',
    enunciado: 'O que a perspectiva do ciclo de vida exige da organização na ISO 14001:2015?',
    pontos: 1,
    alternativas: [
      { texto: 'Considerar o ciclo de vida ao determinar controles ambientais, desde a aquisição de insumos até o uso e descarte final dos produtos/serviços', correta: true },
      { texto: 'Fabricar apenas produtos recicláveis e biodegradáveis', correta: false },
      { texto: 'Implementar programas de logística reversa para todos os produtos', correta: false },
      { texto: 'Certificar os fornecedores com ISO 14001 antes de contratá-los', correta: false },
    ],
  },
];

const QUESTOES_ISO45001 = [
  {
    pasta: 'Contexto da Organização (Seção 4)',
    enunciado: 'O que a organização deve considerar ao determinar as questões externas e internas relevantes para o Sistema de Gestão de SSO conforme a ISO 45001:2018?',
    pontos: 1,
    alternativas: [
      { texto: 'As condições de trabalho, necessidades e expectativas dos trabalhadores e partes interessadas que podem afetar a saúde e segurança ocupacional', correta: true },
      { texto: 'Apenas os acidentes de trabalho ocorridos nos últimos 5 anos', correta: false },
      { texto: 'Somente os requisitos da legislação trabalhista federal', correta: false },
      { texto: 'Os custos com plano de saúde e seguro de acidentes pessoais', correta: false },
    ],
  },
  {
    pasta: 'Contexto da Organização (Seção 4)',
    enunciado: 'O que são "perigos" no contexto da ISO 45001:2018?',
    pontos: 1,
    alternativas: [
      { texto: 'Fonte com potencial para causar lesão e deterioração da saúde', correta: true },
      { texto: 'A probabilidade de ocorrência de um acidente de trabalho', correta: false },
      { texto: 'Equipamentos e máquinas sem manutenção preventiva', correta: false },
      { texto: 'Trabalhadores que descumprem as normas de segurança', correta: false },
    ],
  },
  {
    pasta: 'Liderança e Participação dos Trabalhadores (Seção 5)',
    enunciado: 'O que a alta direção deve demonstrar em relação ao Sistema de Gestão de SSO segundo a ISO 45001:2018?',
    pontos: 1,
    alternativas: [
      { texto: 'Liderança e comprometimento, assumindo responsabilidade pela proteção da saúde e segurança dos trabalhadores e promovendo uma cultura de SSO', correta: true },
      { texto: 'Apenas assinar os documentos do sistema e aprovar o orçamento de segurança', correta: false },
      { texto: 'Delegar toda a responsabilidade de SSO ao SESMT ou técnico de segurança', correta: false },
      { texto: 'Participar uma vez por ano da CIPA como presidente nato', correta: false },
    ],
  },
  {
    pasta: 'Liderança e Participação dos Trabalhadores (Seção 5)',
    enunciado: 'Qual é o requisito específico da ISO 45001:2018 em relação à participação dos trabalhadores?',
    pontos: 1,
    alternativas: [
      { texto: 'Estabelecer, implementar e manter processo para consulta e participação dos trabalhadores no desenvolvimento, planejamento, implementação e melhoria contínua do SGS de SSO', correta: true },
      { texto: 'Realizar eleições anuais para representantes de segurança', correta: false },
      { texto: 'Exigir que os trabalhadores assinem o manual de segurança anualmente', correta: false },
      { texto: 'Permitir que trabalhadores recusem apenas tarefas classificadas como insalubres', correta: false },
    ],
  },
  {
    pasta: 'Planejamento (Seção 6)',
    enunciado: 'O que a organização deve fazer para identificar perigos e avaliar riscos de SSO?',
    pontos: 1,
    alternativas: [
      { texto: 'Estabelecer processo proativo e contínuo de identificação de perigos, incluindo trabalho rotineiro, não rotineiro, emergências e fatores humanos e organizacionais', correta: true },
      { texto: 'Realizar apenas inspeções de segurança após a ocorrência de acidentes', correta: false },
      { texto: 'Contratar exclusivamente engenheiro de segurança para identificar os perigos', correta: false },
      { texto: 'Aplicar o checklist do Ministério do Trabalho anualmente', correta: false },
    ],
  },
  {
    pasta: 'Planejamento (Seção 6)',
    enunciado: 'O que a hierarquia de controles exige na ISO 45001:2018?',
    pontos: 1,
    alternativas: [
      { texto: 'Aplicar controles na ordem: eliminação, substituição, controles de engenharia, controles administrativos e EPI, priorizando a eliminação do perigo', correta: true },
      { texto: 'Fornecer EPI a todos os trabalhadores como primeira medida de controle', correta: false },
      { texto: 'Aplicar apenas controles administrativos por serem mais econômicos', correta: false },
      { texto: 'Eliminar todos os perigos ou fechar as atividades de risco', correta: false },
    ],
  },
  {
    pasta: 'Suporte (Seção 7)',
    enunciado: 'O que a organização deve assegurar em relação à tomada de consciência dos trabalhadores em SSO segundo a ISO 45001:2018?',
    pontos: 1,
    alternativas: [
      { texto: 'Que os trabalhadores estejam conscientes da política de SSO, dos perigos/riscos associados ao seu trabalho e das consequências de desvios dos requisitos do sistema', correta: true },
      { texto: 'Apenas que os trabalhadores assinem o recebimento dos EPI obrigatórios', correta: false },
      { texto: 'Que todos os trabalhadores tenham formação em primeiros socorros', correta: false },
      { texto: 'Que o técnico de segurança treine os trabalhadores uma vez por admissão', correta: false },
    ],
  },
  {
    pasta: 'Operação (Seção 8)',
    enunciado: 'O que a ISO 45001:2018 exige em relação à preparação e resposta a emergências?',
    pontos: 1,
    alternativas: [
      { texto: 'Estabelecer, implementar e manter processo para responder a potenciais situações de emergência, incluindo prestação de primeiros socorros e comunicação com trabalhadores', correta: true },
      { texto: 'Contratar brigada de incêndio terceirizada para todas as instalações', correta: false },
      { texto: 'Apenas instalar extintores e sinalização de emergência nas saídas', correta: false },
      { texto: 'Realizar um simulacro de emergência a cada 5 anos', correta: false },
    ],
  },
  {
    pasta: 'Avaliação de Desempenho (Seção 9)',
    enunciado: 'O que a organização deve fazer em relação à investigação de incidentes na ISO 45001:2018?',
    pontos: 1,
    alternativas: [
      { texto: 'Estabelecer processo para investigar incidentes e não conformidades, determinar causas e implementar ações corretivas para evitar recorrência', correta: true },
      { texto: 'Registrar apenas os acidentes com afastamento superior a 15 dias', correta: false },
      { texto: 'Comunicar apenas ao eSocial os acidentes ocorridos', correta: false },
      { texto: 'Realizar investigação somente quando houver vítimas fatais', correta: false },
    ],
  },
  {
    pasta: 'Avaliação de Desempenho (Seção 9)',
    enunciado: 'O que a análise crítica pela direção deve incluir em relação ao SGS de SSO?',
    pontos: 1,
    alternativas: [
      { texto: 'O desempenho de SSO incluindo tendências em incidentes, resultados de monitoramento, avaliação de riscos e oportunidades de melhoria', correta: true },
      { texto: 'Apenas o orçamento de segurança e os custos com equipamentos de proteção', correta: false },
      { texto: 'Somente os indicadores de absenteísmo e turnover da organização', correta: false },
      { texto: 'Os processos trabalhistas movidos pelos empregados nos últimos 12 meses', correta: false },
    ],
  },
  {
    pasta: 'Melhoria (Seção 10)',
    enunciado: 'O que a organização deve fazer para promover melhoria contínua do SGS de SSO?',
    pontos: 1,
    alternativas: [
      { texto: 'Melhorar continuamente a adequação e eficácia do SGS de SSO, eliminando perigos e reduzindo riscos de SSO, promovendo cultura de SSO e participação dos trabalhadores', correta: true },
      { texto: 'Substituir o manual de segurança e os procedimentos a cada dois anos', correta: false },
      { texto: 'Aumentar o investimento em EPI e monitoramento ambiental anualmente', correta: false },
      { texto: 'Certificar-se de que zero acidente é atingido antes de encerrar o ciclo', correta: false },
    ],
  },
  {
    pasta: 'Melhoria (Seção 10)',
    enunciado: 'Qual a diferença entre incidente e não conformidade na ISO 45001:2018?',
    pontos: 1,
    alternativas: [
      { texto: 'Incidente é ocorrência que resulta ou tem potencial de resultar em lesão/doença; não conformidade é o não atendimento de um requisito do sistema', correta: true },
      { texto: 'Incidente é qualquer acidente com afastamento; não conformidade é falha documentada em auditoria', correta: false },
      { texto: 'São sinônimos e ambos exigem o mesmo tratamento pelo sistema de gestão', correta: false },
      { texto: 'Incidente envolve apenas danos materiais; não conformidade envolve pessoas', correta: false },
    ],
  },
];

// Pasta do banco que aparece em Simulados. Vínculo explícito pela marcação
// "Usar como simulado" (pasta.simulado). Pasta antiga sem marcação segue a
// regra anterior: é simulado se nenhuma questão dela estiver em um teste.
function pastaEhSimulado(pasta, questoes, testes) {
  if (pasta.simulado === true) return true;
  if (pasta.simulado === false) return false;
  const emTeste = new Set((testes || []).flatMap(t => t.questoesIds || []));
  return !questoes.some(q => q.pastaId === pasta.id && emTeste.has(q.id));
}

// ── ABA: Banco de Questões ─────────────────────────────────────────────────
function AbaBancoQuestoes({ cursos, questoes, setQuestoes, pastas, setPastas, testes = [], setTestes }) {
  const [cursoSel, setCursoSel]   = useState('');
  const [filtroCurso, setFiltroCurso] = useState('');
  const [pastaSel, setPastaSel]   = useState(null); // id da pasta selecionada
  const [form, setForm]           = useState(null); // formulário de questão
  const [editPasta, setEditPasta] = useState(null); // { id, nome } | 'nova'
  const [nomePasta, setNomePasta] = useState('');

  // Pastas do curso selecionado (ordenadas)
  const pastasDoCurso = pastas
    .filter(p => String(p.cursoId) === String(cursoSel))
    .sort((a, b) => a.nome.localeCompare(b.nome));

  // Questões do curso / da pasta selecionada
  const questoesDoCurso = questoes.filter(q => String(q.cursoId) === String(cursoSel));
  const questoesDaPasta = pastaSel
    ? questoesDoCurso.filter(q => q.pastaId === pastaSel)
    : questoesDoCurso;

  // ── Pastas ──
  const criarPasta = () => {
    const nome = nomePasta.trim();
    if (!nome) { toast.aviso('Informe o nome da pasta.'); return; }
    if (pastasDoCurso.some(p => p.nome.toLowerCase() === nome.toLowerCase())) {
      toast.aviso('Já existe uma pasta com este nome.'); return;
    }
    const nova = { id: novoId(), cursoId: cursoSel, nome };
    setPastas([...pastas, nova]);
    setPastaSel(nova.id);
    setNomePasta('');
    setEditPasta(null);
    toast.sucesso('Pasta criada!');
  };

  const salvarNomePasta = () => {
    const nome = nomePasta.trim();
    if (!nome) { toast.aviso('Informe o nome.'); return; }
    if (pastasDoCurso.some(p => p.id !== editPasta.id && p.nome.toLowerCase() === nome.toLowerCase())) {
      toast.aviso('Já existe uma pasta com este nome.'); return;
    }
    setPastas(pastas.map(p => p.id === editPasta.id ? { ...p, nome } : p));
    setEditPasta(null);
    setNomePasta('');
    toast.sucesso('Pasta renomeada!');
  };

  const excluirPasta = (pastaId) => {
    const qtd = questoesDoCurso.filter(q => q.pastaId === pastaId).length;
    const emTestes = testes
      .filter(t => (t.questoesIds || []).some(id => questoes.some(q => q.id === id && q.pastaId === pastaId)))
      .map(t => t.nome);
    const aviso = emTestes.length ? `\n\nAtenção: questões desta pasta estão nos testes ${emTestes.join(', ')}. Elas sairão desses testes.` : '';
    if (!window.confirm(`Excluir esta pasta e as ${qtd} questão(ões) dentro dela? A exclusão não pode ser desfeita pela tela.${aviso}`)) return;
    // Mais de 10 itens de uma vez esbarra na trava contra apagamento em massa;
    // com a confirmação acima, esta gravação vai liberada (mig 027).
    if (qtd > 10) liberarRemocaoEmMassa('tr_dados');
    setPastas(pastas.filter(p => p.id !== pastaId));
    setQuestoes(questoes.filter(q => q.pastaId !== pastaId));
    if (emTestes.length && setTestes) {
      const saem = new Set(questoes.filter(q => q.pastaId === pastaId).map(q => q.id));
      setTestes(testes.map(t => ({ ...t, questoesIds: (t.questoesIds || []).filter(id => !saem.has(id)) })));
    }
    if (pastaSel === pastaId) setPastaSel(null);
    toast.sucesso('Pasta excluída.');
  };

  // ── Questões ──
  const novaQuestao = () => {
    if (!pastaSel) { toast.aviso('Selecione uma pasta para adicionar questões.'); return; }
    setForm({
      cursoId: cursoSel, pastaId: pastaSel, enunciado:'', pontos: 1,
      alternativas: [{ texto:'', correta:true },{ texto:'', correta:false },{ texto:'', correta:false },{ texto:'', correta:false }],
    });
  };

  const setAlternativa = (idx, campo, valor) => {
    setForm(f => ({
      ...f,
      alternativas: f.alternativas.map((a, i) =>
        campo === 'correta' ? { ...a, correta: i === idx } : i === idx ? { ...a, [campo]: valor } : a
      ),
    }));
  };

  const salvarQuestao = () => {
    if (!form.enunciado) { toast.aviso('Informe o enunciado.'); return; }
    if (!form.alternativas.some(a => a.correta)) { toast.aviso('Marque a alternativa correta.'); return; }
    if (form.alternativas.some(a => !a.texto.trim())) { toast.aviso('Preencha todas as alternativas.'); return; }
    const duplicada = questoesDoCurso.some(q => q.id !== form.id && q.enunciado.trim().toLowerCase() === form.enunciado.trim().toLowerCase());
    if (duplicada) { toast.aviso('Já existe uma questão com este enunciado neste curso.'); return; }
    if (form.id) {
      setQuestoes(questoes.map(q => q.id === form.id ? form : q));
      toast.sucesso('Questão atualizada!');
    } else {
      setQuestoes([...questoes, { ...form, id: novoId() }]);
      toast.sucesso('Questão adicionada!');
    }
    setForm(null);
  };

  // ── Importação (filtra duplicatas e importa para a pasta selecionada) ──
  const importar = (banco, nomeNorma) => {
    if (!pastaSel) { toast.aviso('Selecione uma pasta para importar as questões.'); return; }
    const enunciadosExistentes = new Set(questoesDoCurso.map(q => q.enunciado.trim().toLowerCase()));
    const novas = banco
      .filter(q => !enunciadosExistentes.has(q.enunciado.trim().toLowerCase()))
      .map(({ pasta: _pasta, ...q }) => ({ ...q, cursoId: cursoSel, pastaId: pastaSel, id: novoId() }));
    const ignoradas = banco.length - novas.length;
    if (novas.length === 0) { toast.aviso(`Todas as questões de ${nomeNorma} já existem neste curso.`); return; }
    if (!window.confirm(`Importar ${novas.length} questão(ões) de ${nomeNorma} para esta pasta?${ignoradas > 0 ? ` (${ignoradas} ignorada(s) por duplicata)` : ''}`)) return;
    setQuestoes([...questoes, ...novas]);
    toast.sucesso(`${novas.length} questão(ões) importada(s)!${ignoradas > 0 ? ` ${ignoradas} duplicada(s) ignorada(s).` : ''}`);
  };

  return (
    <div style={s.secao}>
      {/* Seletor de curso */}
      <div style={{ display:'flex', gap:'0.75rem', alignItems:'center', marginBottom:'1.25rem', flexWrap:'wrap' }}>
        <div style={s.secaoTitulo}>Banco de Questões</div>
        <input
          style={{ ...s.input, maxWidth: 220 }}
          placeholder="🔍 Filtrar cursos..."
          value={filtroCurso}
          onChange={e => { setFiltroCurso(e.target.value); setCursoSel(''); setPastaSel(null); setForm(null); setEditPasta(null); }}
        />
        <select style={s.select} value={cursoSel} onChange={e => { setCursoSel(e.target.value); setPastaSel(null); setForm(null); setEditPasta(null); }}>
          <option value="">Selecione o curso...</option>
          {cursos.filter(c => c.nome.toLowerCase().includes(filtroCurso.toLowerCase())).map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
        </select>
      </div>

      {!cursoSel && <div style={s.vazio}>Selecione um curso para gerenciar o banco de questões.</div>}

      {cursoSel && (
        <div style={{ display:'flex', gap:'1.25rem', alignItems:'flex-start' }}>

          {/* ── Coluna esquerda: Pastas ── */}
          <div style={{ width:240, flexShrink:0 }}>
            <div style={{ background:'#ffffff', border:'1px solid #e5e0ef', borderRadius:10, overflow:'hidden' }}>
              {/* Cabeçalho */}
              <div style={{ padding:'0.75rem 1rem', borderBottom:'1px solid #e5e0ef', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ fontSize:12, fontWeight:700, color:'#5f5874', textTransform:'uppercase', letterSpacing:'0.06em' }}>Pastas</span>
                <button style={{ background:'#7c3aed', border:'none', color:'white', borderRadius:6, padding:'4px 10px', fontSize:11, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', gap:4 }}
                  onClick={() => { setEditPasta('nova'); setNomePasta(''); }}>
                  <Plus size={11}/> Nova
                </button>
              </div>

              {/* Form nova pasta */}
              {editPasta === 'nova' && (
                <div style={{ padding:'0.625rem', borderBottom:'1px solid #e5e0ef' }}>
                  <input
                    autoFocus
                    style={{ ...s.input, fontSize:12, padding:'6px 8px', marginBottom:6 }}
                    placeholder="Nome da pasta..."
                    value={nomePasta}
                    onChange={e => setNomePasta(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') criarPasta(); if (e.key === 'Escape') setEditPasta(null); }}
                  />
                  <div style={{ display:'flex', gap:4 }}>
                    <button style={{ ...s.btnSalvar, flex:1, fontSize:11, padding:'5px 0' }} onClick={criarPasta}>Criar</button>
                    <button style={{ ...s.btnCancelar, fontSize:11, padding:'5px 10px' }} onClick={() => setEditPasta(null)}>✕</button>
                  </div>
                </div>
              )}

              {/* "Todas" */}
              <button
                style={{ display:'block', width:'100%', textAlign:'left', background: pastaSel === null ? 'rgba(139,92,246,0.12)' : 'transparent', border:'none', borderBottom:'1px solid #eeeaf5', padding:'9px 14px', color: pastaSel === null ? '#5b21b6' : '#64748b', fontSize:13, cursor:'pointer', fontWeight: pastaSel === null ? 700 : 400 }}
                onClick={() => { setPastaSel(null); setForm(null); }}
              >
                📂 Todas ({questoesDoCurso.length})
              </button>

              {/* Lista de pastas */}
              {pastasDoCurso.length === 0 && (
                <div style={{ padding:'0.75rem 1rem', fontSize:12, color:'#64748b' }}>Nenhuma pasta. Crie a primeira.</div>
              )}
              {pastasDoCurso.map(pasta => {
                const qtd = questoesDoCurso.filter(q => q.pastaId === pasta.id).length;
                const ativa = pastaSel === pasta.id;
                return (
                  <div key={pasta.id}>
                    {editPasta?.id === pasta.id ? (
                      <div style={{ padding:'0.5rem', borderBottom:'1px solid #eeeaf5', background:'#f7f5fb' }}>
                        <input
                          autoFocus
                          style={{ ...s.input, fontSize:12, padding:'5px 8px', marginBottom:5 }}
                          value={nomePasta}
                          onChange={e => setNomePasta(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') salvarNomePasta(); if (e.key === 'Escape') setEditPasta(null); }}
                        />
                        <div style={{ display:'flex', gap:4 }}>
                          <button style={{ ...s.btnSalvar, flex:1, fontSize:11, padding:'4px 0' }} onClick={salvarNomePasta}>OK</button>
                          <button style={{ ...s.btnCancelar, fontSize:11, padding:'4px 8px' }} onClick={() => setEditPasta(null)}>✕</button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display:'flex', alignItems:'center', borderBottom:'1px solid #eeeaf5', background: ativa ? 'rgba(139,92,246,0.12)' : 'transparent' }}>
                        <button
                          style={{ flex:1, textAlign:'left', background:'transparent', border:'none', padding:'9px 14px', color: ativa ? '#5b21b6' : '#64748b', fontSize:13, cursor:'pointer', fontWeight: ativa ? 700 : 400 }}
                          onClick={() => { setPastaSel(pasta.id); setForm(null); }}
                        >
                          📁 {pasta.nome} <span style={{ color:'#475569', fontSize:11 }}>({qtd})</span>
                        </button>
                        {(() => {
                          const sim = pastaEhSimulado(pasta, questoes, testes);
                          return (
                            <button
                              title={sim ? 'Aparece para os alunos em Simulados. Clique para tirar.' : 'Clique para os alunos usarem esta pasta como simulado.'}
                              onClick={() => setPastas(pastas.map(p => p.id === pasta.id ? { ...p, simulado: !sim } : p))}
                              style={{ flexShrink:0, fontSize:11, fontWeight:700, padding:'3px 8px', marginRight:4, borderRadius:20, cursor:'pointer', border:'1px solid', minHeight:0, background: sim ? '#e2f5ec' : '#ffffff', color: sim ? '#047857' : '#8a849c', borderColor: sim ? '#a7e3c8' : '#e5e0ef' }}>
                              {sim ? '✓ Simulado' : 'Simulado'}
                            </button>
                          );
                        })()}
                        <button style={{ ...s.btnIcone, color:'#64748b', padding:'6px 6px' }} onClick={() => { setEditPasta(pasta); setNomePasta(pasta.nome); }}><Edit2 size={12}/></button>
                        <button style={{ ...s.btnIcone, color:'#ef4444', padding:'6px 8px' }} onClick={() => excluirPasta(pasta.id)}><Trash2 size={12}/></button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Coluna direita: Questões ── */}
          <div style={{ flex:1, minWidth:0 }}>
            {/* Barra de ações da pasta */}
            {!form && (
              <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap', marginBottom:'1rem' }}>
                <button style={{ ...s.btnNovo, opacity: pastaSel ? 1 : 0.4 }} onClick={novaQuestao} title={!pastaSel ? 'Selecione uma pasta primeiro' : ''}>
                  <Plus size={14}/> Nova Questão
                </button>
                <button style={{ ...s.btnNovo, background:'#ffffff', border:'1px solid #1e3a5f', color:'#1d4ed8', opacity: pastaSel ? 1 : 0.4 }}
                  onClick={() => importar(QUESTOES_ISO9001, 'ISO 9001:2015')} title={!pastaSel ? 'Selecione uma pasta primeiro' : 'Importar questões ISO 9001:2015'}>
                  📥 ISO 9001:2015
                </button>
                <button style={{ ...s.btnNovo, background:'#ffffff', border:'1px solid #14532d', color:'#15803d', opacity: pastaSel ? 1 : 0.4 }}
                  onClick={() => importar(QUESTOES_ISO14001, 'ISO 14001:2015')} title={!pastaSel ? 'Selecione uma pasta primeiro' : 'Importar questões ISO 14001:2015'}>
                  📥 ISO 14001:2015
                </button>
                <button style={{ ...s.btnNovo, background:'#ffffff', border:'1px solid #7c2d12', color:'#fb923c', opacity: pastaSel ? 1 : 0.4 }}
                  onClick={() => importar(QUESTOES_ISO45001, 'ISO 45001:2018')} title={!pastaSel ? 'Selecione uma pasta primeiro' : 'Importar questões ISO 45001:2018'}>
                  📥 ISO 45001:2018
                </button>
              </div>
            )}

            {!pastaSel && !form && questoesDoCurso.length === 0 && (
              <div style={s.vazio}>Crie uma pasta e importe ou cadastre questões.</div>
            )}
            {!pastaSel && !form && questoesDoCurso.length > 0 && (
              <div style={s.vazio}>Selecione uma pasta à esquerda para ver as questões.</div>
            )}

            {/* Formulário de questão */}
            {form && (
              <div style={s.formCard}>
                <div style={s.formTitulo}>{form.id ? 'Editar Questão' : 'Nova Questão'}</div>
                <div style={s.formRow}>
                  <Campo label="Pontos" style={{ flex:'0 0 auto', minWidth:100 }}>
                    <input type="number" style={{ ...s.input, width:80 }} value={form.pontos} min={1} onChange={e => setForm(f => ({...f, pontos: parseInt(e.target.value)||1}))} />
                  </Campo>
                  {/* Item da norma (30/10/2026): alimenta o "Onde estudar mais" do aluno. */}
                  <Campo label="Item da norma">
                    <input style={s.input} value={form.itemNorma || ''} placeholder="Ex.: 6.1 Ações para abordar riscos e oportunidades" onChange={e => setForm(f => ({...f, itemNorma: e.target.value}))} />
                  </Campo>
                </div>
                <Campo label="Enunciado *">
                  <textarea style={{...s.input, height:90, resize:'vertical'}} value={form.enunciado} onChange={e => setForm(f => ({...f, enunciado: e.target.value}))} />
                </Campo>
                <div style={{ fontSize:12, color:'#5f5874', fontWeight:600, marginBottom:'0.5rem' }}>ALTERNATIVAS (marque a correta)</div>
                {form.alternativas.map((alt, i) => (
                  <div key={i} style={{ display:'flex', gap:'0.5rem', alignItems:'center', marginBottom:'0.5rem' }}>
                    <input type="radio" name="correta" checked={alt.correta} onChange={() => setAlternativa(i, 'correta', true)} style={{ accentColor:'#10b981', width:16, height:16, cursor:'pointer', flexShrink:0 }} />
                    <span style={{ color:'#64748b', fontSize:12, width:20 }}>{String.fromCharCode(65+i)})</span>
                    <input style={{...s.input, flex:1}} value={alt.texto} placeholder={`Alternativa ${String.fromCharCode(65+i)}`} onChange={e => setAlternativa(i, 'texto', e.target.value)} />
                  </div>
                ))}
                <div style={s.formAcoes}>
                  <button style={s.btnSalvar} onClick={salvarQuestao}>Salvar</button>
                  <button style={s.btnCancelar} onClick={() => setForm(null)}>Cancelar</button>
                </div>
              </div>
            )}

            {/* Lista de questões */}
            {!form && pastaSel && questoesDaPasta.length === 0 && (
              <div style={s.vazio}>Nenhuma questão nesta pasta. Adicione manualmente ou importe uma norma.</div>
            )}
            {!form && questoesDaPasta.map((q, idx) => (
              <div key={q.id} style={s.card}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:12, color:'#64748b', marginBottom:4 }}>Q{idx+1} · {q.pontos} ponto(s)</div>
                    <div style={{ color:'#1c1530', fontSize:14, lineHeight:1.5 }}>{q.enunciado}</div>
                    <div style={{ marginTop:'0.5rem', display:'flex', flexWrap:'wrap', gap:'0.375rem' }}>
                      {q.alternativas.map((a, i) => (
                        <span key={i} style={{ fontSize:12, padding:'2px 8px', borderRadius:6, background: a.correta ? 'rgba(16,185,129,0.1)' : 'transparent', color: a.correta ? '#10b981' : '#64748b', border: a.correta ? '1px solid rgba(16,185,129,0.3)' : '1px solid #e5e0ef' }}>
                          {String.fromCharCode(65+i)}) {a.texto}{a.correta && ' ✓'}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:4, marginLeft:'0.5rem', flexShrink:0 }}>
                    <button style={s.btnIcone} onClick={() => setForm({...q})}><Edit2 size={13}/></button>
                    <button style={{...s.btnIcone, color:'#ef4444'}} onClick={() => { if(window.confirm('Excluir questão?')) { setQuestoes(questoes.filter(x => x.id !== q.id)); toast.sucesso('Questão excluída.'); }}}><Trash2 size={13}/></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── ABA: Testes ────────────────────────────────────────────────────────────
function AbaTestes({ cursos, turmas, questoes, pastas, testes, setTestes, respostas, setRespostas, isGestor, usuarioAtual, cursosDoAluno, turmasDoAluno }) {
  const [filtroCurso, setFiltroCurso] = useState('');
  const [formTeste, setFormTeste] = useState(null);
  const [realizando, setRealizando] = useState(null); // { testeId, respostas: {} }

  const listaCursos = isGestor ? cursos : cursosDoAluno;
  const listaCursosFiltrados = listaCursos.filter(c =>
    c.nome.toLowerCase().includes(filtroCurso.toLowerCase())
  );

  // computed per-course (used when form is active)
  const cursoSel = formTeste?.cursoId || '';
  const turmasDoCurso = turmas.filter(t => String(t.cursoId) === String(cursoSel));
  const questoesDoCurso = questoes.filter(q => String(q.cursoId) === String(cursoSel));

  const jaRespondeu = (testeId) => respostas.some(r => r.testeId === testeId && r.alunoId === String(usuarioAtual?.id));

  // ── Reteste (30/10/2026) ──
  // Aluno reprovado (< 70%) solicita; o instrutor libera; o sistema sorteia,
  // das mesmas pastas do teste, questões que o aluno ainda não respondeu.
  // Até 2 retestes por teste. Pedidos ficam em teste.retestes.
  const tentativasDe = (testeId, alunoId) => respostas
    .filter(r => r.testeId === testeId && r.alunoId === String(alunoId))
    .sort((a, b) => String(a.dataRealizacao).localeCompare(String(b.dataRealizacao)));
  const retestesDe = (teste, alunoId) => (teste.retestes || []).filter(x => x.alunoId === String(alunoId));
  const atualizarRetestes = (testeId, fn) =>
    setTestes(prev => prev.map(t => t.id === testeId ? { ...t, retestes: fn(t.retestes || []) } : t));

  const solicitarReteste = (teste) => {
    atualizarRetestes(teste.id, lista => [...lista, {
      id: novoId(), alunoId: String(usuarioAtual?.id), alunoNome: usuarioAtual?.nome || 'Aluno',
      status: 'solicitado', solicitadoEm: new Date().toISOString(),
    }]);
    toast.sucesso('Reteste solicitado. O instrutor será avisado para liberar.');
  };

  const liberarReteste = (teste, pedido) => {
    const n = (teste.questoesIds || []).length;
    const tentativas = tentativasDe(teste.id, pedido.alunoId);
    const jaVistas = new Set(tentativas.flatMap(r => Object.keys(r.respostas || {})));
    const pastasDoTeste = new Set((teste.questoesIds || [])
      .map(id => questoes.find(q => q.id === id)?.pastaId).filter(v => v != null).map(String));
    const sortear = (l) => l.map(x => [Math.random(), x]).sort((a, b) => a[0] - b[0]).map(x => x[1]);
    let escolhidas = sortear(questoes.filter(q => pastasDoTeste.has(String(q.pastaId)) && !jaVistas.has(String(q.id)))).slice(0, n);
    let repetidas = 0;
    if (escolhidas.length < n) {
      // Faltou questão inédita: completa com as que ele errou, depois com as demais.
      const ultima = tentativas[tentativas.length - 1];
      const usadas = new Set(escolhidas.map(q => String(q.id)));
      const vistas = questoes.filter(q => jaVistas.has(String(q.id)) && !usadas.has(String(q.id)));
      const errou = (q) => { const r = ultima?.respostas?.[q.id] ?? ultima?.respostas?.[String(q.id)]; return !q.alternativas?.[r]?.correta; };
      const extra = [...sortear(vistas.filter(errou)), ...sortear(vistas.filter(q => !errou(q)))].slice(0, n - escolhidas.length);
      repetidas = extra.length;
      escolhidas = [...escolhidas, ...extra];
    }
    if (escolhidas.length === 0) { toast.aviso('Não há questões nas pastas deste teste para montar o reteste.'); return; }
    atualizarRetestes(teste.id, lista => lista.map(x => x.id === pedido.id ? {
      ...x, status: 'liberado', liberadoEm: new Date().toISOString(), liberadoPor: usuarioAtual?.nome || '',
      questoesIds: escolhidas.map(q => q.id), questoesRepetidas: repetidas,
    } : x));
    toast.sucesso(repetidas > 0
      ? `Reteste liberado. Faltaram questões inéditas: ${repetidas} questão(ões) já respondida(s) foram usadas.`
      : 'Reteste liberado com questões inéditas.');
  };

  const testeExpirado = (teste) => {
    if (!teste.prazo || !teste.horaFim) return false;
    const fimStr = `${teste.prazo}T${teste.horaFim}`;
    return new Date() > new Date(fimStr);
  };

  const testeNaoIniciado = (teste) => {
    if (!teste.prazo || !teste.horaInicio) return false;
    const inicioStr = `${teste.prazo}T${teste.horaInicio}`;
    return new Date() < new Date(inicioStr);
  };

  const salvarTeste = () => {
    if (!formTeste.nome || !formTeste.turmaId) { toast.aviso('Informe nome e turma.'); return; }
    if (!formTeste.questoesIds?.length) { toast.aviso('Selecione ao menos uma questão.'); return; }
    if (formTeste.id) {
      setTestes(testes.map(t => t.id === formTeste.id ? formTeste : t));
      toast.sucesso('Teste atualizado!');
    } else {
      setTestes([...testes, { ...formTeste, id: novoId(), disponivel: false }]);
      toast.sucesso('Teste criado!');
    }
    setFormTeste(null);
  };

  const toggleDisponivel = (id) => {
    setTestes(testes.map(t => t.id === id ? { ...t, disponivel: !t.disponivel } : t));
    const teste = testes.find(t => t.id === id);
    toast.sucesso(teste?.disponivel ? 'Teste ocultado.' : 'Teste liberado para a turma!');
  };

  const toggleQuestao = (qId) => {
    const ids = formTeste.questoesIds || [];
    setFormTeste({ ...formTeste, questoesIds: ids.includes(qId) ? ids.filter(x => x !== qId) : [...ids, qId] });
  };

  const submeterTeste = () => {
    const teste = testes.find(t => t.id === realizando.testeId);
    if (!teste) return;
    const idsDaVez = realizando.questoesIds || teste.questoesIds;
    if (!realizando.retesteId && testeExpirado(teste)) {
      toast.aviso('O prazo deste teste foi encerrado. Suas respostas não podem ser enviadas.');
      setRealizando(null);
      return;
    }
    const total = idsDaVez.reduce((s, qId) => {
      const q = questoes.find(x => x.id === qId); return s + (q?.pontos || 1);
    }, 0);
    const acertos = idsDaVez.reduce((s, qId) => {
      const q = questoes.find(x => x.id === qId);
      const resp = realizando.respostas[qId];
      if (resp === undefined || !q) return s;
      return q.alternativas[resp]?.correta ? s + (q.pontos || 1) : s;
    }, 0);
    const pct = total > 0 ? (acertos / total) * 100 : 0;
    const resultado = { id: novoId(), testeId: teste.id, alunoId: String(usuarioAtual?.id), alunoNome: usuarioAtual?.nome || 'Aluno', respostas: realizando.respostas, pontuacao: acertos, total, percentual: pct, dataRealizacao: new Date().toISOString(),
      ...(realizando.retesteId ? { retesteId: realizando.retesteId, retesteNum: realizando.retesteNum, questoesIds: idsDaVez } : {}) };
    setRespostas([...respostas, resultado]);
    if (realizando.retesteId) atualizarRetestes(teste.id, lista => lista.map(x => x.id === realizando.retesteId ? { ...x, status: 'realizado', realizadoEm: resultado.dataRealizacao } : x));
    setRealizando(null);
    toast.sucesso(`Teste concluído! Você acertou ${acertos}/${total} questões (${pct.toFixed(1)}%)`);
  };

  // Modo realização de teste (aluno)
  if (realizando) {
    const teste = testes.find(t => t.id === realizando.testeId);
    const qs = (realizando.questoesIds || teste?.questoesIds || []).map(qId => questoes.find(q => q.id === qId)).filter(Boolean);
    const respondidas = Object.keys(realizando.respostas).length;
    return (
      <div style={s.secao}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem' }}>
          <div style={s.secaoTitulo}>{teste?.nome}{realizando.retesteNum ? ` — Reteste ${realizando.retesteNum}` : ''}</div>
          <div style={{ color:'#64748b', fontSize:13 }}>{respondidas}/{qs.length} respondidas</div>
        </div>
        {qs.map((q, idx) => (
          <div key={q.id} style={s.card}>
            <div style={{ fontSize:12, color:'#64748b', marginBottom:6 }}>Questão {idx+1} · {q.pontos} ponto(s)</div>
            <div style={{ color:'#1c1530', fontSize:14, marginBottom:'0.75rem', lineHeight:1.6 }}>{q.enunciado}</div>
            <div style={{ display:'flex', flexDirection:'column', gap:'0.375rem' }}>
              {q.alternativas.map((a, i) => {
                const sel = realizando.respostas[q.id] === i;
                return (
                  <button key={i} style={{ textAlign:'left', background: sel ? 'rgba(139,92,246,0.15)' : 'transparent', border: sel ? '1.5px solid #8b5cf6' : '1.5px solid #e5e0ef', borderRadius:8, padding:'10px 14px', color: sel ? '#1c1530' : '#64748b', fontSize:13, cursor:'pointer' }} onClick={() => setRealizando({ ...realizando, respostas: { ...realizando.respostas, [q.id]: i } })}>
                    <span style={{ fontWeight:600, marginRight:6 }}>{String.fromCharCode(65+i)})</span>{a.texto}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        <div style={s.formAcoes}>
          <button style={s.btnSalvar} onClick={submeterTeste} disabled={respondidas < qs.length} title={respondidas < qs.length ? 'Responda todas as questões' : ''}>
            {respondidas < qs.length ? `Responda mais ${qs.length - respondidas} questão(ões)` : '✓ Entregar Teste'}
          </button>
          <button style={s.btnCancelar} onClick={() => { if(window.confirm('Cancelar o teste? Suas respostas serão perdidas.')) setRealizando(null); }}>Cancelar</button>
        </div>
      </div>
    );
  }

  return (
    <div style={s.secao}>
      <div style={s.secaoHeader}>
        <div style={s.secaoTitulo}>{isGestor ? 'Gerenciar Testes' : 'Meus Testes'}</div>
        <input
          style={{ ...s.input, maxWidth: 280 }}
          placeholder="🔍 Filtrar cursos..."
          value={filtroCurso}
          onChange={e => setFiltroCurso(e.target.value)}
        />
      </div>

      {listaCursosFiltrados.length === 0 && <div style={s.vazio}>Nenhum curso encontrado.</div>}

      {listaCursosFiltrados.map(curso => {
        const testesDoCursoAtual = testes.filter(t => String(t.cursoId) === String(curso.id));
        const testeParaAlunoAtual = testesDoCursoAtual.filter(t =>
          t.disponivel && turmasDoAluno.some(tu => String(tu.id) === String(t.turmaId))
        );

        return (
          <div key={curso.id} style={{ marginBottom:'2rem' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.75rem', padding:'0.5rem 0.75rem', background:'rgba(245,158,11,0.08)', borderRadius:8, borderLeft:'3px solid #f59e0b' }}>
              <div style={{ fontWeight:600, color:'#2d2640', fontSize:14 }}>🧪 {curso.nome} <span style={{ color:'#5f5874', fontWeight:400, fontSize:12 }}>({testesDoCursoAtual.length} teste{testesDoCursoAtual.length !== 1 ? 's' : ''})</span></div>
              {isGestor && !(formTeste?.cursoId === curso.id) && (
                <button style={s.btnNovo} onClick={() => setFormTeste({ cursoId: curso.id, nome:'', turmaId:'', questoesIds:[], prazo:'', horaInicio:'', horaFim:'' })}>
                  <Plus size={14}/> Novo Teste
                </button>
              )}
            </div>

            {/* Aluno: testes disponíveis */}
            {!isGestor && (
              <>
                {testeParaAlunoAtual.length === 0 && <div style={s.vazio}>Nenhum teste disponível neste curso.</div>}
                {testeParaAlunoAtual.map(teste => {
                  const respondeu = jaRespondeu(teste.id);
                  const expirado = testeExpirado(teste);
                  const naoIniciado = testeNaoIniciado(teste);
                  const horarioInfo = teste.prazo && (teste.horaInicio || teste.horaFim)
                    ? ` · ${fmtData(teste.prazo)}${teste.horaInicio ? ` das ${teste.horaInicio}` : ''}${teste.horaFim ? ` às ${teste.horaFim}` : ''}`
                    : (teste.prazo ? ` · Prazo: ${fmtData(teste.prazo)}` : '');
                  return (
                    <div key={teste.id} style={s.card}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                        <div>
                          <div style={s.cardTitulo}>{teste.nome}</div>
                          <div style={s.cardMeta}>{teste.questoesIds?.length || 0} questões{horarioInfo}</div>
                          {expirado && <div style={{ fontSize:12, color:'#ef4444', marginTop:4 }}>⏰ Prazo encerrado — não é mais possível responder.</div>}
                          {naoIniciado && <div style={{ fontSize:12, color:'#f59e0b', marginTop:4 }}>⏳ Disponível a partir das {teste.horaInicio}.</div>}
                        </div>
                        {respondeu
                          ? <SituacaoReteste teste={teste} tentativas={tentativasDe(teste.id, usuarioAtual?.id)} pedidos={retestesDe(teste, usuarioAtual?.id)}
                              onSolicitar={() => solicitarReteste(teste)}
                              onFazer={(p, num) => setRealizando({ testeId: teste.id, respostas: {}, retesteId: p.id, retesteNum: num, questoesIds: p.questoesIds })} />
                          : expirado || naoIniciado
                            ? <span style={{ color:'#64748b', fontSize:13 }}>{expirado ? 'Encerrado' : 'Aguardando'}</span>
                            : <button style={s.btnNovo} onClick={() => setRealizando({ testeId: teste.id, respostas: {} })}>Iniciar Teste</button>
                        }
                      </div>
                    </div>
                  );
                })}
              </>
            )}

            {/* Gestor: form de criação/edição */}
            {isGestor && formTeste?.cursoId === curso.id && (
              <div style={s.formCard}>
                <div style={s.formTitulo}>{formTeste.id ? 'Editar Teste' : 'Novo Teste'}</div>
                <div style={s.formRow}>
                  <Campo label="Nome do teste *"><input style={s.input} value={formTeste.nome} onChange={e => setFormTeste({...formTeste, nome: e.target.value})} /></Campo>
                  <Campo label="Turma *">
                    <select style={s.input} value={formTeste.turmaId} onChange={e => setFormTeste({...formTeste, turmaId: e.target.value})}>
                      <option value="">Selecione...</option>
                      {turmasDoCurso.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
                    </select>
                  </Campo>
                  <Campo label="Prazo"><input type="date" style={s.input} value={formTeste.prazo} onChange={e => setFormTeste({...formTeste, prazo: e.target.value})} /></Campo>
                  <Campo label="Horário início"><input type="time" style={s.input} value={formTeste.horaInicio||''} onChange={e => setFormTeste({...formTeste, horaInicio: e.target.value})} /></Campo>
                  <Campo label="Horário fim"><input type="time" style={s.input} value={formTeste.horaFim||''} onChange={e => setFormTeste({...formTeste, horaFim: e.target.value})} /></Campo>
                </div>
                {questoesDoCurso.length === 0 && <div style={{ color:'#f59e0b', fontSize:13 }}>⚠️ Nenhuma questão no banco deste curso. Cadastre questões primeiro.</div>}
                {(() => {
                  // Questões do teste em cima (na ordem do teste); o resto do banco
                  // embaixo, filtrado por pasta — antes vinha tudo misturado e as
                  // questões do teste ficavam perdidas no meio do banco.
                  const idsSel = formTeste.questoesIds || [];
                  const doTeste = idsSel.map(id => questoesDoCurso.find(q => q.id === id)).filter(Boolean);
                  const pastasCurso = (pastas || []).filter(p => questoesDoCurso.some(q => q.pastaId === p.id));
                  const pastaFiltro = formTeste.filtroPasta ?? (doTeste[0]?.pastaId ?? '');
                  const resto = questoesDoCurso.filter(q => !idsSel.includes(q.id) && (!pastaFiltro || q.pastaId === pastaFiltro));
                  const linha = (q, i, sel) => (
                    <div key={q.id} style={{ display:'flex', alignItems:'flex-start', gap:'0.5rem', padding:'8px 12px', borderRadius:8, background: sel ? 'rgba(139,92,246,0.08)' : 'transparent', border: sel ? '1px solid rgba(139,92,246,0.3)' : '1px solid #e5e0ef', cursor:'pointer' }} onClick={() => toggleQuestao(q.id)}>
                      <input type="checkbox" checked={sel} readOnly style={{ marginTop:2, accentColor:'#8b5cf6' }} />
                      <div>
                        <span style={{ fontSize:12, color:'#64748b' }}>{sel ? `${i + 1}. ` : ''}{q.codigo ? `${q.codigo} · ` : ''}{q.pontos}pt · </span>
                        <span style={{ fontSize:13, color:'#1c1530' }}>{q.enunciado.substring(0,110)}{q.enunciado.length>110?'...':''}</span>
                      </div>
                    </div>
                  );
                  return (
                    <>
                      <div style={{ fontSize:12, color:'#5f5874', fontWeight:700, marginBottom:'0.5rem' }}>QUESTÕES DESTE TESTE ({doTeste.length})</div>
                      {doTeste.length === 0
                        ? <div style={{ fontSize:13, color:'#64748b', marginBottom:'0.75rem' }}>Nenhuma questão ainda. Escolha abaixo.</div>
                        : <div style={{ display:'flex', flexDirection:'column', gap:'0.375rem', maxHeight:320, overflowY:'auto', marginBottom:'1rem' }}>{doTeste.map((q, i) => linha(q, i, true))}</div>}
                      <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', marginBottom:'0.5rem' }}>
                        <div style={{ fontSize:12, color:'#5f5874', fontWeight:700 }}>ADICIONAR QUESTÕES DO BANCO</div>
                        <select style={{ ...s.input, width:'auto', fontSize:12, padding:'5px 8px' }} value={pastaFiltro} onChange={e => setFormTeste({ ...formTeste, filtroPasta: e.target.value === '' ? '' : Number(e.target.value) })}>
                          <option value="">Todas as pastas</option>
                          {pastasCurso.map(p => <option key={p.id} value={p.id}>📁 {p.nome}</option>)}
                        </select>
                      </div>
                      <div style={{ display:'flex', flexDirection:'column', gap:'0.375rem', maxHeight:260, overflowY:'auto' }}>
                        {resto.length === 0
                          ? <div style={{ fontSize:13, color:'#64748b' }}>Todas as questões desta pasta já estão no teste.</div>
                          : resto.map((q, i) => linha(q, i, false))}
                      </div>
                    </>
                  );
                })()}
                <div style={s.formAcoes}>
                  <button style={s.btnSalvar} onClick={salvarTeste}>Salvar</button>
                  <button style={s.btnCancelar} onClick={() => setFormTeste(null)}>Cancelar</button>
                </div>
              </div>
            )}

            {/* Gestor: lista de testes do curso */}
            {isGestor && testesDoCursoAtual.length === 0 && !(formTeste?.cursoId === curso.id) && <div style={s.vazio}>Nenhum teste cadastrado neste curso.</div>}
            {isGestor && testesDoCursoAtual.map(teste => {
              const qtdResp = respostas.filter(r => r.testeId === teste.id).length;
              return (
                <div key={teste.id} style={s.card}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <div>
                      <div style={s.cardTitulo}>{teste.nome}</div>
                      <div style={s.cardMeta}>{teste.questoesIds?.length || 0} questões · {qtdResp} resposta(s){teste.prazo && ` · Prazo: ${fmtData(teste.prazo)}`}{teste.horaInicio && ` · das ${teste.horaInicio}`}{teste.horaFim && ` às ${teste.horaFim}`}</div>
                    </div>
                    <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                      <button style={{ ...s.btnNovo, background: teste.disponivel ? '#10b981' : '#f7f5fb', border:'1px solid', borderColor: teste.disponivel ? '#10b981' : '#e5e0ef', gap:4, padding:'7px 14px', fontSize:12 }} onClick={() => toggleDisponivel(teste.id)}>
                        {teste.disponivel ? <><Unlock size={13}/> Disponível</> : <><Lock size={13}/> Oculto</>}
                      </button>
                      <button style={s.btnIcone} onClick={() => setFormTeste({...teste})}><Edit2 size={13}/></button>
                      <button style={{...s.btnIcone, color:'#ef4444'}} onClick={() => { if(window.confirm('Excluir teste?')) { setTestes(testes.filter(t => t.id !== teste.id)); toast.sucesso('Teste excluído.'); }}}><Trash2 size={13}/></button>
                    </div>
                  </div>
                  {(teste.retestes || []).filter(p => p.status === 'solicitado').map(p => (
                    <div key={p.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:8, flexWrap:'wrap', marginTop:10, padding:'8px 12px', background:'rgba(245,158,11,0.08)', border:'1px solid rgba(245,158,11,0.3)', borderRadius:8 }}>
                      <span style={{ fontSize:13, color:'#92400e' }}>🔁 <strong>{p.alunoNome}</strong> solicitou reteste em {new Date(p.solicitadoEm).toLocaleString('pt-BR', { dateStyle:'short', timeStyle:'short' })}</span>
                      <button style={{ ...s.btnNovo, padding:'6px 12px', fontSize:12 }} onClick={() => liberarReteste(teste, p)}>Liberar reteste</button>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

// Situação do aluno num teste já respondido: aprovado, ou reprovado com o
// caminho do reteste (solicitar → aguardar liberação → fazer), até 2 vezes.
const MAX_RETESTES = 2;
function SituacaoReteste({ tentativas, pedidos, onSolicitar, onFazer }) {
  const ultima = tentativas[tentativas.length - 1];
  const pct = ultima?.percentual ?? 0;
  if (pct >= 70) return <span style={{ color:'#10b981', fontSize:13, fontWeight:600 }}>✓ Aprovado ({fmtPct(pct)})</span>;
  const liberado = pedidos.find(p => p.status === 'liberado');
  const solicitado = pedidos.find(p => p.status === 'solicitado');
  const reprovado = <span style={{ color:'#ef4444', fontSize:12, fontWeight:600 }}>Reprovado ({fmtPct(pct)})</span>;
  const caixa = (conteudo) => <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4 }}>{reprovado}{conteudo}</div>;
  if (liberado) return caixa(<button style={s.btnNovo} onClick={() => onFazer(liberado, pedidos.indexOf(liberado) + 1)}>Fazer reteste {pedidos.indexOf(liberado) + 1}</button>);
  if (solicitado) return caixa(<span style={{ color:'#b45309', fontSize:12 }}>Reteste solicitado — aguardando liberação do instrutor</span>);
  if (pedidos.length >= MAX_RETESTES) return caixa(<span style={{ color:'#64748b', fontSize:12 }}>Limite de {MAX_RETESTES} retestes atingido. Fale com o instrutor.</span>);
  return caixa(<button style={s.btnNovo} onClick={onSolicitar}>Solicitar reteste ({MAX_RETESTES - pedidos.length} restante{MAX_RETESTES - pedidos.length > 1 ? 's' : ''})</button>);
}

// ── ABA: Resultados ────────────────────────────────────────────────────────
function AbaResultados({ cursos, turmas, testes, respostas, questoes, isGestor, usuarioAtual, cursosDoAluno }) {
  const [cursoSel, setCursoSel] = useState('');
  const [testeSel, setTesteSel] = useState('');

  const listaCursos = isGestor ? cursos : cursosDoAluno;
  const testesDoCurso = testes.filter(t => String(t.cursoId) === String(cursoSel));

  const resultadosFiltrados = respostas.filter(r => {
    const teste = testes.find(t => t.id === r.testeId);
    if (!teste || String(teste.cursoId) !== String(cursoSel)) return false;
    if (testeSel && r.testeId !== testeSel) return false;
    if (!isGestor) return r.alunoId === String(usuarioAtual?.id);
    return true;
  });

  const exportarResultadoPDF = (resp) => {
    const teste = testes.find(t => t.id === resp.testeId);
    const qs = (resp.questoesIds || teste?.questoesIds || []).map(qId => questoes.find(q => q.id === qId)).filter(Boolean);
    const html = `
      <div class="campo"><span class="label">Aluno</span><div class="valor">${resp.alunoNome}</div></div>
      <div class="campo"><span class="label">Teste</span><div class="valor">${teste?.nome || '—'}</div></div>
      <div class="campo"><span class="label">Data de realização</span><div class="valor">${new Date(resp.dataRealizacao).toLocaleString('pt-BR')}</div></div>
      <div class="campo"><span class="label">Pontuação</span><div class="valor" style="font-size:22px;font-weight:bold;color:${resp.percentual>=70?'#059669':'#dc2626'};">${resp.pontuacao}/${resp.total} pontos (${resp.percentual.toFixed(1)}%)</div></div>
      <div class="campo"><span class="label">Resultado</span><div class="valor"><span class="badge" style="background:${resp.percentual>=70?'#d1fae5':'#fee2e2'};color:${resp.percentual>=70?'#065f46':'#991b1b'};">${resp.percentual>=70?'APROVADO':'NECESSITA DESENVOLVIMENTO'}</span></div></div>
      <h2>Gabarito detalhado</h2>
      <table>
        <thead><tr><th>Questão</th><th>Resposta</th><th>Resultado</th><th>Pts</th></tr></thead>
        <tbody>
          ${qs.map((q, i) => {
            const respIdx = resp.respostas[q.id];
            const correta = q.alternativas.findIndex(a => a.correta);
            const acertou = respIdx === correta;
            return `<tr>
              <td>${q.enunciado.substring(0,80)}</td>
              <td>${respIdx !== undefined ? `${String.fromCharCode(65+respIdx)}) ${q.alternativas[respIdx]?.texto}` : 'Não respondida'}</td>
              <td style="color:${acertou?'#059669':'#dc2626'}">${acertou?'✓ Correta':'✗ Incorreta'}</td>
              <td>${acertou?q.pontos:0}/${q.pontos}</td>
            </tr>`;
          }).join('')}
        </tbody>
      </table>
      ${resp.percentual < 70 ? '<h2>Indicações de Desenvolvimento</h2><p>Recomendamos a revisão do conteúdo das questões incorretas. Entre em contato com o instrutor para orientações adicionais.</p>' : '<h2>Parabéns!</h2><p>Aprovação confirmada. Continue com excelência!</p>'}
    `;
    exportarPDF(html, `Resultado - ${resp.alunoNome} - ${teste?.nome}`);
  };

  const exportarResultadoWord = (resp) => {
    const teste = testes.find(t => t.id === resp.testeId);
    const qs = (resp.questoesIds || teste?.questoesIds || []).map(qId => questoes.find(q => q.id === qId)).filter(Boolean);
    const html = `
      <div><strong>Aluno:</strong> ${resp.alunoNome}</div>
      <div><strong>Teste:</strong> ${teste?.nome || '—'}</div>
      <div><strong>Data:</strong> ${new Date(resp.dataRealizacao).toLocaleString('pt-BR')}</div>
      <div><strong>Pontuação:</strong> ${resp.pontuacao}/${resp.total} (${resp.percentual.toFixed(1)}%)</div>
      <div><strong>Resultado:</strong> ${resp.percentual>=70?'APROVADO':'NECESSITA DESENVOLVIMENTO'}</div>
      <table>
        <thead><tr><th>Questão</th><th>Resposta</th><th>Resultado</th><th>Pts</th></tr></thead>
        <tbody>
          ${qs.map((q, i) => {
            const respIdx = resp.respostas[q.id];
            const correta = q.alternativas.findIndex(a => a.correta);
            const acertou = respIdx === correta;
            return `<tr><td>${q.enunciado}</td><td>${respIdx !== undefined ? q.alternativas[respIdx]?.texto : 'Não respondida'}</td><td>${acertou?'Correta':'Incorreta'}</td><td>${acertou?q.pontos:0}/${q.pontos}</td></tr>`;
          }).join('')}
        </tbody>
      </table>
    `;
    exportarWord(html, `Resultado - ${resp.alunoNome} - ${teste?.nome}`);
  };

  return (
    <div style={s.secao}>
      <div style={s.secaoHeader}>
        <div style={s.secaoTitulo}>{isGestor ? 'Resultados dos Testes' : 'Meus Resultados'}</div>
        <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
          <select style={s.select} value={cursoSel} onChange={e => { setCursoSel(e.target.value); setTesteSel(''); }}>
            <option value="">Selecione o curso...</option>
            {listaCursos.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
          {cursoSel && (
            <select style={s.select} value={testeSel} onChange={e => setTesteSel(e.target.value)}>
              <option value="">Todos os testes</option>
              {testesDoCurso.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
            </select>
          )}
        </div>
      </div>

      {!cursoSel && <div style={s.vazio}>Selecione um curso para ver os resultados.</div>}
      {cursoSel && resultadosFiltrados.length === 0 && <div style={s.vazio}>Nenhum resultado encontrado.</div>}

      {resultadosFiltrados.sort((a,b) => new Date(b.dataRealizacao) - new Date(a.dataRealizacao)).map(resp => {
        const teste = testes.find(t => t.id === resp.testeId);
        const aprovado = resp.percentual >= 70;
        return (
          <div key={resp.id} style={s.card}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
              <div style={{ flex:1 }}>
                {isGestor && <div style={{ fontSize:13, fontWeight:700, color:'#1c1530', marginBottom:2 }}>{resp.alunoNome}</div>}
                <div style={s.cardTitulo}>{teste?.nome || '—'}{resp.retesteNum ? ` — Reteste ${resp.retesteNum}` : ''}</div>
                <div style={s.cardMeta}>{new Date(resp.dataRealizacao).toLocaleString('pt-BR')}</div>
                <div style={{ display:'flex', alignItems:'center', gap:'1rem', marginTop:'0.5rem' }}>
                  <div style={{ fontSize:24, fontWeight:800, color: aprovado ? '#10b981' : '#ef4444' }}>
                    {resp.pontuacao}/{resp.total}
                    <span style={{ fontSize:14, fontWeight:400, marginLeft:4 }}>pts</span>
                  </div>
                  <div>
                    <div style={{ fontSize:18, fontWeight:700, color: aprovado ? '#10b981' : '#ef4444' }}>{fmtPct(resp.percentual)}</div>
                    <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:20, background: aprovado ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', color: aprovado ? '#10b981' : '#ef4444', border: `1px solid ${aprovado?'rgba(16,185,129,0.3)':'rgba(239,68,68,0.3)'}` }}>
                      {aprovado ? '✓ APROVADO' : '⚠ NECESSITA DESENVOLVIMENTO'}
                    </span>
                  </div>
                </div>
                {!aprovado && <div style={{ marginTop:'0.5rem', fontSize:12, color:'#f59e0b' }}>💡 Recomendamos revisão do conteúdo e nova tentativa com o instrutor.</div>}
              </div>
              <div style={{ display:'flex', gap:4, flexShrink:0 }}>
                <button style={{...s.btnNovo, background:'#7c3aed', padding:'6px 12px', fontSize:12}} onClick={() => exportarResultadoPDF(resp)}>PDF</button>
                <button style={{...s.btnNovo, background:'#1d4ed8', padding:'6px 12px', fontSize:12}} onClick={() => exportarResultadoWord(resp)}>Word</button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── ABA: Calendário de Atividades ─────────────────────────────────────────
// Converte evento do Treinamentos em agendamento da Agenda principal
function AbaAgenda({ cursos, turmas, agendaTr, setAgendaTr, isGestor, turmasDoAluno, cursosDoAluno, agendamentos, setAgendamentos }) {
  const [form, setForm] = useState(null);
  const [mesSel, setMesSel] = useState(() => new Date().toISOString().slice(0,7));

  const eventosDoMes = agendaTr.filter(e => e.data?.startsWith(mesSel));
  const eventosFiltrados = isGestor ? eventosDoMes : eventosDoMes.filter(e => turmasDoAluno.some(t => String(t.id) === String(e.turmaId)));

  // Sincroniza evento com a agenda principal (upsert pelo id derivado)
  const sincronizarAgenda = (ev) => {
    if (!setAgendamentos) return;
    const ag = eventoParaAgendamento(ev, cursos, turmas);
    setAgendamentos(prev => {
      const sem = prev.filter(a => a.origemEvId !== ev.id);
      return [...sem, ag];
    });
  };

  const removerDaAgenda = (evId) => {
    if (!setAgendamentos) return;
    setAgendamentos(prev => prev.filter(a => a.origemEvId !== evId));
  };

  const salvar = () => {
    if (!form.titulo || !form.data) { toast.aviso('Informe título e data.'); return; }
    if (form.id) {
      const atualizado = { ...form };
      setAgendaTr(agendaTr.map(e => e.id === form.id ? atualizado : e));
      sincronizarAgenda(atualizado);
      toast.sucesso('Evento atualizado!');
    } else {
      const novo = { ...form, id: novoId() };
      setAgendaTr([...agendaTr, novo]);
      sincronizarAgenda(novo);
      toast.sucesso('Evento criado!');
    }
    setForm(null);
  };

  const excluirEvento = (ev) => {
    if (!window.confirm('Excluir evento?')) return;
    setAgendaTr(agendaTr.filter(e => e.id !== ev.id));
    removerDaAgenda(ev.id);
    toast.sucesso('Evento excluído.');
  };

  const tipos = { aula:'🎬 Aula', prova:'📝 Prova/Teste', evento:'📅 Evento', sincrona:'🔴 Aula ao Vivo', aberturaTurma:'🎓 Abertura de Turma' };

  return (
    <div style={s.secao}>
      <div style={s.secaoHeader}>
        <div style={s.secaoTitulo}>Calendário de Atividades</div>
        <div style={{ display:'flex', gap:'0.5rem', alignItems:'center' }}>
          <input type="month" style={s.select} value={mesSel} onChange={e => setMesSel(e.target.value)} />
          {isGestor && !form && <button style={s.btnNovo} onClick={() => setForm({ titulo:'', cursoId:'', turmaId:'', data:'', hora:'', horaFim:'', tipo:'aula', descricao:'', link:'' })}><Plus size={14}/> Novo Evento</button>}
        </div>
      </div>

      {isGestor && form && (
        <div style={s.formCard}>
          <div style={s.formTitulo}>{form.id ? 'Editar Evento' : 'Novo Evento'}</div>
          <div style={s.formRow}>
            <Campo label="Título *"><input style={s.input} value={form.titulo} onChange={e => setForm({...form, titulo: e.target.value})} /></Campo>
            <Campo label="Tipo">
              <select style={s.input} value={form.tipo} onChange={e => setForm({...form, tipo: e.target.value})}>
                {Object.entries(tipos).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Campo>
          </div>
          <div style={s.formRow}>
            <Campo label="Curso">
              <select style={s.input} value={form.cursoId} onChange={e => setForm({...form, cursoId: e.target.value, turmaId:''})}>
                <option value="">Todos</option>
                {cursos.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </Campo>
            <Campo label="Turma">
              <select style={s.input} value={form.turmaId} onChange={e => setForm({...form, turmaId: e.target.value})}>
                <option value="">Todas</option>
                {turmas.filter(t => !form.cursoId || String(t.cursoId) === String(form.cursoId)).map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
              </select>
            </Campo>
          </div>
          <div style={s.formRow}>
            <Campo label="Data *"><input type="date" style={s.input} value={form.data} onChange={e => setForm({...form, data: e.target.value})} /></Campo>
            <Campo label="Início"><input type="time" style={s.input} value={form.hora} onChange={e => setForm({...form, hora: e.target.value})} /></Campo>
            <Campo label="Fim"><input type="time" style={s.input} value={form.horaFim} onChange={e => setForm({...form, horaFim: e.target.value})} /></Campo>
          </div>
          {form.tipo === 'sincrona' && <Campo label="Link da aula"><input style={s.input} value={form.link} placeholder="https://teams.microsoft.com/..." onChange={e => setForm({...form, link: e.target.value})} /></Campo>}
          <Campo label="Descrição"><textarea style={{...s.input, height:60, resize:'vertical'}} value={form.descricao} onChange={e => setForm({...form, descricao: e.target.value})} /></Campo>
          <div style={{ fontSize:12, color:'rgba(139,92,246,0.8)', marginBottom:'0.25rem' }}>
            📅 Este evento também aparecerá na Agenda principal como Treinamento.
          </div>
          <div style={s.formAcoes}>
            <button style={s.btnSalvar} onClick={salvar}>Salvar</button>
            <button style={s.btnCancelar} onClick={() => setForm(null)}>Cancelar</button>
          </div>
        </div>
      )}

      {eventosFiltrados.length === 0 && <div style={s.vazio}>Nenhum evento neste mês.</div>}

      {[...eventosFiltrados].sort((a,b) => a.data.localeCompare(b.data)).map(ev => {
        const curso = cursos.find(c => c.id === ev.cursoId);
        const corBorda = ev.tipo === 'prova' ? '#ef4444' : ev.tipo === 'sincrona' ? '#f59e0b' : ev.tipo === 'aberturaTurma' ? '#10b981' : '#8b5cf6';
        return (
          <div key={ev.id} style={{ ...s.card, borderLeft:'3px solid', borderLeftColor: corBorda }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
              <div>
                <div style={{ fontSize:12, color:'#64748b', marginBottom:2 }}>{tipos[ev.tipo] || ev.tipo} · {fmtData(ev.data)}{ev.hora && ` · ${ev.hora}${ev.horaFim ? ` – ${ev.horaFim}` : ''}`}</div>
                <div style={s.cardTitulo}>{ev.titulo}</div>
                {curso && <div style={s.cardMeta}>{curso.nome}</div>}
                {ev.descricao && <div style={{ fontSize:13, color:'#64748b', marginTop:4, whiteSpace:'pre-wrap' }}>{ev.descricao}</div>}
                {ev.link && <a href={ev.link} target="_blank" rel="noopener noreferrer" style={{ display:'inline-flex', alignItems:'center', gap:4, color:'#f59e0b', fontSize:13, marginTop:6, textDecoration:'none' }}><ExternalLink size={13}/> Entrar na aula</a>}
                <div style={{ fontSize:11, color:'rgba(139,92,246,0.6)', marginTop:4 }}>📅 Sincronizado com Agenda</div>
              </div>
              {isGestor && (
                <div style={{ display:'flex', gap:4 }}>
                  <button style={s.btnIcone} onClick={() => setForm({...ev})}><Edit2 size={13}/></button>
                  <button style={{...s.btnIcone, color:'#ef4444'}} onClick={() => excluirEvento(ev)}><Trash2 size={13}/></button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── ABA: Meu Perfil (Aluno) ────────────────────────────────────────────────
function AbaMeuPerfil({ usuarioAtual, perfisExtra, setPerfisExtra }) {
  const extra = perfisExtra.find(p => p.userId === String(usuarioAtual?.id)) || {};
  const [form, setForm] = useState({ telefone:'', whatsapp:'', empresa:'', cargo:'', bio:'', ...extra, userId: String(usuarioAtual?.id) });
  const [editando, setEditando] = useState(false);

  const salvar = () => {
    const novo = { ...form, userId: String(usuarioAtual?.id) };
    const existe = perfisExtra.some(p => p.userId === novo.userId);
    setPerfisExtra(existe ? perfisExtra.map(p => p.userId === novo.userId ? novo : p) : [...perfisExtra, novo]);
    setEditando(false);
    toast.sucesso('Perfil atualizado!');
  };

  return (
    <div style={s.secao}>
      <div style={s.secaoHeader}>
        <div style={s.secaoTitulo}>Meu Perfil</div>
        {!editando && <button style={s.btnNovo} onClick={() => setEditando(true)}><Edit2 size={14}/> Editar</button>}
      </div>
      <div style={s.card}>
        <div style={{ display:'flex', alignItems:'center', gap:'1rem', marginBottom:'1.5rem' }}>
          {form.foto
            ? <img src={form.foto} alt={`Foto de ${usuarioAtual?.nome || 'aluno'}`} style={{ width:60, height:60, borderRadius:'50%', objectFit:'cover', flexShrink:0 }} />
            : (
              <div style={{ width:60, height:60, borderRadius:'50%', background:'linear-gradient(135deg,#7c3aed,#4f46e5)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, color:'white', fontWeight:700, flexShrink:0 }}>
                {(usuarioAtual?.nome||'A')[0].toUpperCase()}
              </div>
            )}
          <div>
            <div style={{ fontSize:20, fontWeight:700, color:'#1c1530' }}>{usuarioAtual?.nome}</div>
            <div style={{ fontSize:13, color:'#5f5874' }}>{usuarioAtual?.email}</div>
            <span style={{ fontSize:11, padding:'2px 8px', borderRadius:20, background:'rgba(59,130,246,0.1)', color:'#3b82f6', border:'1px solid rgba(59,130,246,0.3)', fontWeight:600 }}>📚 Aluno</span>
          </div>
        </div>
        {editando ? (
          <>
            <div style={s.formRow}>
              <Campo label="Telefone / WhatsApp"><input style={s.input} value={form.telefone} placeholder="(11) 99999-9999" onChange={e => setForm({...form, telefone: e.target.value})} /></Campo>
              <Campo label="Empresa"><input style={s.input} value={form.empresa} onChange={e => setForm({...form, empresa: e.target.value})} /></Campo>
              <Campo label="Cargo / Função"><input style={s.input} value={form.cargo} onChange={e => setForm({...form, cargo: e.target.value})} /></Campo>
            </div>
            <Campo label="Foto">
              <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                <input
                  type="file"
                  accept="image/*"
                  aria-label="Escolher foto do perfil"
                  onChange={async e => {
                    const arq = e.target.files?.[0];
                    e.target.value = '';
                    if (!arq) return;
                    if (!arq.type.startsWith('image/')) { toast.aviso('Escolha um arquivo de imagem.'); return; }
                    try { setForm(f => ({ ...f, foto: '' })); const foto = await reduzirFoto(arq); setForm(f => ({ ...f, foto })); }
                    catch (err) { toast.erro(err.message); }
                  }}
                  style={{ fontSize:13 }}
                />
                {form.foto && <button type="button" style={s.btnCancelar} onClick={() => setForm(f => ({ ...f, foto: '' }))}>Remover foto</button>}
              </div>
            </Campo>
            <Campo label="Bio / Apresentação"><textarea style={{...s.input, height:80, resize:'vertical'}} value={form.bio} onChange={e => setForm({...form, bio: e.target.value})} /></Campo>
            <div style={s.formAcoes}>
              <button style={s.btnSalvar} onClick={salvar}>Salvar</button>
              <button style={s.btnCancelar} onClick={() => setEditando(false)}>Cancelar</button>
            </div>
          </>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:'1rem' }}>
            {[['Telefone', form.telefone], ['Empresa', form.empresa], ['Cargo', form.cargo]].map(([label, valor]) => (
              <div key={label}>
                <div style={{ fontSize:11, color:'#64748b', fontWeight:700, textTransform:'uppercase', marginBottom:4 }}>{label}</div>
                <div style={{ color:'#1c1530', fontSize:14 }}>{valor || '—'}</div>
              </div>
            ))}
            {form.bio && <div style={{ gridColumn:'1/-1' }}><div style={{ fontSize:11, color:'#64748b', fontWeight:700, textTransform:'uppercase', marginBottom:4 }}>Bio</div><div style={{ color:'#64748b', fontSize:14, lineHeight:1.6 }}>{form.bio}</div></div>}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Meu Perfil (aluno): histórico de simulados e análise por item ──────────
// Bianca, 30/10/2026: "meus resultados pode puxar histórico de realização de
// simulados?" e "uma análise em qual item da norma o aluno precisa estudar
// mais". O item é a pasta da questão no Banco de Questões (as pastas seguem
// as seções da norma). Entram testes e simulados: cada questão respondida
// conta uma vez por tentativa.
const META_ITEM = 70;
const juntarComE = (l) => l.length <= 1 ? (l[0] || '') : `${l.slice(0, -1).join(', ')} e ${l[l.length - 1]}`;

function AbaDesempenhoAluno({ respostas = [], questoes = [], pastas = [], testes = [], usuarioAtual }) {
  const minhas = respostas.filter(r => r.alunoId === String(usuarioAtual?.id));
  const simulados = minhas
    .filter(r => r.tipo === 'simulado')
    .sort((a, b) => String(b.dataRealizacao).localeCompare(String(a.dataRealizacao)));
  const nomePasta = (id) => pastas.find(p => String(p.id) === String(id))?.nome || 'Sem item definido';
  const questaoPorId = new Map(questoes.map(q => [String(q.id), q]));

  const porItem = new Map();
  for (const r of minhas) {
    for (const [qId, idx] of Object.entries(r.respostas || {})) {
      const q = questaoPorId.get(String(qId));
      if (!q) continue;
      const corr = (q.alternativas || []).findIndex(a => a.correta);
      // Com item da norma, agrupa por norma + item (6.1 da 9001 ≠ 6.1 da 14001);
      // sem ele, cai na pasta.
      const norma = (nomePasta(q.pastaId).match(/ISO\s*\d+/i) || [''])[0].toUpperCase().replace(/\s+/, ' ');
      const item = (q.itemNorma || '').trim();
      const chave = item ? `${norma}|${item}` : `pasta|${q.pastaId ?? ''}`;
      const atual = porItem.get(chave) || {
        item: item ? [norma, item].filter(Boolean).join(' · ') : nomePasta(q.pastaId),
        codigo: item ? [norma, item.split(/\s/)[0]].filter(Boolean).join(' ') : '',
        acertos: 0, total: 0,
      };
      atual.total += 1;
      if (idx === corr) atual.acertos += 1;
      porItem.set(chave, atual);
    }
  }
  const itens = [...porItem.values()]
    .map(i => ({ ...i, pct: i.total ? (i.acertos / i.total) * 100 : 0 }))
    .sort((a, b) => a.pct - b.pct || a.item.localeCompare(b.item, 'pt-BR'));
  const estudar = itens.filter(i => i.pct < META_ITEM);

  return (
    <>
      <div style={s.secao}>
        <div style={s.secaoHeader}><div style={s.secaoTitulo}>🎯 Onde estudar mais</div></div>
        {itens.length === 0 && <div style={s.vazio}>A análise aparece depois do primeiro teste ou simulado.</div>}
        {itens.length > 0 && (
          <div style={s.card}>
            <div style={{ fontSize: 13, color: '#5f5874', marginBottom: 12 }}>
              {estudar.length > 0
                ? (
                  <>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#b45309', marginBottom: 4 }}>
                      Estude mais: {juntarComE(estudar.map(i => i.codigo || i.item))}.
                    </div>
                    Itens com aproveitamento abaixo de {META_ITEM}% nos seus testes e simulados aparecem primeiro.
                  </>
                )
                : <>Todos os itens respondidos estão com aproveitamento igual ou acima de {META_ITEM}%.</>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {itens.map(i => {
                const cor = i.pct < 50 ? '#dc2626' : i.pct < META_ITEM ? '#d97706' : '#059669';
                return (
                  <div key={i.item}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 13, color: '#1c1530', marginBottom: 4 }}>
                      <span style={{ fontWeight: i.pct < META_ITEM ? 700 : 500 }}>{i.pct < META_ITEM ? '⚠️ ' : ''}{i.item}</span>
                      <span style={{ color: cor, fontWeight: 700, flexShrink: 0 }}>{fmtPct(i.pct)} · {i.acertos}/{i.total} acertos</span>
                    </div>
                    <div style={{ height: 8, background: '#eeeaf5', borderRadius: 4, overflow: 'hidden' }} role="progressbar" aria-valuenow={Math.round(i.pct)} aria-valuemin={0} aria-valuemax={100} aria-label={`Aproveitamento em ${i.item}`}>
                      <div style={{ width: `${Math.max(2, i.pct)}%`, height: '100%', background: cor }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div style={s.secao}>
        <div style={s.secaoHeader}><div style={s.secaoTitulo}>📝 Histórico de simulados</div></div>
        {simulados.length === 0 && <div style={s.vazio}>Nenhum simulado realizado.</div>}
        {simulados.length > 0 && (
          <div style={{ ...s.card, padding: 0, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f7f5fb', color: '#5f5874', textAlign: 'left' }}>
                  <th style={{ padding: '8px 12px' }}>Data</th>
                  <th style={{ padding: '8px 12px' }}>Simulado</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right' }}>Acertos</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right' }}>Aproveitamento</th>
                </tr>
              </thead>
              <tbody>
                {simulados.map(r => (
                  <tr key={r.id} style={{ borderTop: '1px solid #eeeaf5' }}>
                    <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>{new Date(r.dataRealizacao).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}</td>
                    <td style={{ padding: '8px 12px' }}>{nomePasta(r.pastaId)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>{r.pontuacao}/{r.total}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: (r.percentual ?? 0) >= META_ITEM ? '#059669' : '#dc2626' }}>{fmtPct(r.percentual ?? 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

// Foto do perfil: reduzida a 256 px no navegador e guardada como JPEG
// (cerca de 20 KB), para não pesar a gravação dos dados do Ensino.
function reduzirFoto(arquivo) {
  return new Promise((resolve, reject) => {
    const leitor = new FileReader();
    leitor.onerror = () => reject(new Error('Não foi possível ler a imagem.'));
    leitor.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Arquivo de imagem inválido.'));
      img.onload = () => {
        const lado = 256;
        const escala = Math.min(1, lado / Math.min(img.width, img.height));
        const w = Math.round(img.width * escala), h = Math.round(img.height * escala);
        const canvas = document.createElement('canvas');
        canvas.width = Math.min(w, lado); canvas.height = Math.min(h, lado);
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.src = leitor.result;
    };
    leitor.readAsDataURL(arquivo);
  });
}

// ── Helpers ────────────────────────────────────────────────────────────────
function Campo({ label, children, style }) {
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:5, flex:1, minWidth:140, ...style }}>
      <label style={{ fontSize:11, fontWeight:700, color:'#5f5874', textTransform:'uppercase', letterSpacing:'0.05em' }}>{label}</label>
      {children}
    </div>
  );
}

// ── ABA: Simulados ─────────────────────────────────────────────────────────
function AbaSimulados({ cursosDoAluno = [], questoes, pastas: todasPastas, testes = [], respostas, setRespostas, usuarioAtual }) {
  // Só cursos em que o aluno está matriculado (antes listava todos os cursos).
  const cursos = cursosDoAluno;
  // Só pastas marcadas como simulado no Banco de Questões (pastaEhSimulado).
  const pastas = todasPastas.filter(p => pastaEhSimulado(p, questoes, testes));
  const [realizando, setRealizando] = useState(null);
  // realizando: { pastaId, cursoId, questoes: [...], respostas: {}, enviado: false }

  const embaralhar = (arr) => [...arr].sort(() => Math.random() - 0.5);

  const iniciarSimulado = (pasta, cursoId) => {
    const qs = embaralhar(questoes.filter(q => q.pastaId === pasta.id));
    if (qs.length === 0) { toast.aviso('Esta pasta não tem questões cadastradas.'); return; }
    setRealizando({ pastaId: pasta.id, cursoId, questoes: qs, respostas: {}, enviado: false });
  };

  const responder = (qId, idx) => {
    if (realizando.enviado) return;
    setRealizando(prev => ({ ...prev, respostas: { ...prev.respostas, [qId]: idx } }));
  };

  const enviarSimulado = () => {
    const { questoes: qs, respostas: resp } = realizando;
    const total = qs.reduce((s, q) => s + (q.pontos || 1), 0);
    let pontuacao = 0;
    qs.forEach(q => {
      const corrIdx = q.alternativas.findIndex(a => a.correta);
      if (resp[q.id] === corrIdx) pontuacao += (q.pontos || 1);
    });
    const percentual = total > 0 ? (pontuacao / total) * 100 : 0;
    const nova = {
      id: novoId(),
      tipo: 'simulado',
      pastaId: realizando.pastaId,
      cursoId: realizando.cursoId,
      alunoId: String(usuarioAtual?.id),
      alunoNome: usuarioAtual?.nome || usuarioAtual?.email || 'Aluno',
      pontuacao, total, percentual,
      respostas: resp,
      dataRealizacao: new Date().toISOString(),
    };
    setRespostas(prev => [...prev, nova]);
    setRealizando(prev => ({ ...prev, enviado: true, resultado: nova }));
  };

  // Tela de realização do simulado
  if (realizando) {
    const { questoes: qs, respostas: resp, enviado, resultado, pastaId } = realizando;
    const pasta = pastas.find(p => p.id === pastaId);
    const totalRespondidas = Object.keys(resp).length;

    if (enviado && resultado) {
      const aprovado = resultado.percentual >= 70;
      return (
        <div style={s.secao}>
          <div style={{ textAlign:'center', padding:'2rem 1rem' }}>
            <div style={{ fontSize:48, marginBottom:'1rem' }}>{aprovado ? '🏆' : '📚'}</div>
            <div style={{ fontSize:22, fontWeight:700, color: aprovado ? '#10b981' : '#f59e0b', marginBottom:'0.5rem' }}>
              {aprovado ? 'Muito bem!' : 'Continue praticando!'}
            </div>
            <div style={{ fontSize:15, color:'#64748b', marginBottom:'1.5rem' }}>
              Simulado: <strong style={{ color:'#2d2640' }}>{pasta?.nome}</strong>
            </div>
            <div style={{ fontSize:32, fontWeight:700, color: aprovado ? '#10b981' : '#f59e0b', marginBottom:'0.25rem' }}>
              {resultado.pontuacao}/{resultado.total} pts
            </div>
            <div style={{ fontSize:18, color:'#5f5874', marginBottom:'2rem' }}>{resultado.percentual.toFixed(1)}% de acerto</div>

            {/* Gabarito */}
            <div style={{ textAlign:'left', maxWidth:640, margin:'0 auto', display:'flex', flexDirection:'column', gap:'0.75rem', marginBottom:'2rem' }}>
              {qs.map((q, i) => {
                const corrIdx = q.alternativas.findIndex(a => a.correta);
                const respIdx = resp[q.id];
                const acertou = respIdx === corrIdx;
                return (
                  <div key={q.id} style={{ background: acertou ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)', border:`1px solid ${acertou?'rgba(16,185,129,0.25)':'rgba(239,68,68,0.25)'}`, borderRadius:8, padding:'0.75rem 1rem' }}>
                    <div style={{ fontSize:13, color:'#1c1530', marginBottom:'0.5rem' }}><strong>Q{i+1}.</strong> {q.enunciado}</div>
                    {q.alternativas.map((alt, ai) => (
                      <div key={ai} style={{ fontSize:12, color: ai===corrIdx ? '#10b981' : ai===respIdx && !acertou ? '#ef4444' : '#64748b', marginLeft:'0.75rem', marginBottom:2 }}>
                        {ai===corrIdx ? '✓' : ai===respIdx ? '✗' : '·'} {String.fromCharCode(65+ai)}) {alt.texto}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>

            <div style={{ display:'flex', gap:'1rem', justifyContent:'center', flexWrap:'wrap' }}>
              <button style={s.btnNovo} onClick={() => iniciarSimulado(pasta, realizando.cursoId)}>
                🔄 Refazer Simulado
              </button>
              <button style={s.btnCancelar} onClick={() => setRealizando(null)}>
                ← Voltar aos Simulados
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div style={s.secao}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem' }}>
          <div>
            <div style={s.secaoTitulo}>📝 {pasta?.nome}</div>
            <div style={{ fontSize:12, color:'#5f5874' }}>{qs.length} questões · {totalRespondidas}/{qs.length} respondidas</div>
          </div>
          <button style={s.btnCancelar} onClick={() => setRealizando(null)}>✕ Sair</button>
        </div>

        {/* Barra de progresso */}
        <div style={{ height:6, background:'#f3f0f9', borderRadius:3, marginBottom:'1.5rem', overflow:'hidden' }}>
          <div style={{ height:'100%', background:'#8b5cf6', borderRadius:3, width:`${(totalRespondidas/qs.length)*100}%`, transition:'width 0.3s' }} />
        </div>

        <div style={{ display:'flex', flexDirection:'column', gap:'1.25rem' }}>
          {qs.map((q, i) => {
            const respIdx = resp[q.id];
            return (
              <div key={q.id} style={{ background:'#ffffff', border:'1px solid #e5e0ef', borderRadius:10, padding:'1rem 1.25rem' }}>
                <div style={{ fontSize:14, color:'#1c1530', fontWeight:500, marginBottom:'0.875rem' }}>
                  <span style={{ color:'#8b5cf6', marginRight:6 }}>Q{i+1}.</span>{q.enunciado}
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
                  {q.alternativas.map((alt, ai) => {
                    const selecionada = respIdx === ai;
                    return (
                      <div key={ai}
                        onClick={() => responder(q.id, ai)}
                        style={{ display:'flex', alignItems:'flex-start', gap:'0.75rem', padding:'0.625rem 0.875rem', borderRadius:8, cursor:'pointer', border:`1px solid ${selecionada ? '#8b5cf6' : '#e5e0ef'}`, background: selecionada ? 'rgba(139,92,246,0.12)' : 'transparent', transition:'all 0.15s' }}>
                        <div style={{ width:20, height:20, borderRadius:'50%', border:`2px solid ${selecionada?'#8b5cf6':'#e5e0ef'}`, background: selecionada?'#8b5cf6':'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:1 }}>
                          {selecionada && <div style={{ width:8, height:8, borderRadius:'50%', background:'white' }} />}
                        </div>
                        <span style={{ fontSize:13, color: selecionada ? '#1c1530' : '#64748b' }}>
                          <strong style={{ color: selecionada ? '#8b5cf6' : '#64748b' }}>{String.fromCharCode(65+ai)})</strong> {alt.texto}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ marginTop:'1.5rem', display:'flex', justifyContent:'center' }}>
          <button
            style={{ ...s.btnSalvar, padding:'12px 32px', fontSize:15, opacity: totalRespondidas < qs.length ? 0.5 : 1 }}
            onClick={enviarSimulado}
            disabled={totalRespondidas < qs.length}>
            {totalRespondidas < qs.length ? `Responda todas (${qs.length - totalRespondidas} restantes)` : '✓ Enviar Simulado'}
          </button>
        </div>
      </div>
    );
  }

  // Tela principal — lista de simulados por curso
  return (
    <div style={s.secao}>
      <div style={s.secaoHeader}>
        <div style={s.secaoTitulo}>📝 Simulados</div>
        <div style={{ fontSize:12, color:'#5f5874' }}>Pratique quando quiser, quantas vezes quiser</div>
      </div>

      {cursos.filter(c => pastas.some(p => String(p.cursoId) === String(c.id))).length === 0 && (
        <div style={s.vazio}>Nenhum simulado disponível ainda. O instrutor precisa cadastrar questões no banco de questões.</div>
      )}

      {cursos.map(curso => {
        const pastasDoC = pastas.filter(p => String(p.cursoId) === String(curso.id)).sort((a,b) => a.nome.localeCompare(b.nome));
        if (pastasDoC.length === 0) return null;

        return (
          <div key={curso.id} style={{ marginBottom:'2rem' }}>
            <div style={{ padding:'0.5rem 0.75rem', background:'rgba(139,92,246,0.08)', borderRadius:8, borderLeft:'3px solid #8b5cf6', marginBottom:'0.75rem' }}>
              <div style={{ fontWeight:600, color:'#2d2640', fontSize:14 }}>🎓 {curso.nome}</div>
            </div>

            <div style={{ display:'flex', flexDirection:'column', gap:'0.625rem' }}>
              {pastasDoC.map(pasta => {
                const qsDaPasta = questoes.filter(q => q.pastaId === pasta.id);
                const ultimoRes = respostas
                  .filter(r => r.tipo === 'simulado' && r.pastaId === pasta.id && r.alunoId === String(usuarioAtual?.id))
                  .sort((a,b) => new Date(b.dataRealizacao) - new Date(a.dataRealizacao))[0];

                return (
                  <div key={pasta.id} style={s.card}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
                        <div style={{ width:36, height:36, borderRadius:8, background:'rgba(139,92,246,0.15)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0 }}>
                          📁
                        </div>
                        <div>
                          <div style={s.cardTitulo}>{pasta.nome}</div>
                          <div style={s.cardMeta}>
                            {qsDaPasta.length} questão{qsDaPasta.length !== 1 ? 'ões' : ''}
                            {ultimoRes && ` · Último: ${ultimoRes.percentual.toFixed(0)}% — ${new Date(ultimoRes.dataRealizacao).toLocaleDateString('pt-BR')}`}
                          </div>
                        </div>
                      </div>
                      <div style={{ display:'flex', gap:'0.5rem', alignItems:'center' }}>
                        {ultimoRes && (
                          <span style={{ fontSize:12, fontWeight:700, color: ultimoRes.percentual>=70 ? '#10b981' : '#f59e0b', padding:'3px 8px', borderRadius:6, background: ultimoRes.percentual>=70 ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)' }}>
                            {ultimoRes.percentual>=70 ? '✓ Aprovado' : `${ultimoRes.percentual.toFixed(0)}%`}
                          </span>
                        )}
                        <button
                          style={{ ...s.btnNovo, opacity: qsDaPasta.length===0 ? 0.4 : 1 }}
                          disabled={qsDaPasta.length === 0}
                          onClick={() => iniciarSimulado(pasta, curso.id)}>
                          {ultimoRes ? '🔄 Refazer' : '▶ Iniciar'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── ABA: Presencial ────────────────────────────────────────────────────────
function AbaPresencial({ cursos, presencial, setPresencial }) {
  const [filtroCurso, setFiltroCurso] = useState('');
  const [formMat,     setFormMat]     = useState(null); // { cursoId, nome, tipoArq, url, modulo, descricao }
  const [formDin,     setFormDin]     = useState(null); // { cursoId, titulo, objetivo, duracao, minPart, maxPart, instrucoes }
  const [abertos,     setAbertos]     = useState({});   // id → bool

  const cursosFiltrados = cursos.filter(c =>
    c.nome.toLowerCase().includes(filtroCurso.toLowerCase())
  );

  const toggle = (id) => setAbertos(prev => ({ ...prev, [id]: !prev[id] }));

  const salvarMat = () => {
    if (!formMat.nome || !formMat.url) { toast.aviso('Informe nome e URL do arquivo.'); return; }
    if (formMat.id) {
      setPresencial(presencial.map(p => p.id === formMat.id ? formMat : p));
      toast.sucesso('Material atualizado!');
    } else {
      setPresencial([...presencial, { ...formMat, id: novoId(), tipo: 'material' }]);
      toast.sucesso('Material adicionado!');
    }
    setFormMat(null);
  };

  const salvarDin = () => {
    if (!formDin.titulo) { toast.aviso('Informe o título da dinâmica.'); return; }
    if (formDin.id) {
      setPresencial(presencial.map(p => p.id === formDin.id ? formDin : p));
      toast.sucesso('Dinâmica atualizada!');
    } else {
      setPresencial([...presencial, { ...formDin, id: novoId(), tipo: 'dinamica' }]);
      toast.sucesso('Dinâmica adicionada!');
    }
    setFormDin(null);
  };

  const excluir = (id) => {
    if (!window.confirm('Excluir este item?')) return;
    setPresencial(presencial.filter(p => p.id !== id));
    toast.sucesso('Item excluído.');
  };

  const getEmbedPdf = (url) => {
    const m = url?.match(/\/file\/d\/([^/]+)/);
    if (m) return `https://drive.google.com/file/d/${m[1]}/preview`;
    return url;
  };

  const iconeTipo = (tipoArq) => {
    if (tipoArq === 'ppt') return '📊';
    if (tipoArq === 'pdf') return '📄';
    return '📁';
  };

  return (
    <div style={s.secao}>
      <div style={s.secaoHeader}>
        <div style={s.secaoTitulo}>Treinamento Presencial</div>
        <input
          style={{ ...s.input, maxWidth: 280 }}
          placeholder="🔍 Filtrar cursos..."
          value={filtroCurso}
          onChange={e => setFiltroCurso(e.target.value)}
        />
      </div>

      {cursosFiltrados.length === 0 && <div style={s.vazio}>Nenhum curso encontrado.</div>}

      {cursosFiltrados.map(curso => {
        const matsDoC = presencial.filter(p => String(p.cursoId) === String(curso.id) && p.tipo === 'material');
        const dinsDoC = presencial.filter(p => String(p.cursoId) === String(curso.id) && p.tipo === 'dinamica');

        return (
          <div key={curso.id} style={{ marginBottom:'2rem' }}>
            {/* Cabeçalho do curso */}
            <div style={{ padding:'0.5rem 0.75rem', background:'rgba(99,102,241,0.08)', borderRadius:8, borderLeft:'3px solid #6366f1', marginBottom:'1rem' }}>
              <div style={{ fontWeight:700, color:'#2d2640', fontSize:14 }}>🏢 {curso.nome}</div>
            </div>

            {/* ── Seção: Materiais de Apresentação ── */}
            <div style={{ marginBottom:'1.25rem' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.5rem' }}>
                <div style={{ fontSize:12, color:'#6366f1', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em' }}>
                  📊 Materiais de Apresentação ({matsDoC.length})
                </div>
                {!(formMat?.cursoId === curso.id) && (
                  <button style={s.btnNovo} onClick={() => setFormMat({ cursoId: curso.id, nome:'', tipoArq:'ppt', url:'', modulo:'', descricao:'' })}>
                    <Plus size={14}/> Adicionar Arquivo
                  </button>
                )}
              </div>

              {formMat?.cursoId === curso.id && (
                <div style={s.formCard}>
                  <div style={s.formTitulo}>{formMat.id ? 'Editar Material' : 'Novo Material'}</div>
                  <div style={s.formRow}>
                    <Campo label="Nome / Título *">
                      <input style={s.input} value={formMat.nome} onChange={e => setFormMat({...formMat, nome: e.target.value})} />
                    </Campo>
                    <Campo label="Tipo">
                      <select style={s.input} value={formMat.tipoArq} onChange={e => setFormMat({...formMat, tipoArq: e.target.value})}>
                        <option value="ppt">📊 PowerPoint / Apresentação</option>
                        <option value="pdf">📄 PDF</option>
                        <option value="outro">📁 Outro</option>
                      </select>
                    </Campo>
                    <Campo label="Módulo / Tema">
                      <input style={s.input} value={formMat.modulo} placeholder="Ex: Módulo 1 — Introdução" onChange={e => setFormMat({...formMat, modulo: e.target.value})} />
                    </Campo>
                  </div>
                  <Campo label="URL do arquivo * (Google Drive, OneDrive, link direto...)">
                    <input style={s.input} value={formMat.url} placeholder="https://docs.google.com/presentation/d/..." onChange={e => setFormMat({...formMat, url: e.target.value})} />
                  </Campo>
                  <div style={{ background:'rgba(99,102,241,0.08)', border:'1px solid rgba(99,102,241,0.2)', borderRadius:8, padding:'0.75rem', fontSize:12, color:'#a5b4fc', marginBottom:'0.75rem' }}>
                    💡 Para Google Slides: use o link de compartilhamento ou link de incorporação.<br/>
                    Para PDFs do Drive: compartilhe com "Qualquer pessoa com o link" e cole a URL.<br/>
                    Para OneDrive/SharePoint: use o link de incorporação gerado pelo Office.
                  </div>
                  <Campo label="Observações">
                    <textarea style={{...s.input, height:60, resize:'vertical'}} value={formMat.descricao} onChange={e => setFormMat({...formMat, descricao: e.target.value})} />
                  </Campo>
                  <div style={s.formAcoes}>
                    <button style={s.btnSalvar} onClick={salvarMat}>Salvar</button>
                    <button style={s.btnCancelar} onClick={() => setFormMat(null)}>Cancelar</button>
                  </div>
                </div>
              )}

              {matsDoC.length === 0 && !(formMat?.cursoId === curso.id) && (
                <div style={s.vazio}>Nenhum material cadastrado. Adicione apresentações e PDFs para o dia do treinamento.</div>
              )}

              {matsDoC.map(mat => {
                const aberto = abertos[mat.id];
                const isPdf = mat.tipoArq === 'pdf';
                return (
                  <div key={mat.id} style={s.card}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', cursor: isPdf ? 'pointer' : 'default', flex:1 }}
                        onClick={() => isPdf && toggle(mat.id)}>
                        <div style={{ width:36, height:36, borderRadius:8, background:'rgba(99,102,241,0.15)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontSize:18 }}>
                          {iconeTipo(mat.tipoArq)}
                        </div>
                        <div>
                          <div style={s.cardTitulo}>{mat.nome}</div>
                          <div style={s.cardMeta}>
                            {mat.tipoArq === 'ppt' ? 'Apresentação' : mat.tipoArq === 'pdf' ? 'PDF' : 'Arquivo'}
                            {mat.modulo && ` · ${mat.modulo}`}
                            {mat.descricao && ` · ${mat.descricao.substring(0,50)}`}
                          </div>
                        </div>
                      </div>
                      <div style={{ display:'flex', gap:4, alignItems:'center' }}>
                        {!isPdf && (
                          <a href={mat.url} target="_blank" rel="noopener noreferrer"
                            style={{ ...s.btnNovo, textDecoration:'none', padding:'6px 12px', fontSize:12, display:'inline-flex', alignItems:'center', gap:4 }}>
                            <ExternalLink size={13}/> Abrir
                          </a>
                        )}
                        <button style={s.btnIcone} onClick={() => setFormMat({...mat})}><Edit2 size={13}/></button>
                        <button style={{...s.btnIcone, color:'#ef4444'}} onClick={() => excluir(mat.id)}><Trash2 size={13}/></button>
                        {isPdf && (aberto ? <ChevronUp size={16} color="#5f5874"/> : <ChevronDown size={16} color="#5f5874"/>)}
                      </div>
                    </div>
                    {isPdf && aberto && (
                      <div style={{ marginTop:'1rem', borderTop:'1px solid #e5e0ef', paddingTop:'1rem' }}>
                        <iframe
                          src={getEmbedPdf(mat.url)}
                          style={{ width:'100%', height:600, border:'none', borderRadius:8 }}
                          title={mat.nome}
                          sandbox="allow-same-origin allow-scripts allow-presentation"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* ── Seção: Dinâmicas de Grupo ── */}
            <div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.5rem' }}>
                <div style={{ fontSize:12, color:'#10b981', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em' }}>
                  🎯 Dinâmicas de Grupo ({dinsDoC.length})
                </div>
                {!(formDin?.cursoId === curso.id) && (
                  <button style={{ ...s.btnNovo, background:'rgba(16,185,129,0.15)', color:'#10b981', border:'1px solid rgba(16,185,129,0.3)' }}
                    onClick={() => setFormDin({ cursoId: curso.id, titulo:'', objetivo:'', duracao:'', minPart:'', maxPart:'', instrucoes:'' })}>
                    <Plus size={14}/> Nova Dinâmica
                  </button>
                )}
              </div>

              {formDin?.cursoId === curso.id && (
                <div style={s.formCard}>
                  <div style={s.formTitulo}>{formDin.id ? 'Editar Dinâmica' : 'Nova Dinâmica de Grupo'}</div>
                  <div style={s.formRow}>
                    <Campo label="Título da Dinâmica *">
                      <input style={s.input} value={formDin.titulo} placeholder="Ex: Torre de Marshmallow" onChange={e => setFormDin({...formDin, titulo: e.target.value})} />
                    </Campo>
                    <Campo label="Duração estimada">
                      <input style={s.input} value={formDin.duracao} placeholder="Ex: 30 minutos" onChange={e => setFormDin({...formDin, duracao: e.target.value})} />
                    </Campo>
                  </div>
                  <div style={s.formRow}>
                    <Campo label="Mín. participantes">
                      <input type="number" style={s.input} value={formDin.minPart} placeholder="Ex: 5" onChange={e => setFormDin({...formDin, minPart: e.target.value})} />
                    </Campo>
                    <Campo label="Máx. participantes">
                      <input type="number" style={s.input} value={formDin.maxPart} placeholder="Ex: 20" onChange={e => setFormDin({...formDin, maxPart: e.target.value})} />
                    </Campo>
                  </div>
                  <Campo label="Objetivo da dinâmica">
                    <textarea style={{...s.input, height:60, resize:'vertical'}} value={formDin.objetivo}
                      placeholder="Ex: Desenvolver comunicação em equipe e resolução de problemas sob pressão"
                      onChange={e => setFormDin({...formDin, objetivo: e.target.value})} />
                  </Campo>
                  <Campo label="Instruções / Passo a Passo *">
                    <textarea style={{...s.input, height:140, resize:'vertical'}} value={formDin.instrucoes}
                      placeholder={'1. Divida o grupo em equipes de 4 a 5 pessoas.\n2. Cada equipe recebe os materiais...\n3. O desafio é...'}
                      onChange={e => setFormDin({...formDin, instrucoes: e.target.value})} />
                  </Campo>
                  <div style={s.formAcoes}>
                    <button style={s.btnSalvar} onClick={salvarDin}>Salvar</button>
                    <button style={s.btnCancelar} onClick={() => setFormDin(null)}>Cancelar</button>
                  </div>
                </div>
              )}

              {dinsDoC.length === 0 && !(formDin?.cursoId === curso.id) && (
                <div style={s.vazio}>Nenhuma dinâmica cadastrada. Adicione atividades para engajar a turma no dia do treinamento.</div>
              )}

              {dinsDoC.map(din => {
                const aberto = abertos[din.id];
                return (
                  <div key={din.id} style={s.card}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', cursor:'pointer', flex:1 }} onClick={() => toggle(din.id)}>
                        <div style={{ width:36, height:36, borderRadius:8, background:'rgba(16,185,129,0.15)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontSize:18 }}>
                          🎯
                        </div>
                        <div>
                          <div style={s.cardTitulo}>{din.titulo}</div>
                          <div style={s.cardMeta}>
                            {din.duracao && `⏱ ${din.duracao}`}
                            {(din.minPart || din.maxPart) && ` · 👥 ${din.minPart || '?'}–${din.maxPart || '?'} participantes`}
                            {din.objetivo && ` · ${din.objetivo.substring(0,60)}${din.objetivo.length>60?'...':''}`}
                          </div>
                        </div>
                      </div>
                      <div style={{ display:'flex', gap:4 }}>
                        <button style={s.btnIcone} onClick={() => setFormDin({...din})}><Edit2 size={13}/></button>
                        <button style={{...s.btnIcone, color:'#ef4444'}} onClick={() => excluir(din.id)}><Trash2 size={13}/></button>
                        {aberto ? <ChevronUp size={16} color="#5f5874"/> : <ChevronDown size={16} color="#5f5874"/>}
                      </div>
                    </div>
                    {aberto && (
                      <div style={{ marginTop:'1rem', borderTop:'1px solid #e5e0ef', paddingTop:'1rem', display:'flex', flexDirection:'column', gap:'0.75rem' }}>
                        {din.objetivo && (
                          <div>
                            <div style={{ fontSize:11, color:'#10b981', fontWeight:700, textTransform:'uppercase', marginBottom:'0.25rem' }}>Objetivo</div>
                            <div style={{ fontSize:13, color:'#64748b' }}>{din.objetivo}</div>
                          </div>
                        )}
                        {din.instrucoes && (
                          <div>
                            <div style={{ fontSize:11, color:'#10b981', fontWeight:700, textTransform:'uppercase', marginBottom:'0.25rem' }}>Instruções</div>
                            <pre style={{ fontSize:13, color:'#1c1530', whiteSpace:'pre-wrap', fontFamily:'inherit', margin:0, background:'rgba(16,185,129,0.05)', padding:'0.75rem', borderRadius:8, border:'1px solid rgba(16,185,129,0.15)' }}>{din.instrucoes}</pre>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Estilos ────────────────────────────────────────────────────────────────
const s = {
  container:  { display:'flex', flexDirection:'column', gap:'1.25rem' },
  titulo:     { fontSize:'1.8rem', fontWeight:700, color:'#1c1530' },
  abas:       { display:'flex', gap:'0.5rem', borderBottom:'1px solid #e5e0ef', paddingBottom:'0.75rem', flexWrap:'wrap' },
  aba:        { background:'transparent', border:'1px solid #e5e0ef', borderRadius:20, color:'#5f5874', padding:'7px 18px', fontSize:13, fontWeight:500, cursor:'pointer' },
  abaAtiva:   { background:'#7c3aed', border:'1px solid #7c3aed', color:'white' },
  secao:      { display:'flex', flexDirection:'column', gap:'1rem' },
  secaoHeader:{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'0.5rem' },
  secaoTitulo:{ fontSize:16, fontWeight:700, color:'#1c1530' },
  card:       { background:'#ffffff', border:'1px solid #e5e0ef', borderRadius:12, padding:'1.25rem' },
  cardTitulo: { fontSize:14, fontWeight:600, color:'#1c1530' },
  cardMeta:   { fontSize:12, color:'#5f5874', marginTop:2 },
  formCard:   { background:'#ffffff', border:'1px solid #e5e0ef', borderRadius:12, padding:'1.5rem', display:'flex', flexDirection:'column', gap:'1rem' },
  formTitulo: { fontSize:15, fontWeight:700, color:'#1c1530' },
  formRow:    { display:'flex', gap:'1rem', flexWrap:'wrap' },
  formAcoes:  { display:'flex', gap:'0.75rem', justifyContent:'flex-end', marginTop:'0.25rem' },
  input:      { background:'#f7f5fb', border:'1px solid #e5e0ef', borderRadius:8, color:'#1c1530', padding:'8px 12px', fontSize:13, outline:'none', width:'100%', boxSizing:'border-box' },
  select:     { background:'#f7f5fb', border:'1px solid #e5e0ef', borderRadius:8, color:'#1c1530', padding:'8px 12px', fontSize:13 },
  btnNovo:    { display:'flex', alignItems:'center', gap:6, background:'#7c3aed', color:'white', border:'none', borderRadius:20, padding:'8px 18px', fontSize:13, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' },
  btnSalvar:  { background:'#7c3aed', border:'none', borderRadius:8, color:'white', cursor:'pointer', padding:'9px 22px', fontSize:13, fontWeight:600 },
  btnCancelar:{ background:'transparent', border:'1px solid #e5e0ef', borderRadius:8, color:'#5f5874', cursor:'pointer', padding:'9px 16px', fontSize:13 },
  btnIcone:   { background:'transparent', border:'none', color:'#8b5cf6', cursor:'pointer', padding:4, display:'flex' },
  badge:      { fontSize:11, fontWeight:700, padding:'2px 10px', borderRadius:20, background:'rgba(139,92,246,0.1)', color:'#8b5cf6', border:'1px solid rgba(139,92,246,0.3)' },
  vazio:      { padding:'2rem', textAlign:'center', color:'#64748b', fontSize:13, background:'#ffffff', borderRadius:12, border:'1px solid #e5e0ef' },
};
